import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaShoppingCart, FaTruckLoading, FaFileInvoiceDollar,
  FaHandHoldingUsd, FaLayerGroup, FaTimes, FaSync, FaMoneyCheckAlt, FaWallet, FaClipboardList
} from 'react-icons/fa';
import { Row, Col, Spinner, Alert, Badge, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import api from '../services/api';
import documentService from '../services/documentService';
import { defaultSelectStyles } from '../utils/selectStyles';
import { canAccess } from '../utils/canAccess';
import InfoTip from './decisionnel/components/InfoTip';
import { rowsToHtml, copyHtmlToClipboard } from './decisionnel/utils/exports';

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const today = () => new Date().toISOString().split('T')[0];

// Formate un montant selon l'unité choisie par section (Auto/MAD/kMAD/MMAD) — utilisé sur la ligne TOTAL uniquement (12)
const fmtUnit = (v, unit) => {
  const n = Number(v || 0);
  if (unit === 'kmad') return fmt(Math.round(n / 1000)) + ' k';
  if (unit === 'mmad') return (n / 1_000_000).toFixed(1).replace('.', ',') + ' M';
  return fmt(n);
};
const UNIT_LABELS = { mad: 'MAD', kmad: 'kMAD', mmad: 'MMAD' };

const UnitPicker = ({ unit, onChange }) => (
  <div style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
    {Object.entries(UNIT_LABELS).map(([val, label]) => (
      <button key={val} onClick={() => onChange(val)} style={{
        padding: '2px 7px', fontSize: '0.6rem', fontWeight: 700,
        border: `1px solid ${unit === val ? '#6d4e36' : '#d8c8b8'}`,
        background: unit === val ? '#6d4e36' : '#fff',
        color: unit === val ? '#fff' : '#999',
        borderRadius: 4, cursor: 'pointer', lineHeight: 1.6
      }}>{label}</button>
    ))}
  </div>
);
const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const exportExcel = (rows, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const exportBalanceExcel = (rows, extraCol, filename) => exportExcel(rows.map(r => ({
  'ID': r.IDFACTURE,
  'N° Facture': r.NFacture,
  'Date': fmtDate(r.date_fac),
  ...(extraCol ? { [extraCol.label]: r[extraCol.field] } : {}),
  'Chantier': r.chantier,
  'Fournisseur': r.fournisseur,
  'Total TTC': r.total_ttc,
  'Part chantier TTC': r.part_chantier_ttc,
  'Reste TTC': r.total_ttc_left
})), filename);

/* ─── pagination ────────────────────────────────────────────────────────────── */
const btnPage = (disabled, active) => ({
  minWidth: 28, height: 26, border: `1px solid ${active ? '#6d4e36' : '#e0d8d0'}`,
  borderRadius: 6, background: active ? '#6d4e36' : disabled ? '#f7f5f2' : '#fff',
  color: active ? '#fff' : disabled ? '#ccc' : '#6d4e36',
  fontWeight: active ? 700 : 400, cursor: disabled ? 'default' : 'pointer',
  fontSize: '0.75rem', padding: '0 5px', lineHeight: '24px',
  transition: 'background 0.12s, border-color 0.12s'
});

const pageWindow = (cur, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total]);
  for (let i = Math.max(2, cur - 2); i <= Math.min(total - 1, cur + 2); i++) set.add(i);
  const sorted = [...set].sort((a, b) => a - b);
  const out = []; let prev = 0;
  for (const p of sorted) { if (p - prev > 1) out.push(null); out.push(p); prev = p; }
  return out;
};

const usePaginated = (data, defaultPageSize = 20) => {
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  useEffect(() => { setPage(1); }, [data]);
  const total      = data?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage   = Math.min(page, totalPages);
  const visible    = (data || []).slice((safePage - 1) * pageSize, safePage * pageSize);
  return { visible, page: safePage, setPage, totalPages, total, pageSize, setPageSize };
};

const TablePager = ({ page, totalPages, setPage, total, pageSize = 20, setPageSize }) => {
  if (total <= 20 && pageSize === 20) return null;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '7px 10px', borderTop: '1px solid #f0e8df',
      flexWrap: 'wrap', gap: 6
    }}>
      {/* info + sélecteur lignes/page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.73rem', color: '#aaa' }}>
        <span style={{ color: '#6d4e36', fontWeight: 600 }}>{from}–{to}</span>
        <span>sur {total} lignes</span>
        {setPageSize && (
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ fontSize: '0.72rem', padding: '1px 4px', border: '1px solid #e0d8d0', borderRadius: 5, color: '#6d4e36', cursor: 'pointer', background: '#fdfcf8' }}
          >
            {[20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        )}
      </div>
      {/* boutons de page */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={btnPage(page === 1)}>«</button>
          <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={btnPage(page === 1)}>‹</button>
          {pageWindow(page, totalPages).map((p, i) =>
            p === null
              ? <span key={`e${i}`} style={{ padding: '0 3px', color: '#bbb', fontSize: '0.75rem', lineHeight: '26px' }}>…</span>
              : <button key={p} onClick={() => setPage(p)} style={btnPage(false, p === page)}>{p}</button>
          )}
          <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={btnPage(page === totalPages)}>›</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={btnPage(page === totalPages)}>»</button>
        </div>
      )}
    </div>
  );
};

/* ─── tri générique ─────────────────────────────────────────────────────────── */
const useSortable = (data, defaultKey = null, defaultAsc = false) => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [asc, setAsc] = useState(defaultAsc);

  const handleSort = (key) => {
    if (sortKey === key) setAsc((a) => !a);
    else { setSortKey(key); setAsc(true); }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !data?.length) return [...(data || [])];
    return [...data].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return asc ? av - bv : bv - av;
      return asc
        ? String(av ?? '').localeCompare(String(bv ?? ''), 'fr')
        : String(bv ?? '').localeCompare(String(av ?? ''), 'fr');
    });
  }, [data, sortKey, asc]);

  const Th = ({ field, children, className, style }) => (
    <th
      className={className}
      onClick={() => handleSort(field)}
      style={{
        padding: '9px 12px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        color: sortKey === field ? '#6d4e36' : '#888',
        background: '#fdf8f4', borderBottom: '2px solid #e8ddd4',
        ...style
      }}
    >
      {children}
      <span style={{ marginLeft: 3, fontSize: '0.58rem', opacity: sortKey === field ? 1 : 0.28 }}>
        {sortKey === field ? (asc ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );

  return { sorted, sortKey, asc, handleSort, Th };
};

/* ─── en-tête de colonne statique (non triable) — même style que Th ─────────── */
const ThStatic = ({ children, className, style }) => (
  <th
    className={className}
    style={{
      padding: '9px 12px', whiteSpace: 'nowrap',
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: '#888', background: '#fdf8f4', borderBottom: '2px solid #e8ddd4',
      ...style
    }}
  >
    {children}
  </th>
);

/* ─── ligne de tableau zébrée + survol (style Décisionnel) ──────────────────── */
const Tr = ({ children, style, ...rest }) => (
  <tr
    style={{ borderBottom: '1px solid #f0e8df', ...style }}
    onMouseEnter={e => { e.currentTarget.dataset.bg = e.currentTarget.style.background; e.currentTarget.style.background = '#f9f3ed'; }}
    onMouseLeave={e => { e.currentTarget.style.background = e.currentTarget.dataset.bg || ''; }}
    {...rest}
  >
    {children}
  </tr>
);
const trZebra = (i) => ({ background: i % 2 === 0 ? '#fff' : '#fdfaf7' });

/* ─── style helpers ─────────────────────────────────────────────────────────── */
const sectionCard = {
  borderRadius: 14,
  border: '1px solid rgba(109,78,54,0.15)',
  boxShadow: '0 2px 12px rgba(109,78,54,0.08)',
  background: '#fff',
  overflow: 'hidden',
  marginBottom: '1.5rem'
};

/* ─── carte de tableau, style Décisionnel (bordure épaisse, repli possible) ──── */
const detailSectionCard = {
  borderRadius: 10,
  border: '2px solid #d4c8bc',
  boxShadow: '0 2px 12px rgba(109,78,54,0.08)',
  background: '#fff',
  overflow: 'hidden',
  marginBottom: '1.5rem'
};

const selectStylesSm = {
  ...defaultSelectStyles,
  control: (base) => ({
    ...defaultSelectStyles.control(base),
    minHeight: 34,
    fontSize: '0.82rem'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 34 })
};

const SectionHeader = ({ children, count, open, setOpen, onExport, onCopy, unitPicker, dragHandle }) => (
  <div
    onClick={() => setOpen && setOpen((o) => !o)}
    style={{
      background: 'linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%)',
      color: '#fff',
      padding: '0.6rem 1rem',
      borderRadius: open === false ? 9 : '8px 8px 0 0',
      fontWeight: 600,
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: setOpen ? 'pointer' : 'default',
      userSelect: 'none'
    }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {dragHandle && (
        <span
          draggable
          onDragStart={dragHandle.onDragStart}
          onDragEnd={dragHandle.onDragEnd}
          onClick={(e) => e.stopPropagation()}
          title="Glisser pour réorganiser"
          style={{ cursor: 'grab', opacity: 0.7, fontSize: '1rem', padding: '0 2px' }}
        >⠿</span>
      )}
      {children}
      {count != null && (
        <span style={{ fontSize: '0.68rem', background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 8px', fontWeight: 500 }}>
          {count} ligne{count !== 1 ? 's' : ''}
        </span>
      )}
    </span>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      {unitPicker}
      {onExport && (
        <button onClick={onExport} style={{
          background: '#e8f5e9', border: '1px solid #4caf50', color: '#2e7d32',
          borderRadius: 5, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600,
          cursor: 'pointer', lineHeight: '1.6'
        }}>⬇ Excel</button>
      )}
      {onCopy && (
        <button onClick={onCopy} style={{
          background: '#e6f0fb', border: '1px solid #9ab8e0', color: '#1a5296',
          borderRadius: 5, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600,
          cursor: 'pointer', lineHeight: '1.6'
        }}>⎘ Copier</button>
      )}
      <button onClick={scrollTop} style={{
        background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.5)',
        color: '#fff', borderRadius: 5, padding: '1px 7px', fontSize: '0.75rem',
        cursor: 'pointer', lineHeight: '1.6'
      }}>↑ haut</button>
      {setOpen && (
        <button onClick={() => setOpen((o) => !o)} style={{
          background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.5)',
          color: '#fff', borderRadius: 5, padding: '1px 7px', fontSize: '0.75rem',
          cursor: 'pointer', lineHeight: '1.6'
        }}>{open ? '▲' : '▼'}</button>
      )}
    </div>
  </div>
);

/* ─── KPI card avec taux + raccourci ─────────────────────────────────────────── */
const KpiCard = ({ icon, label, info, value, valueLabel, rate, rateLabel, rateSub, details, onClick, onValueClick, onRateClick, color = '#6d4e36' }) => {
  // valeur / taux cliquables séparément (sinon le clic remonte au onClick de la carte)
  const clickable = (fn) => fn ? {
    onClick: (e) => { e.stopPropagation(); fn(); },
    title: 'Voir les documents',
    style: { cursor: 'pointer', borderBottom: `1px dotted ${color}` }
  } : {};
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 12,
        border: `1.5px solid ${color}28`,
        borderTop: `4px solid ${color}`,
        background: hovered ? `${color}0a` : '#fff',
        boxShadow: hovered ? `0 8px 24px ${color}28` : '0 2px 10px rgba(0,0,0,0.06)',
        height: '100%', display: 'flex', flexDirection: 'column',
        padding: '0.85rem 1rem 0.75rem',
        cursor: onClick ? 'pointer' : 'default',
        transform: hovered && onClick ? 'translateY(-3px)' : 'none',
        transition: 'all 0.18s ease',
        position: 'relative', overflow: 'hidden'
      }}
    >
      {/* icône + label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ color, fontSize: '1.3rem' }}>{icon}</div>
        <div style={{ fontSize: '0.68rem', color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center' }}>
          {label}<InfoTip text={info} />
        </div>
      </div>

      {/* valeur + taux */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 5 }}>
        <span {...clickable(onValueClick)} style={{ fontSize: '1.05rem', fontWeight: 700, color, ...clickable(onValueClick).style }}>{value}{valueLabel ? <span style={{ fontWeight: 500, fontSize: '0.8rem' }}> {valueLabel}</span> : null}</span>
        {rate !== undefined && (
          <span {...clickable(onRateClick)} style={{ fontSize: '1.05rem', fontWeight: 700, color, whiteSpace: 'nowrap', ...clickable(onRateClick).style }}>
            {rate}%{rateLabel ? <span style={{ fontWeight: 500, fontSize: '0.9rem' }}> {rateLabel}</span> : null}
          </span>
        )}
      </div>

      {/* barre de taux */}
      {rate !== undefined && (
        <div style={{ margin: '0 0 5px', background: `${color}18`, borderRadius: 4, height: 6 }}>
          <div style={{
            width: `${Math.min(100, Math.max(0, rate))}%`,
            background: color, borderRadius: 4, height: 6,
            transition: 'width 0.4s ease'
          }} />
        </div>
      )}

      {/* sous-indicateurs cliquables → ouvrent le panneau détail (8/9) */}
      {details?.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }} onClick={(e) => e.stopPropagation()}>
          {details.map((d, i) => (
            <button
              key={i}
              onClick={d.onClick}
              disabled={!d.onClick}
              style={{
                fontSize: d.strong ? '0.74rem' : '0.66rem', fontWeight: d.strong ? 800 : 600, color: d.color || '#999',
                background: `${d.color || '#999'}14`, border: `1px solid ${d.color || '#999'}30`,
                borderRadius: 20, padding: '1px 8px', cursor: d.onClick ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: 3
              }}
            >
              {d.label}{d.onClick && <span style={{ opacity: 0.6 }}>↗</span>}
            </button>
          ))}
        </div>
      ) : (
        rateSub && <div style={{ fontSize: '0.69rem', color: '#999', marginBottom: 4 }}>{rateSub}</div>
      )}

      {/* hint détail */}
      {onClick && (
        <div style={{
          marginTop: 'auto', paddingTop: 8,
          fontSize: '0.7rem', fontWeight: 600, color,
          opacity: hovered ? 0.9 : 0.35, transition: 'opacity 0.17s'
        }}>
          🔍 voir le détail
        </div>
      )}
    </div>
  );
};

const PctBar = ({ value, color = '#4caf50' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ flex: 1, background: '#eee', borderRadius: 4, height: 7, minWidth: 60 }}>
      <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color, borderRadius: 4, height: 7 }} />
    </div>
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color, minWidth: 34 }}>{value}%</span>
  </div>
);

const colsProjetMontant = (valueLabel) => [
  { key: 'label', label: 'Projet' },
  { key: 'value', label: valueLabel, right: true, format: (v) => `${fmt(v)} MAD` }
];

// Transforme des lignes brutes (un document = une ligne) en lignes de détail prêtes pour le panneau,
// avec un champ "montant" calculé par valueFn et triées par montant décroissant (8/9 — détail document).
const toDocRows = (rows, pick, valueFn, includeZero = false) =>
  (rows || [])
    .map((r) => ({ ...pick(r), montant: valueFn(r) || 0 }))
    .filter((r) => includeZero || r.montant > 0)
    .sort((a, b) => b.montant - a.montant);

const COLS_BDC = [
  { key: 'IDBDC', label: 'N° BDC', link: (r) => `https://cienco.ngrok.io/fiche_bon_de_commande/${r.IDBDC}` },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'projet', label: 'Projet' },
  { key: 'fournisseur', label: 'Fournisseur' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
const COLS_BR = [
  { key: 'IDBR', label: 'N° BR', link: (r) => `https://cienco.ngrok.io/fiche_bon_de_reception/${r.IDBR}` },
  { key: 'facture_vente_ids', label: 'Fact. vente', nowrap: true,
    render: (r, v) => {
      if (!v) return <span style={{ color: '#bbb' }}>—</span>;
      const ids = String(v).split(',');
      return ids.map((id, i) => (
        <React.Fragment key={id}>
          {i > 0 && <span style={{ color: '#ccc' }}> · </span>}
          <a href={`https://cienco.ngrok.io/fiche_groupe_factures/${id}`} target="_blank" rel="noreferrer"
             style={{ color: '#1565c0', fontWeight: 700, textDecoration: 'none' }}>{id}</a>
        </React.Fragment>
      ));
    }
  },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'projet', label: 'Projet' },
  { key: 'fournisseur', label: 'Fournisseur' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
// Détail « À facturer vente » : Montant BR complet (discret) + Reliquat réellement restant à facturer (mis en avant)
const COLS_BR_FV = [
  ...COLS_BR.filter((c) => c.key !== 'montant'),
  { key: 'montant',  label: 'Montant BR', right: true, muted: true, total: true, format: (v) => `${fmt(v)} MAD` },
  { key: 'rg', label: 'RG retenue', right: true, muted: true, format: (v) => (v ? `${fmt(v)} MAD` : '—') },
  { key: 'reliquat', label: 'Reliquat à facturer', right: true, total: true, format: (v) => `${fmt(v)} MAD` }
];
// Lignes BR encore à facturer au client, triées par reliquat décroissant
const toBrFvRows = (rows) =>
  toDocRows(rows, (r) => ({ ...pickBr(r), rg: r.rg_vente_ttc || 0, reliquat: r.reliquat_vente_ttc || 0 }), (r) => r.non_facturees_vente_ttc)
    .sort((a, b) => b.reliquat - a.reliquat);
// Détail « reliquat avec RG » : reliquat à facturer + RG retenue sur les factures vente déjà émises (prix BR)
const COLS_BR_FV_RG = [
  ...COLS_BR.filter((c) => c.key !== 'montant'),
  { key: 'reliquat', label: 'Reliquat à facturer', right: true, muted: true, total: true, format: (v) => `${fmt(v)} MAD` },
  { key: 'rg',       label: 'RG retenue', right: true, muted: true, total: true, format: (v) => (v ? `${fmt(v)} MAD` : '—') },
  { key: 'montant',  label: 'Total avec RG', right: true, total: true, format: (v) => `${fmt(v)} MAD` }
];
const toBrFvRgRows = (rows) =>
  toDocRows(rows, (r) => ({ ...pickBr(r), reliquat: r.reliquat_vente_ttc || 0, rg: r.rg_vente_ttc || 0 }),
    (r) => (r.reliquat_vente_ttc || 0) + (r.rg_vente_ttc || 0))
    .filter((r) => r.montant >= 0.5);
const COLS_FV = [
  { key: 'IDFACTURE', label: 'N° Facture', link: (r) => `https://cienco.ngrok.io/fiche_groupe_factures/${r.IDFACTURE}` },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'projet', label: 'Projet' },
  { key: 'societe_client', label: 'Société client' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'rg_ttc', label: 'RG retenue', right: true, muted: true, format: (v) => (v ? `${fmt(v)} MAD` : '—') },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
const COLS_FA = [
  { key: 'IDFACTURE', label: 'N° Facture', link: (r) => `https://cienco.ngrok.io/fiche_facture/${r.IDFACTURE}` },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'Fournisseur', label: 'Fournisseur' },
  { key: 'Societe', label: 'Société' },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
const COLS_CHEQUES = [
  { key: 'id', label: 'N° Chèque', link: (r) => `https://cienco.ngrok.io/fiche_cheque/${r.id}` },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'fournisseur', label: 'Fournisseur' },
  { key: 'societe', label: 'Société' },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
const COLS_CHARGES = [
  { key: 'IDCHARGE',    label: 'N° Charge', link: (r) => `https://cienco.ngrok.io/fiche_charge/${r.IDCHARGE}` },
  { key: 'date',        label: 'Date',           format: fmtDate, nowrap: true },
  { key: 'type_charge', label: 'Type de charge' },
  { key: 'projet',      label: 'Projet' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'montant_ht',  label: 'HT',  right: true, format: (v) => `${fmt(v)} MAD` },
  { key: 'montant',     label: 'TTC', right: true, format: (v) => `${fmt(v)} MAD` },
];

const pickBdc     = (r) => ({ IDBDC: r.IDBDC, date: r.date, projet: r.projet, fournisseur: r.fournisseur, Responsable: r.Responsable });
const pickBr      = (r) => ({ IDBR: r.IDBR, date: r.date, projet: r.projet, fournisseur: r.fournisseur, Responsable: r.Responsable, facture_vente_ids: r.facture_vente_ids || null });
const pickFv      = (r) => ({ IDFACTURE: r.IDFACTURE, date: r.date, projet: r.projet, societe_client: r.societe_client, Responsable: r.Responsable, rg_ttc: r.rg_ttc || 0 });
const pickFa      = (r) => ({ IDFACTURE: r.IDFACTURE, date: r.DateFacture, Fournisseur: r.Fournisseur, Societe: r.Societe });
const pickCheque  = (r) => ({ id: r.id, date: r.date_payment, fournisseur: r.fournisseur, societe: r.societe });
// Détails des cartes : montant total du document (gris) + reliquat restant (mis en avant, valueKey 'montant')
const withReliquat = (cols, docLabel, relLabel) => [
  ...cols.filter((c) => c.key !== 'montant'),
  { key: 'total_doc', label: docLabel, right: true, muted: true, total: true, format: (v) => `${fmt(v)} MAD` },
  { key: 'montant',   label: relLabel, right: true, format: (v) => `${fmt(v)} MAD` }
];
const COLS_BDC_REL = withReliquat(COLS_BDC, 'Montant BDC',     'Reliquat à réceptionner');
const COLS_BR_REL  = withReliquat(COLS_BR,  'Montant BR',      'Reliquat à facturer');
const COLS_FA_REL  = withReliquat(COLS_FA,  'Montant facture', 'Reliquat à payer');
const COLS_FV_REL  = withReliquat(COLS_FV,  'Montant facture', 'Reliquat à encaisser (hors RG)');
// pick + montant total du document (champ source selon le type de ligne)
const withDocTotal = (pick, key) => (r) => ({ ...pick(r), total_doc: Number(r[key]) || 0 });

const pickCharges = (r) => ({ IDCHARGE: r.IDCHARGE, date: r.date, type_charge: r.type_charge, projet: r.projet, Responsable: r.Responsable, montant_ht: r.montant_ht });

// Factures achat au grain facture × projet (kpi-detail type 'fa') — montant = part du projet (facturepart)
const COLS_FA_PART = [
  { key: 'IDFACTURE', label: 'ID', link: (r) => `https://cienco.ngrok.io/fiche_facture/${r.IDFACTURE}` },
  { key: 'NFacture', label: 'N° Facture', nowrap: true },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'projet', label: 'Projet' },
  { key: 'fournisseur', label: 'Fournisseur' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'montant', label: 'Montant', right: true, format: (v) => `${fmt(v)} MAD` }
];
const pickFaPart = (r) => ({ IDFACTURE: r.IDFACTURE, NFacture: r.NFacture, date: r.date, projet: r.projet, fournisseur: r.fournisseur, Responsable: r.Responsable });

// Demandes d'achat — PRIX_R jamais renseigné en base : suivi en lignes, pas en MAD
const COLS_DA = [
  { key: 'IDDA', label: 'N° DA', link: (r) => `https://cienco.ngrok.io/fiche_demande_achat/${r.IDDA}` },
  { key: 'date', label: 'Date', format: fmtDate, nowrap: true },
  { key: 'projet', label: 'Projet' },
  { key: 'Responsable', label: 'Responsable' },
  { key: 'SAISIE_PAR', label: 'Demandeur' },
  { key: 'nb_lignes', label: 'Lignes', right: true, muted: true, total: true },
  { key: 'nb_lignes_commandees', label: 'Commandées', right: true, total: true },
  { key: 'nb_lignes_restantes', label: 'À commander', right: true, total: true },
  { key: 'anciennete_jours', label: 'Ancienneté (j)', right: true }
];

/* ─── cellule de tableau cliquable → popup des documents qui composent le montant ── */
const DrillCell = ({ onClick, children, className, style }) => (
  <td className={className} style={{ ...style, cursor: 'pointer' }} onClick={onClick} title="Voir les documents">
    <span style={{ borderBottom: '1px dotted currentColor' }}>{children}</span>
  </td>
);
// même chose dans une carte cliquable (le clic ne remonte pas au filtre de la carte)
const DrillSpan = ({ onClick, children, style }) => (
  <span onClick={(e) => { e.stopPropagation(); onClick(); }} title="Voir les documents"
    style={{ cursor: 'pointer', borderBottom: '1px dotted currentColor', ...style }}>{children}</span>
);

/* ─── panneau détail KPI — colonnes dynamiques, jusqu'au document individuel (8/9) ── */
const KpiDetailPanel = ({ active, onClose }) => {
  const [sortKey, setSortKey] = useState(null);
  const [asc, setAsc] = useState(true);

  useEffect(() => { setSortKey(null); setAsc(true); }, [active?.title]);

  const rows = active?.rows || [];
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return asc ? av - bv : bv - av;
      return asc
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
  }, [rows, sortKey, asc]);

  const handleSort = (key) => {
    if (sortKey === key) setAsc((a) => !a);
    else { setSortKey(key); setAsc(true); }
  };

  const p = usePaginated(sortedRows);

  if (!active) return null;
  const { title, columns, valueKey, color, anchor, loading } = active;
  const colTotal = (key) => rows.reduce((a, r) => a + (Number(r[key]) || 0), 0);
  const total = colTotal(valueKey);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '8px 8px 0' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px 12px 0 0', width: '100%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fdf8f4', borderBottom: '1px solid #e8ddd4', flexShrink: 0, gap: 8 }}>
          <div style={{ fontWeight: 700, color: '#6d4e36', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            {title}
            {!loading && (
              <span style={{ background: '#f5ede4', color: '#6d4e36', fontSize: '0.65rem', border: '1px solid #e8ddd4', borderRadius: 10, padding: '1px 8px', fontWeight: 500 }}>
                {rows.length} ligne{rows.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {anchor && (
              <button
                onClick={() => { onClose(); setTimeout(() => scrollTo(anchor), 50); }}
                style={{ background: '#e6f0fb', color: '#1a5296', border: '1px solid #9ab8e0', borderRadius: 6, padding: '5px 10px', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}
              >→ Tableau complet</button>
            )}
            {!loading && rows.length > 0 && (
              <button
                onClick={() => exportExcel(sortedRows.map((r) => Object.fromEntries(columns.map((c) => [c.label, r[c.key]]))), title.toLowerCase().replace(/[^a-z0-9]+/gi, '_'))}
                style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', borderRadius: 6, padding: '5px 10px', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}
              >↓ Excel</button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #ddd', cursor: 'pointer', color: '#888', borderRadius: 6, padding: '5px 10px', fontSize: '0.85rem' }} title="Fermer">✕</button>
          </div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 50, textAlign: 'center' }}><Spinner animation="border" size="sm" /></div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa', fontSize: '0.9rem' }}>Aucune donnée</div>
          ) : (
            <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#fdf8f4', borderBottom: '2px solid #e8ddd4', position: 'sticky', top: 0 }}>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => handleSort(c.key)}
                      style={{
                        padding: '8px 14px', textAlign: c.right ? 'right' : 'left',
                        color: sortKey === c.key ? '#6d4e36' : '#888', fontSize: '0.7rem',
                        textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
                        cursor: 'pointer', userSelect: 'none'
                      }}
                    >
                      {c.label}{sortKey === c.key && (asc ? ' ▲' : ' ▼')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.visible.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fdfaf7', borderBottom: '1px solid #f5f0ea' }}>
                    {columns.map((c) => {
                      const v = r[c.key];
                      const display = c.format ? c.format(v) : (v ?? '—');
                      const isValue = c.key === valueKey;
                      return (
                        <td key={c.key} style={{
                          padding: '7px 14px', textAlign: c.right ? 'right' : 'left',
                          fontWeight: isValue ? 700 : (c.key === columns[0].key ? 500 : 400),
                          color: isValue ? (color || '#6d4e36') : c.muted ? '#9e9e9e' : undefined,
                          fontSize: c.muted ? '0.76rem' : undefined,
                          whiteSpace: c.nowrap ? 'nowrap' : undefined
                        }}>
                          {c.render
                            ? c.render(r, v)
                            : c.link && v != null
                              ? <a href={c.link(r)} target="_blank" rel="noreferrer" style={{ color: '#1565c0', fontWeight: 700, textDecoration: 'none' }}>{display}</a>
                              : display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f5ede4', borderTop: '2px solid #d4c8bc', fontWeight: 700 }}>
                  {columns.map((c, i) => (
                    <td key={c.key} style={{ padding: '8px 14px', textAlign: c.right ? 'right' : 'left', color: '#5c3317' }}>
                      {i === 0 ? 'TOTAL'
                        : c.key === valueKey ? (c.format ? c.format(total) : total)
                        : c.total ? (c.format ? c.format(colTotal(c.key)) : colTotal(c.key)) : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
            <TablePager {...p} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── client-side filter ─────────────────────────────────────────────────────── */
const applyFilters = (rawData, filters, relations) => {
  if (!rawData) return null;
  const projIds     = filters.projet?.length         ? filters.projet.map(p => String(p.value))      : null;
  const respLogin   = filters.responsable?.label     || null;
  const respId      = filters.responsable?.value     ? String(filters.responsable.value)     : null;
  const fourIds     = filters.fournisseur?.length     ? filters.fournisseur.map(f => String(f.value)) : null;
  const societeId   = filters.societe_client?.value  ? String(filters.societe_client.value)  : null;

  // Agrégation charges — toujours nécessaire car le backend retourne une ligne par (type, projet, responsable)
  const chargesRaw      = rawData.charges?.par_type || [];
  const chargesFiltered = chargesRaw.filter(r =>
    (!projIds   || projIds.includes(String(r.projet_id))) &&
    (!respLogin || r.Responsable === respLogin)
  );
  const chargesMap = {};
  for (const r of chargesFiltered) {
    if (!chargesMap[r.type_charge]) chargesMap[r.type_charge] = { type_charge: r.type_charge, montant_ht: 0, montant_ttc: 0 };
    chargesMap[r.type_charge].montant_ht  += r.montant_ht  || 0;
    chargesMap[r.type_charge].montant_ttc += r.montant_ttc || 0;
  }
  const chargesParType    = Object.values(chargesMap).sort((a, b) => b.montant_ttc - a.montant_ttc);
  const totalChargesTtc   = chargesParType.reduce((a, r) => a + r.montant_ttc, 0);

  if (!projIds && !respLogin && !fourIds && !societeId) {
    return { ...rawData, charges: { par_type: chargesParType } };
  }

  // projets liés au(x) fournisseur(s) sélectionné(s) (via relations BDC) — pour les sections BR vente
  const linkedProjIds = fourIds
    ? new Set((relations || []).filter(r => fourIds.includes(String(r.fournisseur_id))).map(r => String(r.projet_id)))
    : null;

  const bdcProjet = (rawData.bdc?.par_projet || []).filter(r =>
    (!projIds   || projIds.includes(String(r.projet_id))) &&
    (!respLogin || r.Responsable           === respLogin) &&
    (!fourIds   || fourIds.includes(String(r.IDFOURNISSEUR)))
  );
  const bdcFour = (rawData.bdc?.par_fournisseur || []).filter(r =>
    (!fourIds || fourIds.includes(String(r.IDFOURNISSEUR))) &&
    (!projIds || (relations || []).some(rel => String(rel.fournisseur_id) === String(r.IDFOURNISSEUR) && projIds.includes(String(rel.projet_id)))) &&
    (!respId || (relations || []).some(rel => String(rel.fournisseur_id) === String(r.IDFOURNISSEUR) && String(rel.responsable_id) === respId))
  );
  const brProjet = (rawData.br?.par_projet || []).filter(r =>
    (!projIds       || projIds.includes(String(r.projet_id))) &&
    (!respLogin     || r.Responsable       === respLogin) &&
    (!linkedProjIds || linkedProjIds.has(String(r.projet_id)))
  );
  const faProjet = (rawData.factures_achat?.par_projet || []).filter(r =>
    (!projIds   || projIds.includes(String(r.projet_id))) &&
    (!respLogin || r.Responsable           === respLogin) &&
    (!fourIds   || fourIds.includes(String(r.IDFOURNISSEUR)))
  );
  const fvProjet = (rawData.factures_vente?.par_projet || []).filter(r =>
    (!projIds       || projIds.includes(String(r.projet_id))) &&
    (!respLogin     || r.Responsable              === respLogin) &&
    (!societeId     || String(r.societe_client_id) === societeId) &&
    (!linkedProjIds || linkedProjIds.has(String(r.projet_id)))
  );
  const brFvFour = (rawData.br_facturation_vente?.par_fournisseur || []).filter(r =>
    (!projIds   || projIds.includes(String(r.projet_id))) &&
    (!respLogin || r.Responsable           === respLogin) &&
    (!fourIds   || fourIds.includes(String(r.fournisseur_id)))
  );
  const brFvProjet = (rawData.br_facturation_vente?.par_projet || []).filter(r =>
    (!projIds       || projIds.includes(String(r.projet_id))) &&
    (!respLogin     || r.Responsable       === respLogin) &&
    (!linkedProjIds || linkedProjIds.has(String(r.projet_id)))
  );
  const feRows = (rawData.factures_echeance || []).filter(r =>
    (!projIds   || String(r.projet_ids || '').split(',').some(id => projIds.includes(id))) &&
    (!respLogin || String(r.Responsables || '').split(', ').includes(respLogin)) &&
    (!fourIds   || fourIds.includes(String(r.IDFOURNISSEUR)))
  );
  const checksDetail = (rawData.checks_sans_facture?.detail || []).filter(r =>
    (!fourIds   || fourIds.includes(String(r.IDFOURNISSEUR))) &&
    (!respLogin || String(r.responsable_noms || '').split(',').map(s => s.trim()).includes(respLogin))
  );
  const totaux = {
    total_commandes_ttc:       bdcProjet.reduce((a, r) => a + (r.total_commandes_ttc        || 0), 0),
    receptionnes_ttc:          bdcProjet.reduce((a, r) => a + (r.receptionnes_ttc           || 0), 0),
    non_receptionnes_ttc:      bdcProjet.reduce((a, r) => a + (r.non_receptionnes_ttc       || 0), 0),
    total_reception_ttc:       brProjet.reduce((a, r) => a + (r.total_reception_ttc         || 0), 0),
    facturees_ttc:             brProjet.reduce((a, r) => a + (r.facturees_ttc               || 0), 0),
    non_facturees_ttc:         brProjet.reduce((a, r) => a + (r.non_facturees_ttc           || 0), 0),
    total_factures_achat_ttc:  faProjet.reduce((a, r) => a + (r.total_facturation_ttc       || 0), 0),
    factures_achat_payees_ttc: faProjet.reduce((a, r) => a + (r.payes_ttc                  || 0), 0),
    factures_achat_non_payees: faProjet.reduce((a, r) => a + (r.non_payes_ttc              || 0), 0),
    total_ca_vente_ttc:        fvProjet.reduce((a, r) => a + (r.total_facturation_vente_ttc || 0), 0),
    ca_vente_paye_ttc:         fvProjet.reduce((a, r) => a + (r.payes_ttc                  || 0), 0),
    ca_vente_non_paye_ttc:     fvProjet.reduce((a, r) => a + (r.non_payes_ttc              || 0), 0),
    ca_vente_rg_ttc:           fvProjet.reduce((a, r) => a + (r.rg_retenue_ttc             || 0), 0),
    total_charges_ttc:         totalChargesTtc,
    total_reception_br_fv_ttc:  brFvProjet.reduce((a, r) => a + (r.total_reception_ttc || 0), 0),
    facturees_br_vente_ttc:     brFvProjet.reduce((a, r) => a + (r.facturees_vente_ttc || 0), 0),
    non_facturees_br_vente_ttc: brFvProjet.reduce((a, r) => a + (r.non_facturees_vente_ttc || 0), 0),
    reliquat_br_vente_ttc:      brFvProjet.reduce((a, r) => a + (r.reliquat_vente_ttc      || 0), 0),
    rg_br_vente_ttc:            brFvProjet.reduce((a, r) => a + (r.rg_vente_ttc            || 0), 0),
    nb_cheques_sans_facture:      checksDetail.length,
    montant_cheques_sans_facture: checksDetail.reduce((a, r) => a + (r.montant || 0), 0),
    nb_cheques_critique:          checksDetail.filter(r => r.statut_alerte === 'critique').length,
    montant_cheques_critique:     checksDetail.filter(r => r.statut_alerte === 'critique').reduce((a, r) => a + (r.montant || 0), 0),
    nb_cheques_minimal:           checksDetail.filter(r => r.statut_alerte === 'minimal').length,
    montant_cheques_minimal:      checksDetail.filter(r => r.statut_alerte === 'minimal').reduce((a, r) => a + (r.montant || 0), 0)
  };

  const checksFourSocFiltered = (rawData.checks_sans_facture?.par_fournisseur_societe || []).filter(r =>
    (!fourIds || fourIds.includes(String(r.IDFOURNISSEUR))) &&
    (!respId  || String(r.responsable_ids_list || '').split(',').includes(String(respId)))
  );
  const checksDetailFiltered = (rawData.checks_sans_facture?.detail || []).filter(r =>
    (!fourIds   || fourIds.includes(String(r.IDFOURNISSEUR))) &&
    (!respLogin || String(r.responsable_noms || '').split(',').map(s => s.trim()).includes(respLogin))
  );

  return {
    ...rawData, totaux,
    bdc:            { par_projet: bdcProjet, par_fournisseur: bdcFour },
    br:             { par_projet: brProjet },
    factures_achat: {
      par_projet: faProjet,
      par_fournisseur: (rawData.factures_achat?.par_fournisseur || []).filter(r =>
        (!fourIds || fourIds.includes(String(r.IDFOURNISSEUR)))
      )
    },
    factures_vente: { par_projet: fvProjet },
    br_facturation_vente: { par_projet: brFvProjet, par_fournisseur: brFvFour },
    factures_echeance: feRows,
    charges: { par_type: chargesParType },
    checks_sans_facture: {
      par_fournisseur_societe: checksFourSocFiltered,
      detail: checksDetailFiltered
    }
  };
};

/* ─── default filter state ───────────────────────────────────────────────────── */
const DEFAULT_FILTERS = {
  date_debut: '2025-01-01',
  date_fin: today(),
  projet: null,
  responsable: null,
  fournisseur: null,
  societe_client: null,
  archived: 'non_archive',
  societe_scope: 'rutas_pistas',
  societes_masquees: null // null = défaut serveur (GASODIS masquée) ; sinon tableau d'ids societe
};

// Clé de comparaison/cache des sociétés masquées (null = défaut serveur)
const masqueesKey = (ids) => (ids == null ? 'defaut' : [...ids].sort((a, b) => a - b).join(','));

// Options du toggle archivage — s'applique à BDC / BR / factures achat / charges.
// N'affecte PAS la section "Factures vente / CA" (mern_cienco.facturegroup n'a pas de colonne archived).
const ARCHIVED_OPTIONS = [
  { value: 'non_archive', label: 'Sans archivé' },
  { value: 'archive',     label: 'Archivé' },
  { value: 'tous',        label: 'Tout' }
];

// Options du toggle société — compare {bdc,br,facture}.societeId à la société CLIENT du chantier
// (chantier.mainSocietealtId, résolu vers son équivalent societe.id par nom). S'applique à BDC / BR / factures achat.
// N'affecte PAS la section "Factures vente / CA" (facturegroup non couvert, exclu par choix produit).
const SOCIETE_SCOPE_OPTIONS = [
  { value: 'meme',         label: 'Vers elle-même' },
  { value: 'autre',        label: 'Vers les autres' },
  { value: 'tous',         label: 'Tout' },
  { value: 'rutas_pistas', label: 'Vers RUTAS/PISTAS' }
];

// Ordre par défaut des 11 sections-tableaux réordonnables par drag & drop (10)
const SECTION_ORDER_DEFAULT = [
  'bdc-projet', 'bdc-fournisseur', 'br', 'br-fv', 'br-fv-four',
  'fa', 'fa-four', 'fv', 'charges', 'factures-echeance', 'checks'
];

const CACHE_KEY_BR   = 'budget_realisation_cache';
const DATES_KEY_BR   = 'budget_realisation_dates';
const FILTERS_KEY_BR = 'budget_realisation_filters';
const CACHE_VERSION = 9; // incrémenter à chaque ajout de champ dans rawData (5 : filtre archivage, 6 : filtre société, 7 : sociétés masquées, 8 : RG factures vente, 9 : RG BR fact. vente)

const getSavedDates = () => {
  try {
    const s = localStorage.getItem(DATES_KEY_BR);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

// Filtres actifs persistés (15) — projet / responsable / fournisseur / société client
const getSavedFilters = () => {
  try {
    const s = localStorage.getItem(FILTERS_KEY_BR);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

/* ─── helpers KPI score (partagés avec le widget en haut et les cartes resp) ── */
const kpiScore  = (val, dir) => dir === 'h' ? val : 100 - val;
const getKpiNote = (p) => {
  if (p >= 90) return { emoji: '👏', label: 'Excellence',  color: '#1b5e20', bg: '#e8f5e9', border: '#a5d6a7' };
  if (p >= 80) return { emoji: '👍', label: 'Bon travail', color: '#2e7d32', bg: '#f1f8e9', border: '#c5e1a5' };
  if (p >= 50) return { emoji: '🤏', label: 'Peut mieux',  color: '#e65100', bg: '#fff3e0', border: '#ffcc80' };
  return              { emoji: '👎', label: 'Alerte',       color: '#b71c1c', bg: '#ffebee', border: '#ef9a9a' };
};

/* ─── RespAvatar — avatar circulaire d'un responsable ──────────────────────── */
const RespAvatar = ({ docId, name, size = 52 }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!docId) { setPhotoUrl(null); return; }
    let revoked = false;
    let blobUrl = null;
    documentService.getDocumentObjectUrl(docId)
      .then(url => {
        if (revoked) { URL.revokeObjectURL(url); return; }
        blobUrl = url;
        setPhotoUrl(url);
      })
      .catch(() => setPhotoUrl(null));
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [docId]);

  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: '2.5px solid #7b1fa2',
      overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#f3e5f5',
      fontSize: Math.round(size * 0.32), fontWeight: 'bold', color: '#7b1fa2',
      margin: '0 auto 8px', flexShrink: 0,
      boxShadow: '0 2px 6px rgba(123,31,162,0.2)'
    }}>
      {photoUrl
        ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initials}
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────────────────── */
export default function BudgetRealisationPage({ user }) {
  // budget-realisation:all débloque la vision complète (tous chantiers/responsables) même pour responsable_budget
  const isRespBudget = user?.role === 'responsable_budget' && !canAccess(user, 'budget-realisation:all');
  const navigate = useNavigate();
  // Lecture du cache au montage — invalidé si version obsolète
  const [_initCache] = useState(() => {
    try {
      const r = localStorage.getItem(CACHE_KEY_BR);
      if (!r) return null;
      const parsed = JSON.parse(r);
      if (parsed.v !== CACHE_VERSION) { localStorage.removeItem(CACHE_KEY_BR); return null; }
      return parsed;
    } catch { return null; }
  });

  const rawDataRef = useRef(_initCache?.rawData || null);

  const [data, setData]         = useState(_initCache?.rawData || null);
  const [loading, setLoading]   = useState(!_initCache?.rawData);
  const [error, setError]       = useState(null);
  const [cachedAt, setCachedAt] = useState(_initCache?.cachedAt || null);

  const [pending, setPending] = useState(() => {
    const saved        = getSavedDates();
    const savedFilters = getSavedFilters();
    return {
      ...DEFAULT_FILTERS,
      ...(savedFilters || {}),
      ...(saved ? {
        date_debut: saved.date_debut,
        archived: saved.archived || DEFAULT_FILTERS.archived,
        societe_scope: saved.societe_scope || DEFAULT_FILTERS.societe_scope,
        societes_masquees: saved.societes_masquees ?? DEFAULT_FILTERS.societes_masquees
      } : {}),
      date_fin: today()
    };
  });
  const [applied, setApplied] = useState(() => {
    const savedFilters = getSavedFilters();
    return {
      projet: savedFilters?.projet || null,
      responsable: savedFilters?.responsable || null,
      fournisseur: savedFilters?.fournisseur || null,
      societe_client: savedFilters?.societe_client || null
    };
  });
  const [lastFetchedDates, setLastFetchedDates] = useState(() => {
    const saved = getSavedDates();
    return saved
      ? {
          debut: saved.date_debut, fin: today(),
          archived: saved.archived || DEFAULT_FILTERS.archived,
          societe_scope: saved.societe_scope || DEFAULT_FILTERS.societe_scope,
          societes_masquees: saved.societes_masquees ?? DEFAULT_FILTERS.societes_masquees
        }
      : {
          debut: DEFAULT_FILTERS.date_debut, fin: today(),
          archived: DEFAULT_FILTERS.archived,
          societe_scope: DEFAULT_FILTERS.societe_scope,
          societes_masquees: DEFAULT_FILTERS.societes_masquees
        };
  });

  const metaRef = useRef(_initCache?.meta || { projets: [], responsables: [], fournisseurs: [], relations: [], societes: [] });
  const [metaLoaded, setMetaLoaded] = useState(!!_initCache?.meta);
  const [brDetailRows, setBrDetailRows] = useState(null);
  const [daDetailRows, setDaDetailRows] = useState(null);

  // Cache du détail ligne par ligne (BDC/BR/factures vente) — préchargé en même temps que le reste (15bis)
  const kpiDetailCacheRef     = useRef({});
  const lastFetchedDatesRef   = useRef(null); // ref stable pour accès dans les effets sans dépendance cyclique

  const prewarmKpiDetail = (debut, fin, archived, societeScope, masquees, respLogin) => {
    const rl = respLogin || '';
    ['bdc', 'br', 'factures_vente', 'charges', 'fa', 'da'].forEach((type) => {
      const key = `${type}:${debut}:${fin}:${archived}:${societeScope}:${masqueesKey(masquees)}:${rl}`;
      if (kpiDetailCacheRef.current[key]) {
        if (type === 'br') setBrDetailRows(kpiDetailCacheRef.current[key]);
        if (type === 'da') setDaDetailRows(kpiDetailCacheRef.current[key]);
        return;
      }
      const params = { type, date_debut: debut, date_fin: fin, archived, societe_scope: societeScope };
      if (masquees != null) params.societes_masquees = masquees.join(',');
      if (rl) params.responsable_login = rl;
      api.get('/dashboard/budget-realisation/kpi-detail', { params })
        .then(({ data: d }) => {
          const rows = d.rows || [];
          kpiDetailCacheRef.current[key] = rows;
          if (type === 'br') setBrDetailRows(rows);
          if (type === 'da') setDaDetailRows(rows);
        })
        .catch(() => {});
    });
  };

  const fetchData = useCallback(async (debut, fin, dropFilters, archived, societeScope, masquees) => {
    setLoading(true);
    setError(null);
    setBrDetailRows(null);
    setDaDetailRows(null);
    try {
      const params = new URLSearchParams({
        date_debut: debut, date_fin: fin,
        archived: archived || DEFAULT_FILTERS.archived,
        societe_scope: societeScope || DEFAULT_FILTERS.societe_scope
      });
      if (masquees != null) params.set('societes_masquees', masquees.join(','));
      const { data: d } = await api.get(`/dashboard/budget-realisation?${params}`);

      rawDataRef.current = d;
      setData(applyFilters(d, dropFilters, metaRef.current.relations));
      const effArchived = d.archived || archived || DEFAULT_FILTERS.archived;
      const effScope     = d.societe_scope || societeScope || DEFAULT_FILTERS.societe_scope;
      const effMasquees  = d.societes_masquees || [];
      const fetchedDates = { debut, fin, archived: effArchived, societe_scope: effScope, societes_masquees: effMasquees };
      setLastFetchedDates(fetchedDates);
      lastFetchedDatesRef.current = fetchedDates;
      // défaut serveur (null) → on reprend la liste effective renvoyée pour afficher les sociétés masquées
      setPending((p) => (p.societes_masquees == null ? { ...p, societes_masquees: effMasquees } : p));
      prewarmKpiDetail(debut, fin, effArchived, effScope, effMasquees, dropFilters?.responsable?.label || null);

      if (d.meta) {
        const newMeta = {
          projets:      (d.meta.projets      || []).map((r) => ({ value: r.id, label: r.libelle })),
          responsables: (d.meta.responsables  || []).map((r) => ({ value: r.id, label: r.login })),
          fournisseurs: (d.meta.fournisseurs  || []).map((r) => ({ value: r.id, label: r.libelle })),
          relations:    d.meta.relations || [],
          societes:     (d.meta.societes || []).map((r) => ({ value: r.id, label: r.name, logo: r.logo || null }))
        };
        metaRef.current = newMeta;
        setMetaLoaded(true);
        try {
          const ts = Date.now();
          localStorage.setItem(CACHE_KEY_BR, JSON.stringify({ v: CACHE_VERSION, rawData: d, meta: newMeta, cachedAt: ts }));
          setCachedAt(ts);
        } catch {}
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const saved         = getSavedDates();
    const savedFilters  = getSavedFilters();
    const debut         = saved?.date_debut    || DEFAULT_FILTERS.date_debut;
    const fin           = saved?.date_fin      || DEFAULT_FILTERS.date_fin;
    const archived      = saved?.archived      || DEFAULT_FILTERS.archived;
    const societe_scope = saved?.societe_scope || DEFAULT_FILTERS.societe_scope;
    const masquees      = saved?.societes_masquees ?? DEFAULT_FILTERS.societes_masquees;
    fetchData(debut, fin, savedFilters || { projet: null, responsable: null, fournisseur: null }, archived, societe_scope, masquees);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chargement des photos de profil — clé = login (commun entre mern_cienco et shemspaiedb)
  useEffect(() => {
    api.get('/users/avatars')
      .then(r => {
        const map = {};
        (r.data || []).forEach(u => { if (u.photo_profil && u.login) map[u.login] = u.photo_profil; });
        setAvatarsMap(map);
      })
      .catch(() => {});
  }, []);

  // Re-fetch kpi-detail BR quand le filtre responsable change (pour cohérence cross-chips admin vs resp_budget)
  useEffect(() => {
    const d = lastFetchedDatesRef.current;
    if (!d) return;
    prewarmKpiDetail(d.debut, d.fin, d.archived, d.societe_scope, d.societes_masquees, applied.responsable?.label || null);
  }, [applied.responsable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Réinitialiser = vide les dropdowns + restaure rawData, aucun appel API
  const handleReset = () => {
    const reset = { ...pending, projet: null, responsable: null, fournisseur: null, societe_client: null };
    setPending(reset);
    setApplied({ projet: null, responsable: null, fournisseur: null, societe_client: null });
    if (rawDataRef.current) setData(rawDataRef.current);
    try { localStorage.removeItem(FILTERS_KEY_BR); } catch {}
  };

  // Actualiser = appel API avec les dates + filtres archivage/société courants, puis applique les dropdowns
  const handleRefresh = () => {
    try {
      localStorage.setItem(DATES_KEY_BR, JSON.stringify({
        date_debut: pending.date_debut, date_fin: pending.date_fin,
        archived: pending.archived, societe_scope: pending.societe_scope,
        societes_masquees: pending.societes_masquees
      }));
    } catch {}
    setApplied({ projet: pending.projet, responsable: pending.responsable, fournisseur: pending.fournisseur });
    fetchData(pending.date_debut, pending.date_fin, pending, pending.archived, pending.societe_scope, pending.societes_masquees);
  };

  const datesDirty = pending.date_debut !== lastFetchedDates.debut
    || pending.date_fin !== lastFetchedDates.fin
    || pending.archived !== lastFetchedDates.archived
    || pending.societe_scope !== lastFetchedDates.societe_scope
    || (pending.societes_masquees != null && masqueesKey(pending.societes_masquees) !== masqueesKey(lastFetchedDates.societes_masquees));
  const isFiltered     = !!(applied.projet?.length || applied.responsable || applied.fournisseur?.length || applied.societe_client);

  /* ─── filtres cascadants ─────────────────────────────────────────────────────── */
  const relations = metaRef.current.relations;

  const responsableOptions = useMemo(() => {
    if (!metaLoaded) return [];
    if (!pending.projet?.length && !pending.fournisseur?.length) return metaRef.current.responsables;
    return metaRef.current.responsables.filter((opt) =>
      relations.some((r) =>
        (!pending.projet?.length      || pending.projet.some((p) => String(r.projet_id)      === String(p.value))) &&
        (!pending.fournisseur?.length || pending.fournisseur.some((f) => String(r.fournisseur_id) === String(f.value))) &&
        String(r.responsable_id) === String(opt.value)
      )
    );
  }, [pending.projet, pending.fournisseur, metaLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const projetOptions = useMemo(() => {
    if (!metaLoaded) return [];
    if (!pending.responsable && !pending.fournisseur?.length) return metaRef.current.projets;
    return metaRef.current.projets.filter((opt) =>
      relations.some((r) =>
        (!pending.responsable        || String(r.responsable_id) === String(pending.responsable.value)) &&
        (!pending.fournisseur?.length || pending.fournisseur.some((f) => String(r.fournisseur_id) === String(f.value))) &&
        String(r.projet_id) === String(opt.value)
      )
    );
  }, [pending.responsable, pending.fournisseur, metaLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const fournisseurOptions = useMemo(() => {
    if (!metaLoaded) return [];
    if (!pending.responsable && !pending.projet?.length) return metaRef.current.fournisseurs;
    return metaRef.current.fournisseurs.filter((opt) =>
      relations.some((r) =>
        (!pending.responsable   || String(r.responsable_id) === String(pending.responsable.value)) &&
        (!pending.projet?.length || pending.projet.some((p) => String(r.projet_id) === String(p.value))) &&
        String(r.fournisseur_id) === String(opt.value)
      )
    );
  }, [pending.responsable, pending.projet, metaLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const societeMasqueeOptions = useMemo(
    () => (metaRef.current.societes || []).map((s) => ({ value: s.value, label: s.label })),
    [metaLoaded, data] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const societeClientOptions = useMemo(() => {
    const rows = rawDataRef.current?.factures_vente?.par_projet || [];
    const seen = new Set();
    return rows
      .filter(r => r.societe_client_id != null)
      .filter(r => { const k = r.societe_client_id; if (seen.has(k)) return false; seen.add(k); return true; })
      .map(r => ({ value: r.societe_client_id, label: r.societe_client }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyImmediate = (next) => {
    setPending(next);
    setApplied({ projet: next.projet, responsable: next.responsable, fournisseur: next.fournisseur, societe_client: next.societe_client });
    if (rawDataRef.current) setData(applyFilters(rawDataRef.current, next, metaRef.current.relations));
    try { localStorage.setItem('budget_realisation_filters', JSON.stringify(next)); } catch {}
  };

  const onChangeResponsable = (v) => {
    const next = { ...pending, responsable: v };
    if (v) {
      if (next.projet?.length) {
        const keep = next.projet.filter((p) => relations.some((r) => String(r.responsable_id) === String(v.value) && String(r.projet_id) === String(p.value)));
        next.projet = keep.length ? keep : null;
      }
      if (next.fournisseur?.length) {
        const keep = next.fournisseur.filter((f) => relations.some((r) => String(r.responsable_id) === String(v.value) && String(r.fournisseur_id) === String(f.value)));
        next.fournisseur = keep.length ? keep : null;
      }
    }
    applyImmediate(next);
  };

  const onChangeProjet = (v) => {
    const next = { ...pending, projet: v && v.length ? v : null };
    if (next.projet) {
      if (next.responsable && !next.projet.some((p) => relations.some((r) => String(r.projet_id) === String(p.value) && String(r.responsable_id) === String(next.responsable.value))))
        next.responsable = null;
      if (next.fournisseur?.length) {
        const keep = next.fournisseur.filter((f) => next.projet.some((p) => relations.some((r) => String(r.projet_id) === String(p.value) && String(r.fournisseur_id) === String(f.value))));
        next.fournisseur = keep.length ? keep : null;
      }
    }
    applyImmediate(next);
  };

  const onChangeSocieteClient = (v) => {
    applyImmediate({ ...pending, societe_client: v });
  };

  const onChangeFournisseur = (v) => {
    const next = { ...pending, fournisseur: v && v.length ? v : null };
    if (next.fournisseur) {
      if (next.responsable && !next.fournisseur.some((f) => relations.some((r) => String(r.fournisseur_id) === String(f.value) && String(r.responsable_id) === String(next.responsable.value))))
        next.responsable = null;
      if (next.projet?.length) {
        const keep = next.projet.filter((p) => next.fournisseur.some((f) => relations.some((r) => String(r.fournisseur_id) === String(f.value) && String(r.projet_id) === String(p.value))));
        next.projet = keep.length ? keep : null;
      }
    }
    applyImmediate(next);
  };

  /* ─── données brutes ────────────────────────────────────────────────────────── */
  const totaux       = data?.totaux          || {};
  const bdcProjets   = data?.bdc?.par_projet      || [];
  const bdcFours     = data?.bdc?.par_fournisseur  || [];
  const brProjets    = data?.br?.par_projet        || [];
  const faProjets    = data?.factures_achat?.par_projet      || [];
  const faFours      = useMemo(() =>
    [...(data?.factures_achat?.par_fournisseur || [])].sort((a, b) => b.non_payes_ttc - a.non_payes_ttc),
    [data]
  );
  const fvProjets    = data?.factures_vente?.par_projet || [];
  const brFvProjets  = data?.br_facturation_vente?.par_projet      || [];
  const brFvFours    = data?.br_facturation_vente?.par_fournisseur || [];
  const chargesTypes      = data?.charges?.par_type       || [];
  const facturesEcheance  = data?.factures_echeance       || [];
  const checksFourSoc     = data?.checks_sans_facture?.par_fournisseur_societe || [];
  const checksDetail      = data?.checks_sans_facture?.detail                  || [];

  /* ─── tri + pagination par tableau ─────────────────────────────────────────── */
  const [echeanceFilter, setEcheanceFilter] = useState(null); // 'depasse'|'proche'|'ok'|'paye'|null
  const [showAllResp, setShowAllResp] = useState(false);
  const [showAllSociete, setShowAllSociete] = useState(false);
  const [avatarsMap, setAvatarsMap] = useState({});

  /* ─── balance interco ───────────────────────────────────────────────────────── */
  const [balanceSocA,    setBalanceSocA]    = useState(null);
  const [balanceSocB,    setBalanceSocB]    = useState(null);
  const [balanceData,    setBalanceData]    = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError,   setBalanceError]   = useState(null);
  const [balanceFilterA, setBalanceFilterA] = useState([]); // chantier_id(s) sélectionné(s) côté A (multi-sélection)
  const [balanceFilterB, setBalanceFilterB] = useState([]); // chantier_id(s) sélectionné(s) côté B (multi-sélection)
  const [balanceHeaderTooltip, setBalanceHeaderTooltip] = useState(null); // { label, chantiers, x, y }
  const [balanceHeaderPinned,  setBalanceHeaderPinned]  = useState(false);

  const fetchBalance = useCallback(async (socA, socB) => {
    if (!socA || !socB) return;
    setBalanceLoading(true);
    setBalanceError(null);
    setBalanceFilterA([]);
    setBalanceFilterB([]);
    try {
      const params = new URLSearchParams({
        date_debut: pending.date_debut,
        date_fin:   pending.date_fin,
        societe_a:  socA.value,
        societe_b:  socB.value
      });
      const { data: d } = await api.get(`/dashboard/balance-interco?${params}`);
      setBalanceData(d);
    } catch (e) {
      setBalanceError(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setBalanceLoading(false);
    }
  }, [pending.date_debut, pending.date_fin]); // eslint-disable-line react-hooks/exhaustive-deps

  const facturesEcheanceFiltered = useMemo(() =>
    echeanceFilter ? facturesEcheance.filter(r => r.statut_echeance === echeanceFilter) : facturesEcheance,
    [facturesEcheance, echeanceFilter]
  );

  const sBdcProjet  = useSortable(bdcProjets);
  const sBdcFour    = useSortable(bdcFours);
  const sBr         = useSortable(brProjets);
  const sFa         = useSortable(faProjets);
  const sFaFour     = useSortable(faFours);
  const sFv         = useSortable(fvProjets);
  const sBrFv       = useSortable(brFvProjets, 'facturees_vente_ttc', false);
  const sBrFvFour   = useSortable(brFvFours, 'facturees_vente_ttc', false);
  const sCharges    = useSortable(chargesTypes);

  const pBdcProjet  = usePaginated(sBdcProjet.sorted);
  const pBdcFour    = usePaginated(sBdcFour.sorted);
  const pBr         = usePaginated(sBr.sorted);
  const pFa         = usePaginated(sFa.sorted);
  const pFaFour     = usePaginated(sFaFour.sorted);
  const pFv         = usePaginated(sFv.sorted);
  const pBrFv       = usePaginated(sBrFv.sorted);
  const pBrFvFour   = usePaginated(sBrFvFour.sorted);
  const pCharges    = usePaginated(sCharges.sorted);

  const sFe = useSortable(facturesEcheanceFiltered, 'DateEcheance', true);
  const pFe = usePaginated(sFe.sorted);

  /* ─── repli/dépli des sections tableau — persisté en localStorage (16) ──────── */
  const [collapseMap, setCollapseMap] = useState(() => {
    try {
      const s = localStorage.getItem('budget_section_collapse');
      return s ? JSON.parse(s) : {};
    } catch { return {}; }
  });
  const makeOpenState = (id) => [
    collapseMap[id] !== false,
    (valOrFn) => setCollapseMap((prev) => {
      const cur  = prev[id] !== false;
      const next = { ...prev, [id]: typeof valOrFn === 'function' ? valOrFn(cur) : valOrFn };
      try { localStorage.setItem('budget_section_collapse', JSON.stringify(next)); } catch {}
      return next;
    })
  ];
  const [openBdcProjet,  setOpenBdcProjet]  = makeOpenState('bdc-projet');
  const [openBdcFour,    setOpenBdcFour]    = makeOpenState('bdc-fournisseur');
  const [openBr,         setOpenBr]         = makeOpenState('br');
  const [openBrFv,       setOpenBrFv]       = makeOpenState('br-fv');
  const [openBrFvFour,   setOpenBrFvFour]   = makeOpenState('br-fv-four');
  const [openFa,         setOpenFa]         = makeOpenState('fa');
  const [openFaFour,     setOpenFaFour]     = makeOpenState('fa-four');
  const [openFv,         setOpenFv]         = makeOpenState('fv');
  const [openCharges,    setOpenCharges]    = makeOpenState('charges');
  const [openFe,         setOpenFe]         = makeOpenState('factures-echeance');
  const [openChecksFour, setOpenChecksFour] = makeOpenState('checks-four');
  const [openChecksDet,  setOpenChecksDet]  = makeOpenState('checks-det');

  /* ─── unité d'affichage (MAD/kMAD/MMAD) par section — persistée (12) ────────── */
  const [unitMap, setUnitMap] = useState(() => {
    try {
      const s = localStorage.getItem('budget_section_unit');
      return s ? JSON.parse(s) : {};
    } catch { return {}; }
  });
  const getUnit = (id) => unitMap[id] || 'mad';
  const setUnit = (id, val) => setUnitMap((prev) => {
    const next = { ...prev, [id]: val };
    try { localStorage.setItem('budget_section_unit', JSON.stringify(next)); } catch {}
    return next;
  });

  /* ─── drag & drop des 11 sections-tableaux — ordre persisté (10) ────────────── */
  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const s = localStorage.getItem('budget_section_order');
      const arr = s ? JSON.parse(s) : null;
      if (Array.isArray(arr) && arr.length === SECTION_ORDER_DEFAULT.length && SECTION_ORDER_DEFAULT.every((id) => arr.includes(id)))
        return arr;
    } catch {}
    return SECTION_ORDER_DEFAULT;
  });
  const [dragSecId, setDragSecId]       = useState(null);
  const [dragOverSecId, setDragOverSecId] = useState(null);

  const moveSectionTo = (targetId) => {
    setDragOverSecId(null);
    if (!dragSecId || dragSecId === targetId) return;
    setSectionOrder((prev) => {
      const next = prev.filter((id) => id !== dragSecId);
      next.splice(next.indexOf(targetId), 0, dragSecId);
      try { localStorage.setItem('budget_section_order', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const sectionWrapStyle = (id) => ({
    order: sectionOrder.indexOf(id),
    opacity: dragSecId === id ? 0.4 : 1,
    outline: dragOverSecId === id && dragSecId !== id ? '2px dashed #6d4e36' : '2px solid transparent',
    borderRadius: 10, transition: 'opacity 0.15s, outline 0.1s'
  });
  const sectionDropProps = (id) => ({
    onDragOver: (e) => { e.preventDefault(); setDragOverSecId(id); },
    onDrop: (e) => { e.preventDefault(); moveSectionTo(id); }
  });
  const sectionDragHandle = (id) => ({
    onDragStart: () => setDragSecId(id),
    onDragEnd: () => { setDragSecId(null); setDragOverSecId(null); }
  });

  /* ─── panneau détail KPI — détail document, jusqu'à l'ID (8/9) ──────────────── */
  const [kpiDetail, setKpiDetail] = useState(null);

  // Applique les filtres actifs (projet/fournisseur/responsable/société client) aux lignes brutes
  // chargées à la demande (BDC/BR/factures vente ne sont pas pré-filtrées côté client comme `data`)
  const filterByActive = (rows) => {
    const projIds   = applied.projet?.length      ? applied.projet.map((p) => String(p.value))      : null;
    const fourIds   = applied.fournisseur?.length  ? applied.fournisseur.map((f) => String(f.value)) : null;
    const respLogin = applied.responsable?.label   || null;
    const societeId = applied.societe_client?.value != null ? String(applied.societe_client.value) : null;
    return (rows || []).filter((r) =>
      (!projIds   || projIds.includes(String(r.projet_id))) &&
      (!fourIds   || r.fournisseur_id === undefined || fourIds.includes(String(r.fournisseur_id))) &&
      (!respLogin || r.Responsable === respLogin) &&
      (!societeId || r.societe_client_id === undefined || String(r.societe_client_id) === societeId)
    );
  };

  // Récupère (et mémorise) le détail ligne par ligne BDC/BR/factures vente/charges pour la période en cours
  const fetchKpiDetailRaw = async (type) => {
    const rl  = applied.responsable?.label || '';
    const key = `${type}:${lastFetchedDates.debut}:${lastFetchedDates.fin}:${lastFetchedDates.archived}:${lastFetchedDates.societe_scope}:${masqueesKey(lastFetchedDates.societes_masquees)}:${rl}`;
    if (kpiDetailCacheRef.current[key]) return kpiDetailCacheRef.current[key];
    const params = {
      type, date_debut: lastFetchedDates.debut, date_fin: lastFetchedDates.fin,
      archived: lastFetchedDates.archived, societe_scope: lastFetchedDates.societe_scope
    };
    if (lastFetchedDates.societes_masquees != null) params.societes_masquees = lastFetchedDates.societes_masquees.join(',');
    if (rl) params.responsable_login = rl;
    const { data: d } = await api.get('/dashboard/budget-realisation/kpi-detail', { params });
    const rows = d.rows || [];
    kpiDetailCacheRef.current[key] = rows;
    return rows;
  };

  // Panneau synchrone — données déjà chargées (chèques, factures achat par échéance, agrégats par projet)
  const openKpiDetail = (title, rows, columns, valueKey, color, anchor) =>
    setKpiDetail({ title, rows, columns, valueKey, color, anchor, loading: false });

  // Panneau asynchrone — détail BDC/BR/factures vente chargé à la demande puis filtré + mappé
  const openKpiDetailAsync = async (title, type, mapFn, columns, valueKey, color, anchor) => {
    setKpiDetail({ title, rows: [], columns, valueKey, color, anchor, loading: true });
    try {
      const raw = await fetchKpiDetailRaw(type);
      const rows = mapFn(filterByActive(raw));
      setKpiDetail((prev) => (prev && prev.title === title) ? { ...prev, rows, loading: false } : prev);
    } catch (e) {
      setKpiDetail((prev) => (prev && prev.title === title) ? { ...prev, rows: [], loading: false } : prev);
    }
  };

  // Drill-down générique (style décisionnel) : un montant de carte / tableau / total → popup des documents
  // qui le composent. `valueKey` = champ du document qui porte le montant cliqué ; `match` restreint aux
  // documents de la ligne cliquée ({ projet_id, fournisseur_id, … }, valeurs null/undefined ignorées,
  // une fonction sert de prédicat sur le champ — ex. Responsable vide).
  const DRILL = {
    bdc:            { pick: pickBdc,     cols: COLS_BDC,     totalKey: 'total_ttc',   docLabel: 'Montant BDC',           color: '#1565c0', anchor: 'sec-bdc-projet' },
    br:             { pick: pickBr,      cols: COLS_BR,      totalKey: 'total_ttc',   docLabel: 'Montant BR',            color: '#2e7d32', anchor: 'sec-br' },
    fa:             { pick: pickFaPart,  cols: COLS_FA_PART, totalKey: 'total_ttc',   docLabel: 'Montant (part projet)', color: '#e65100', anchor: 'sec-fa' },
    factures_vente: { pick: pickFv,      cols: COLS_FV,      totalKey: 'total_ttc',   docLabel: 'Montant facture',       color: '#6d4e36', anchor: 'sec-fv' },
    charges:        { pick: pickCharges, cols: COLS_CHARGES, totalKey: 'montant_ttc', docLabel: 'TTC',                   color: '#5c3317', anchor: 'sec-charges' }
  };
  const openDrill = (title, type, valueKey, valueLabel, match = {}) => {
    const d = DRILL[type];
    const conds = Object.entries(match).filter(([, v]) => v != null && v !== '');
    const columns = valueKey === d.totalKey ? d.cols : withReliquat(d.cols, d.docLabel, valueLabel);
    openKpiDetailAsync(title, type, (rows) => rows
      .filter((r) => conds.every(([k, v]) => (typeof v === 'function' ? v(r[k]) : String(r[k]) === String(v))))
      .map((r) => ({ ...d.pick(r), total_doc: Number(r[d.totalKey]) || 0, montant: Number(r[valueKey]) || 0 }))
      .filter((r) => Math.abs(r.montant) >= 0.5)
      .sort((a, b) => b.montant - a.montant),
    columns, 'montant', d.color, d.anchor);
  };

  // DA : lignes demandées / commandées / restant à commander (pas de prix en base → pas de MAD)
  const daRows = useMemo(() => filterByActive(daDetailRows || []), [daDetailRows, applied]); // eslint-disable-line react-hooks/exhaustive-deps
  const daTotaux = useMemo(() => {
    const s = (k) => daRows.reduce((a, r) => a + (r[k] || 0), 0);
    return {
      nb_da: daRows.length,
      nb_da_ouvertes: daRows.filter((r) => r.nb_lignes_restantes > 0).length,
      nb_lignes: s('nb_lignes'), nb_lignes_commandees: s('nb_lignes_commandees'), nb_lignes_restantes: s('nb_lignes_restantes'),
      nb_da_retard: daRows.filter((r) => r.nb_lignes_restantes > 0 && r.anciennete_jours > 15).length
    };
  }, [daRows]);
  const openDaDetail = (title, filterFn, valueKey, color = '#00695c') =>
    openKpiDetail(title, daRows.filter(filterFn).sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0)), COLS_DA, valueKey, color, null);

  const sBalAtoB = useSortable(balanceData?.atob || [], 'date_fac', false);
  const sBalBtoA = useSortable(balanceData?.btoa || [], 'date_fac', false);
  const isAllMode = balanceSocB?.value === 'all';
  const balAtoB_filtered = useMemo(() =>
    balanceFilterA.length
      ? sBalAtoB.sorted.filter(r => balanceFilterA.includes(isAllMode ? r.societe_chantier_id : r.chantier_id))
      : sBalAtoB.sorted,
    [sBalAtoB.sorted, balanceFilterA, isAllMode] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const balBtoA_filtered = useMemo(() =>
    balanceFilterB.length
      ? sBalBtoA.sorted.filter(r => balanceFilterB.includes(isAllMode ? r.societe_facture_id : r.chantier_id))
      : sBalBtoA.sorted,
    [sBalBtoA.sorted, balanceFilterB, isAllMode] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const pBalAtoB = usePaginated(balAtoB_filtered, 10);
  const pBalBtoA = usePaginated(balBtoA_filtered, 10);

  const [checksAlertFilter, setChecksAlertFilter] = useState(null); // 'critique'|'minimal'|null

  const CHECKS_ALERTES = [
    { key: 'critique', label: '+ de 30 jours', color: '#c62828', bg: '#ffebee', icon: '🔴' },
    { key: 'minimal',  label: '16 – 30 jours', color: '#e65100', bg: '#fff3e0', icon: '🟠' },
    { key: 'ok',       label: '≤ 15 jours',    color: '#2e7d32', bg: '#e8f5e9', icon: '🟢' }
  ];

  const checksAlertStats = useMemo(() =>
    CHECKS_ALERTES.map(s => ({
      ...s,
      count:   checksDetail.filter(r => r.statut_alerte === s.key).length,
      montant: checksDetail.filter(r => r.statut_alerte === s.key).reduce((a, r) => a + (r.montant || 0), 0)
    })),
    [checksDetail] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const checksDetailFiltered = useMemo(() =>
    checksAlertFilter ? checksDetail.filter(r => r.statut_alerte === checksAlertFilter) : checksDetail,
    [checksDetail, checksAlertFilter]
  );

  const sChecksFourSoc = useSortable(checksFourSoc,       'montant_total', false);
  const sChecksDetail  = useSortable(checksDetailFiltered, 'jours_ecart',  false);
  const pChecksFourSoc = usePaginated(sChecksFourSoc.sorted);
  const pChecksDetail  = usePaginated(sChecksDetail.sorted);

  const ECHEANCE_STATUTS = [
    { key: 'depasse', label: 'Échéance dépassée', color: '#c62828', bg: '#ffebee', icon: '🔴' },
    { key: 'proche',  label: 'Échéance proche',   color: '#e65100', bg: '#fff3e0', icon: '🟠' },
    { key: 'ok',      label: 'Pas encore échue',  color: '#2e7d32', bg: '#e8f5e9', icon: '🟢' },
    { key: 'paye',    label: 'Payé',              color: '#555',    bg: '#f5f5f5', icon: '✅' }
  ];

  const echeanceStats = useMemo(() =>
    ECHEANCE_STATUTS.map(s => ({
      ...s,
      count:   facturesEcheance.filter(r => r.statut_echeance === s.key).length,
      montant: facturesEcheance.filter(r => r.statut_echeance === s.key).reduce((a, r) => a + (s.key === 'paye' ? (r.TotalTTC || 0) : (r.ResteAPayer || 0)), 0)
    })),
    [facturesEcheance] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fvParSociete = useMemo(() => {
    const map = {};
    fvProjets.forEach(r => {
      const key = r.societe_client || '—';
      if (!map[key]) map[key] = { societe_client: key, societe_client_id: r.societe_client_id ?? null, logo: r.logo || null, total: 0, payes: 0, non_payes: 0, rg: 0 };
      map[key].total     += r.total_facturation_vente_ttc || 0;
      map[key].payes     += r.payes_ttc                   || 0;
      map[key].non_payes += r.non_payes_ttc               || 0;
      map[key].rg        += r.rg_retenue_ttc              || 0;
    });
    return Object.values(map).sort((a, b) => b.non_payes - a.non_payes);
  }, [fvProjets]);

  const brParResp = useMemo(() => {
    const map = {};
    brProjets.forEach(r => {
      const key = r.Responsable || '—';
      if (!map[key]) map[key] = { resp: key, br_total: 0, br_fact: 0, br_nonfact: 0, fv_total: 0, fv_fact: 0, fv_nonfact: 0 };
      map[key].br_total   += r.total_reception_ttc || 0;
      map[key].br_fact    += r.facturees_ttc        || 0;
      map[key].br_nonfact += r.non_facturees_ttc    || 0;
    });
    brFvProjets.forEach(r => {
      const key = r.Responsable || '—';
      if (!map[key]) map[key] = { resp: key, br_total: 0, br_fact: 0, br_nonfact: 0, fv_total: 0, fv_fact: 0, fv_nonfact: 0 };
      map[key].fv_total   += r.total_reception_ttc     || 0;
      map[key].fv_fact    += r.facturees_vente_ttc     || 0;
      map[key].fv_nonfact += r.non_facturees_vente_ttc || 0;
    });
    return Object.values(map).sort((a, b) => (b.fv_nonfact + b.br_nonfact) - (a.fv_nonfact + a.br_nonfact));
  }, [brProjets, brFvProjets]);

  // BDC à facturer = non réceptionné (BDC) + réceptionné non facturé (BR), fusionnés par projet
  const bdcAFacturerParProjet = useMemo(() => {
    const map = {};
    bdcProjets.forEach((r) => { if (r.non_receptionnes_ttc > 0) map[r.projet] = (map[r.projet] || 0) + r.non_receptionnes_ttc; });
    brProjets.forEach((r) => { if (r.non_facturees_ttc > 0) map[r.projet] = (map[r.projet] || 0) + r.non_facturees_ttc; });
    return Object.entries(map).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }, [bdcProjets, brProjets]);

  // Cross-metric BR : ventile non_facturees_ttc selon facturees_vente_ttc — même logique de filtre que applyFilters/brProjet
  const brCrossMetrics = useMemo(() => {
    if (!brDetailRows) return null;
    const projIds       = applied.projet?.length     ? applied.projet.map(p => String(p.value))     : null;
    const respLogin     = applied.responsable?.label || null;
    const fourIds       = applied.fournisseur?.length ? applied.fournisseur.map(f => String(f.value)) : null;
    const linkedProjIds = fourIds
      ? new Set((metaRef.current.relations || []).filter(r => fourIds.includes(String(r.fournisseur_id))).map(r => String(r.projet_id)))
      : null;
    const filtered = brDetailRows.filter(r =>
      (!projIds       || projIds.includes(String(r.projet_id))) &&
      (!respLogin     || r.Responsable === respLogin) &&
      (!linkedProjIds || linkedProjIds.has(String(r.projet_id)))
    );
    return {
      factVente:    filtered.filter(r => (r.non_facturees_ttc || 0) > 0 && (r.facturees_vente_ttc || 0) > 0)
                            .reduce((a, r) => a + (r.non_facturees_ttc || 0), 0),
      nonFactVente: filtered.filter(r => (r.non_facturees_ttc || 0) > 0 && !((r.facturees_vente_ttc || 0) > 0))
                            .reduce((a, r) => a + (r.non_facturees_ttc || 0), 0),
    };
  }, [brDetailRows, applied]);

  /* ─── KPI rates ─────────────────────────────────────────────────────────────── */
  const pctBdcNonFacture  = pct((totaux.non_receptionnes_ttc || 0) + (totaux.non_facturees_ttc || 0), totaux.total_commandes_ttc);
  const pctReceptionne    = pct(totaux.receptionnes_ttc,          totaux.total_commandes_ttc);
  const pctFactureBR      = pct(totaux.facturees_ttc,             totaux.total_reception_ttc);
  const pctPayeAchat      = pct(totaux.factures_achat_payees_ttc, totaux.total_factures_achat_ttc);
  // RG retenue exclue de la base : une facture soldée hors RG compte pour 100 %
  const pctPayeVente      = pct(totaux.ca_vente_paye_ttc,         (totaux.total_ca_vente_ttc || 0) - (totaux.ca_vente_rg_ttc || 0));
  const pctFactureBRVente = pct(totaux.facturees_br_vente_ttc,    totaux.total_reception_br_fv_ttc);
  const pctChequesCrit    = pct(
    (totaux.nb_cheques_critique || 0) + (totaux.nb_cheques_minimal || 0),
    totaux.nb_cheques_sans_facture || 0
  );

  /* ─── render ─────────────────────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh', background: '#faf7f2' }}>
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: '1rem 1rem 12rem' }}>

      {/* ── titre ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        marginBottom: '1.5rem', padding: '1rem 1.25rem', background: '#fff', borderRadius: 14,
        border: '1px solid rgba(109,78,54,0.12)', boxShadow: '0 2px 12px rgba(109,78,54,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/cienco.ico" alt="Cienco" style={{ height: 48, objectFit: 'contain' }} />
          <div>
            <h4 style={{ color: '#1a1a1a', fontWeight: 800, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
              Cienco Dashboard
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#f9c029', boxShadow: '0 0 0 3px rgba(249,192,41,0.25)' }} />
            </h4>
            <div style={{ color: '#888', fontSize: '0.82rem' }}>
              BDC · BR · Factures achat · Factures vente · Charges
              {data?.periode && (
                <span style={{ marginLeft: 10, color: '#1a1a1a', fontWeight: 600 }}>
                  {data.periode.date_debut} → {data.periode.date_fin}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {cachedAt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.6rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>Synchro</div>
              <span style={{
                fontSize: '0.72rem', fontWeight: 600,
                background: loading ? '#fff3cd' : '#e8f5e9',
                color: loading ? '#856404' : '#2e7d32',
                border: `1px solid ${loading ? '#ffc107' : '#a5d6a7'}`,
                borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap'
              }}>
                {loading ? '↻ actualisation…' : new Date(cachedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          {!isRespBudget && (
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'linear-gradient(135deg, #6d4e36, #8b5e3c)',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 16px', fontWeight: 600, fontSize: '0.85rem',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: '0 2px 8px rgba(109,78,54,0.25)', whiteSpace: 'nowrap'
              }}
            >
              ← Tableau de bord Paie Ouvriers / Engins
            </button>
          )}
        </div>
      </div>

      {/* ── raccourcis sections ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {[
          { id: 'sec-bdc-projet', label: 'BDC',            color: '#1565c0' },
          { id: 'sec-br',         label: 'BR',             color: '#2e7d32' },
          { id: 'sec-fa',         label: 'Factures achat', color: '#e65100' },
          { id: 'sec-fv',         label: 'CA Vente',       color: '#6d4e36' },
          { id: 'sec-checks',     label: 'Chèques',        color: '#c62828' },
          { id: 'sec-balance',    label: '⇄ Balance',      color: '#7b5ea7' },
        ].map(({ id, label, color }) => (
          <button
            key={id}
            onClick={() => scrollTo(id)}
            style={{
              fontSize: '0.75rem', padding: '3px 12px', borderRadius: 20,
              background: '#fff', color, border: `1.5px solid ${color}`,
              cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = color; }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── filtres ── */}
      <div style={{ ...sectionCard, padding: '0.9rem 1.1rem', marginBottom: '1.25rem', borderRadius: 10 }}>
        {/* ligne actions : toujours visible, alignée à droite */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12 }}>
          {isFiltered && <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem' }}>Filtre actif</Badge>}
          {datesDirty && <Badge bg="danger" style={{ fontSize: '0.68rem' }}>Modifié → cliquez Actualiser</Badge>}
          <Button size="sm" variant="outline-secondary" onClick={handleReset} disabled={!isFiltered}
            style={{ borderRadius: 8, fontSize: '0.82rem', padding: '5px 12px' }} title="Supprimer les filtres">
            <FaTimes /> Réinitialiser
          </Button>
          <Button size="sm" onClick={handleRefresh} disabled={loading}
            style={{
              background: datesDirty
                ? 'linear-gradient(135deg,#c0392b,#e74c3c)'
                : 'linear-gradient(135deg,#2e7d32,#43a047)',
              border: 'none', borderRadius: 8, fontWeight: 700,
              padding: '5px 14px', fontSize: '0.82rem', color: '#fff',
              display: 'flex', alignItems: 'center', gap: 5
            }}
            title={datesDirty ? 'Dates/filtres modifiés — recharge depuis le serveur' : 'Actualiser depuis le serveur'}>
            {loading
              ? <><Spinner animation="border" size="sm" /> Chargement…</>
              : <><FaSync style={{ fontSize: '0.72rem' }} /> Actualiser</>}
          </Button>
        </div>

        {/* ligne 1 : période + filtres serveur (nécessitent "Actualiser") */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
          paddingBottom: 12, marginBottom: 12, borderBottom: '1px dashed #e5d9c8'
        }}>
          <div>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Date début</label>
            <Form.Control type="date" size="sm" value={pending.date_debut}
              onChange={(e) => setPending((p) => ({ ...p, date_debut: e.target.value }))}
              style={{ borderColor: '#d8c8b8', fontSize: '0.82rem', width: 145 }} />
          </div>
          <div>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Date fin</label>
            <Form.Control type="date" size="sm" value={pending.date_fin}
              onChange={(e) => setPending((p) => ({ ...p, date_fin: e.target.value }))}
              style={{ borderColor: '#d8c8b8', fontSize: '0.82rem', width: 145 }} />
          </div>
          <div style={{ width: 150 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', marginBottom: 3 }}>
              Archivage
              <InfoTip text="S'applique à BDC, BR, factures achat et charges. N'affecte pas la section Factures vente / CA." />
            </label>
            <Select isSearchable={false} options={ARCHIVED_OPTIONS}
              value={ARCHIVED_OPTIONS.find((o) => o.value === pending.archived)}
              onChange={(opt) => setPending((p) => ({ ...p, archived: opt.value }))}
              styles={selectStylesSm} menuPortalTarget={document.body} />
          </div>
          <div style={{ width: 175 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', marginBottom: 3 }}>
              Société
              <InfoTip text="Compare la société de la ligne (BDC/BR/facture) à la société CLIENT du chantier. S'applique à BDC, BR et factures achat. N'affecte pas la section Factures vente / CA." />
            </label>
            <Select isSearchable={false} options={SOCIETE_SCOPE_OPTIONS}
              value={SOCIETE_SCOPE_OPTIONS.find((o) => o.value === pending.societe_scope)}
              onChange={(opt) => setPending((p) => ({ ...p, societe_scope: opt.value }))}
              styles={selectStylesSm} menuPortalTarget={document.body} />
          </div>
          <div style={{ minWidth: 200, maxWidth: 340 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', marginBottom: 3 }}>
              Sociétés masquées
              <InfoTip text="Exclut les lignes émises par ces sociétés (BDC, BR, factures achat, factures vente / CA, chèques). GASODIS est masquée par défaut. Charges et DA non concernées (pas de société)." />
            </label>
            <Select isMulti isClearable placeholder="Aucune" options={societeMasqueeOptions}
              value={societeMasqueeOptions.filter((o) => (pending.societes_masquees || []).includes(o.value))}
              onChange={(opts) => setPending((p) => ({ ...p, societes_masquees: (opts || []).map((o) => o.value) }))}
              styles={selectStylesSm} menuPortalTarget={document.body} noOptionsMessage={() => 'Aucune'} />
          </div>
        </div>

        {/* ligne 2 : filtres d'affichage (instantanés, sans appel API) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {!isRespBudget && (
            <div style={{ minWidth: 160, flex: '1 1 160px', maxWidth: 220 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Responsable</label>
              <Select isClearable placeholder="Tous" options={responsableOptions}
                value={pending.responsable} onChange={onChangeResponsable}
                styles={selectStylesSm} menuPortalTarget={document.body} noOptionsMessage={() => 'Aucun'} />
            </div>
          )}
          <div style={{ minWidth: 220, flex: '2 1 220px', maxWidth: 380 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Projet(s)</label>
            <Select isMulti isClearable placeholder="Tous les projets" options={projetOptions}
              value={pending.projet} onChange={onChangeProjet}
              styles={selectStylesSm} menuPortalTarget={document.body} noOptionsMessage={() => 'Aucun'} />
          </div>
          <div style={{ minWidth: 200, flex: '2 1 200px', maxWidth: 340 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Fournisseur(s)</label>
            <Select isMulti isClearable placeholder="Tous" options={fournisseurOptions}
              value={pending.fournisseur} onChange={onChangeFournisseur}
              styles={selectStylesSm} menuPortalTarget={document.body} noOptionsMessage={() => 'Aucun'} />
          </div>
          <div style={{ minWidth: 160, flex: '1 1 160px', maxWidth: 220 }}>
            <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>Société Client</label>
            <Select isClearable placeholder="Tous" options={societeClientOptions}
              value={pending.societe_client} onChange={onChangeSocieteClient}
              styles={selectStylesSm} menuPortalTarget={document.body} noOptionsMessage={() => 'Aucun'} />
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {loading && !data && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#6d4e36' }} />
        </div>
      )}

      {loading && data && (
        <div style={{ position: 'fixed', top: 56, left: 0, right: 0, height: 3, zIndex: 1051, overflow: 'hidden', background: 'rgba(109,78,54,0.12)' }}>
          <div style={{ position: 'absolute', height: '100%', width: '40%', background: 'linear-gradient(90deg, transparent, #6d4e36, transparent)', animation: 'bar-sweep 1.4s ease-in-out infinite' }} />
          <style>{`@keyframes bar-sweep{0%{left:-40%}100%{left:140%}}`}</style>
        </div>
      )}

      {data && (
        <>
          {/* ══════════════════════════════════════════
              RÉSUMÉ KPI BR — 7 taux en %
          ══════════════════════════════════════════ */}
          {(() => {
            const kpis = [
              { code: 'BR-01', label: 'BDC réceptionné',  val: pctReceptionne,    dir: 'h', w: 1.5 },
              { code: 'BR-02', label: 'BR facturé four.', val: pctFactureBR,      dir: 'h', w: 1.5 },
              { code: 'BR-03', label: 'Factures payées',  val: pctPayeAchat,      dir: 'h', w: 1.0 },
              { code: 'BR-04', label: 'BDC en attente',   val: pctBdcNonFacture,  dir: 'l', w: 1.0 },
              { code: 'BR-05', label: 'BR fact. client',  val: pctFactureBRVente, dir: 'h', w: 1.5 },
              { code: 'BR-06', label: 'CA encaissé',      val: pctPayeVente,      dir: 'h', w: 2.0 },
              { code: 'BR-07', label: 'Chèques crit.',    val: pctChequesCrit,    dir: 'l', w: 2.0 },
            ];

            const totalW    = kpis.reduce((s, k) => s + k.w, 0);
            const globalPct = Math.round(kpis.reduce((s, k) => s + kpiScore(k.val, k.dir) * k.w, 0) / totalW);

            const getLv = (val, dir) => {
              const s = kpiScore(val, dir);
              if (s >= 80) return { color: '#2e7d32', bg: '#e8f5e9' };
              if (s >= 50) return { color: '#e65100', bg: '#fff3e0' };
              return              { color: '#b71c1c', bg: '#ffebee' };
            };

            const note = getKpiNote(globalPct);

            return (
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                marginBottom: '1.25rem', background: '#fff',
                border: '1px solid rgba(109,78,54,0.13)', borderRadius: 12,
                padding: '0.7rem 1rem', boxShadow: '0 2px 8px rgba(109,78,54,0.06)',
              }}>
                {/* note globale */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: note.bg, border: `1.5px solid ${note.border}`,
                  borderRadius: 10, padding: '4px 12px 4px 8px',
                  marginRight: 4, flexShrink: 0,
                }}>
                  <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{note.emoji}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span style={{ fontSize: '0.65rem', color: note.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>{note.label}</span>
                    <span style={{ fontSize: '1rem', fontWeight: 900, color: note.color }}>{globalPct}%</span>
                  </div>
                </div>

                <div style={{ width: 1, height: 32, background: '#e8dfd6', flexShrink: 0 }} />

                {kpis.map(({ code, label, val, dir, w }) => {
                  const lv = getLv(val, dir);
                  return (
                    <div key={code} style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      background: lv.bg, borderRadius: 20,
                      padding: '3px 10px 3px 8px',
                      border: `1px solid ${lv.color}30`,
                    }}>
                      <span style={{ fontSize: '0.6rem', color: lv.color }}>●</span>
                      <span style={{ fontSize: '0.7rem', color: '#555', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: '0.83rem', fontWeight: 800, color: lv.color }}>{val}%</span>
                      <span style={{ fontSize: '0.6rem', color: '#bbb', marginLeft: 1 }}>×{w}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════
              KPI CARDS
          ══════════════════════════════════════════ */}
          {/* ── ligne 1 : BDC · BR · Factures achat (3 × md=4) ── */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaShoppingCart />} color="#6d4e36"
                label="Commandes BDC (TTC)"
                info="Total TTC des bons de commande émis sur la période. Le % indique la part déjà réceptionnée (BR validés)."
                value={`${fmt(totaux.total_commandes_ttc)} MAD`}
                rate={pctReceptionne} rateLabel="réceptionné"
                onClick={() => openKpiDetailAsync('Restant à réceptionner — détail BDC', 'bdc',
                  (rows) => toDocRows(rows, withDocTotal(pickBdc, 'total_ttc'), (r) => r.non_receptionne_ttc), COLS_BDC_REL, 'montant', '#1565c0', 'sec-bdc-projet')}
                onValueClick={() => openDrill('Commandes BDC — tous les BDC', 'bdc', 'total_ttc')}
                onRateClick={() => openDrill('BDC — part réceptionnée', 'bdc', 'receptionne_ttc', 'Réceptionné')}
                details={[
                  { label: `${fmt(totaux.non_receptionnes_ttc)} reliquat à réceptionner`, color: '#1565c0', strong: true,
                    onClick: () => openKpiDetailAsync('Restant à réceptionner — détail BDC', 'bdc',
                      (rows) => toDocRows(rows, withDocTotal(pickBdc, 'total_ttc'), (r) => r.non_receptionne_ttc), COLS_BDC_REL, 'montant', '#1565c0', 'sec-bdc-projet') },
                  { label: `${fmt(totaux.total_commandes_ttc - totaux.non_receptionnes_ttc)} réceptionné`, color: '#2e7d32',
                    onClick: () => openDrill('BDC — part réceptionnée', 'bdc', 'receptionne_ttc', 'Réceptionné') }
                ]}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaTruckLoading />} color="#6d4e36"
                label="Réceptions BR (TTC)"
                info="Total TTC des bons de réception fournisseur validés sur la période. Le % indique la part déjà facturée par le fournisseur."
                value={`${fmt(totaux.total_reception_ttc)} MAD`}
                rate={pctFactureBR} rateLabel="facturé"
                onClick={() => openKpiDetailAsync('À facturer — détail BR', 'br',
                  (rows) => toDocRows(rows, withDocTotal(pickBr, 'total_ttc'), (r) => r.non_facturees_ttc), COLS_BR_REL, 'montant', '#2e7d32', 'sec-br')}
                onValueClick={() => openDrill('Réceptions BR — tous les BR', 'br', 'total_ttc')}
                onRateClick={() => openDrill('BR — part facturée fournisseur', 'br', 'facturees_ttc', 'Facturé')}
                details={[
                  { label: `${fmt(totaux.facturees_ttc)} facturé`, color: '#1565c0',
                    onClick: () => openDrill('BR — part facturée fournisseur', 'br', 'facturees_ttc', 'Facturé') },
                  { label: `${fmt(totaux.non_facturees_ttc)} reliquat à facturer`, color: '#2e7d32', strong: true,
                    onClick: () => openKpiDetailAsync('À facturer — détail BR', 'br',
                      (rows) => toDocRows(rows, withDocTotal(pickBr, 'total_ttc'), (r) => r.non_facturees_ttc), COLS_BR_REL, 'montant', '#2e7d32', 'sec-br') },
                  ...(brCrossMetrics ? [
                    { label: `${fmt(brCrossMetrics.factVente)} NF four. / fact. vente`, color: '#e65100',
                      onClick: () => openKpiDetailAsync('Non fact. four. + facturé vente — BR', 'br',
                        (rows) => toDocRows(rows.filter(r => (r.facturees_vente_ttc || 0) > 0), withDocTotal(pickBr, 'total_ttc'), (r) => r.non_facturees_ttc),
                        COLS_BR_REL, 'montant', '#2e7d32', 'sec-br') },
                    { label: `${fmt(brCrossMetrics.nonFactVente)} NF four. / NF vente`, color: '#757575',
                      onClick: () => openKpiDetailAsync('Non fact. four. + non fact. vente — BR', 'br',
                        (rows) => toDocRows(rows.filter(r => !((r.facturees_vente_ttc || 0) > 0)), withDocTotal(pickBr, 'total_ttc'), (r) => r.non_facturees_ttc),
                        COLS_BR_REL, 'montant', '#2e7d32', 'sec-br') }
                  ] : [])
                ]}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaFileInvoiceDollar />} color="#6d4e36"
                label="Factures achat (TTC)"
                info="Total TTC des factures fournisseur enregistrées sur la période. Le % indique la part déjà réglée."
                value={`${fmt(totaux.total_factures_achat_ttc)} MAD`}
                rate={pctPayeAchat} rateLabel="payé"
                onClick={() => openKpiDetail('À payer — détail factures achat',
                  toDocRows(facturesEcheance, withDocTotal(pickFa, 'TotalTTC'), (r) => r.ResteAPayer), COLS_FA_REL, 'montant', '#e65100', 'sec-fa')}
                onValueClick={() => openDrill('Factures achat — toutes (part projet)', 'fa', 'total_ttc')}
                onRateClick={() => openDrill('Factures achat — part payée', 'fa', 'paye_ttc', 'Payé')}
                details={[
                  { label: `${fmt(totaux.factures_achat_non_payees)} reliquat à payer`, color: '#e65100', strong: true,
                    onClick: () => openKpiDetail('À payer — détail factures achat',
                      toDocRows(facturesEcheance, withDocTotal(pickFa, 'TotalTTC'), (r) => r.ResteAPayer), COLS_FA_REL, 'montant', '#e65100', 'sec-fa') },
                  { label: `${fmt(totaux.factures_achat_payees_ttc)} payé`, color: '#2e7d32',
                    onClick: () => openDrill('Factures achat — part payée', 'fa', 'paye_ttc', 'Payé') }
                ]}
              />
            </Col>
          </Row>

          {/* ── ligne 2 : BDC non fact · BR Fact. vente · CA Vente (3 × md=4) ── */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaLayerGroup />} color="#6d4e36"
                label="BDC à facturer (TTC)"
                info="Montant restant à facturer : commandes non encore réceptionnées + BR réceptionnés sans facture fournisseur. Le % représente la part du total BDC encore en attente."
                value={`${fmt((totaux.non_receptionnes_ttc || 0) + (totaux.non_facturees_ttc || 0))} MAD`}
                rate={pctBdcNonFacture} rateLabel="à facturer"
                onClick={() => openKpiDetail('BDC à facturer — par projet (fusion BDC + BR)',
                  bdcAFacturerParProjet, colsProjetMontant('À facturer TTC'), 'value', '#6d4e36', 'sec-bdc-projet')}
                onValueClick={() => openKpiDetail('BDC à facturer — par projet (fusion BDC + BR)',
                  bdcAFacturerParProjet, colsProjetMontant('À facturer TTC'), 'value', '#6d4e36', 'sec-bdc-projet')}
                details={[
                  { label: `${fmt(totaux.non_receptionnes_ttc)} non récép.`, color: '#1565c0',
                    onClick: () => openKpiDetailAsync('Non réceptionné — détail BDC', 'bdc',
                      (rows) => toDocRows(rows, withDocTotal(pickBdc, 'total_ttc'), (r) => r.non_receptionne_ttc), COLS_BDC_REL, 'montant', '#1565c0', 'sec-bdc-projet') },
                  { label: `${fmt(totaux.non_facturees_ttc)} récép. à facturer`, color: '#2e7d32',
                    onClick: () => openKpiDetailAsync('Réceptionné à facturer — détail BR', 'br',
                      (rows) => toDocRows(rows, withDocTotal(pickBr, 'total_ttc'), (r) => r.non_facturees_ttc), COLS_BR_REL, 'montant', '#2e7d32', 'sec-br') }
                ]}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaTruckLoading />} color="#37474f"
                label="Réceptions BR — Fact. vente (TTC)"
                info="Montant principal = reliquat à facturer au client (hors RG). Réceptions côté facturation client : BR validés à facturer au client. Le % indique la part déjà facturée en vente. RG retenue = retenue de garantie encore gardée par le client sur les factures vente de ces BR, au prix BR (≤ taux RG × montant facturé). Reliquat avec RG = reliquat à facturer + RG retenue."
                value={`${fmt(totaux.reliquat_br_vente_ttc)} MAD`} valueLabel="à facturer (hors RG)"
                rate={pctFactureBRVente} rateLabel="facturé vente"
                onClick={() => openKpiDetailAsync('À facturer vente — détail BR', 'br', toBrFvRows, COLS_BR_FV, 'reliquat', '#7b1fa2', 'sec-br-fv')}
                onValueClick={() => openKpiDetailAsync('À facturer vente — détail BR', 'br', toBrFvRows, COLS_BR_FV, 'reliquat', '#7b1fa2', 'sec-br-fv')}
                onRateClick={() => openDrill('BR — facturés en vente', 'br', 'facturees_vente_ttc', 'Facturé vente')}
                details={[
                  { label: `${fmt(totaux.total_reception_br_fv_ttc)} réceptions BR (total)`, color: '#37474f',
                    onClick: () => openDrill('Réceptions BR (vente) — tous les BR', 'br', 'total_ttc') },
                  { label: `${fmt(totaux.facturees_br_vente_ttc)} facturé vente`, color: '#2e7d32',
                    onClick: () => openDrill('BR — facturés en vente', 'br', 'facturees_vente_ttc', 'Facturé vente') },
                  { label: `${fmt(totaux.reliquat_br_vente_ttc + totaux.rg_br_vente_ttc)} reliquat à facturer (avec RG)`, color: '#7b1fa2',
                    onClick: () => openKpiDetailAsync('À facturer vente + RG retenue — détail BR', 'br', toBrFvRgRows, COLS_BR_FV_RG, 'montant', '#7b1fa2', 'sec-br-fv') },
                  { label: `${fmt(totaux.rg_br_vente_ttc)} RG retenue (fact. vente)`, color: '#7b5ea7',
                    onClick: () => openDrill('BR — RG retenue sur factures vente', 'br', 'rg_vente_ttc', 'RG retenue') },
                  { label: `${fmt(totaux.non_facturees_br_vente_ttc)} BR concernés (montant total)`, color: '#757575',
                    onClick: () => openKpiDetailAsync('À facturer vente — détail BR', 'br', toBrFvRows, COLS_BR_FV, 'reliquat', '#7b1fa2', 'sec-br-fv') }
                ]}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaHandHoldingUsd />} color="#37474f"
                label="CA Vente (TTC)"
                info="Chiffre d'affaires facturé aux clients sur la période (factures vente émises). RG = retenue de garantie (facturegroup.rg, % du TTC) gardée par le client jusqu'à la réception définitive, pas encore due. Le % encaissé est calculé hors RG : encaissé / (CA − RG retenue)."
                value={`${fmt(totaux.total_ca_vente_ttc)} MAD`}
                rate={pctPayeVente} rateLabel="encaissé (hors RG)"
                onClick={() => openKpiDetailAsync('Restant à encaisser — détail factures vente', 'factures_vente',
                  (rows) => toDocRows(rows, withDocTotal(pickFv, 'total_ttc'), (r) => r.non_paye_ttc), COLS_FV_REL, 'montant', '#6d4e36', 'sec-fv')}
                onValueClick={() => openDrill('CA Vente — toutes les factures vente', 'factures_vente', 'total_ttc')}
                onRateClick={() => openDrill('Factures vente — encaissé', 'factures_vente', 'paye_ttc', 'Encaissé')}
                details={[
                  { label: `${fmt(totaux.ca_vente_non_paye_ttc)} reliquat à encaisser (hors RG)`, color: '#6d4e36', strong: true,
                    onClick: () => openKpiDetailAsync('Restant à encaisser — détail factures vente', 'factures_vente',
                      (rows) => toDocRows(rows, withDocTotal(pickFv, 'total_ttc'), (r) => r.non_paye_ttc), COLS_FV_REL, 'montant', '#6d4e36', 'sec-fv') },
                  { label: `${fmt(totaux.ca_vente_rg_ttc)} RG retenue`, color: '#7b5ea7',
                    onClick: () => openDrill('RG retenue — factures vente', 'factures_vente', 'rg_ttc', 'RG retenue') },
                  { label: `${fmt(totaux.ca_vente_paye_ttc)} encaissé`, color: '#2e7d32',
                    onClick: () => openDrill('Factures vente — encaissé', 'factures_vente', 'paye_ttc', 'Encaissé') }
                ]}
              />
            </Col>
          </Row>

          {/* ── ligne 3 : Charges (md=4) ── */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaWallet />} color="#5c3317"
                label="Charges (TTC)"
                info="Total TTC des charges enregistrées sur la période, toutes catégories confondues. Cliquez pour voir le détail ligne par ligne."
                value={`${fmt(totaux.total_charges_ttc)} MAD`}
                onClick={() => openKpiDetailAsync('Charges — détail', 'charges',
                  (rows) => toDocRows(rows, pickCharges, (r) => r.montant_ttc), COLS_CHARGES, 'montant', '#5c3317', 'sec-charges')}
                onValueClick={() => openDrill('Charges — toutes', 'charges', 'montant_ttc')}
                details={chargesTypes.map((t) => (
                  { label: `${fmt(t.montant_ttc)} ${t.type_charge}`, color: '#5c3317',
                    onClick: () => openDrill(`Charges — ${t.type_charge}`, 'charges', 'montant_ttc', 'TTC', { type_charge: t.type_charge }) }
                ))}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <KpiCard
                icon={<FaClipboardList />} color="#00695c"
                label="Demandes d'achat (DA)"
                info="Demandes d'achat saisies sur la période (lignes non annulées). Le % indique la part des lignes déjà passées en BDC. Les DA ne sont pas chiffrées : aucun prix n'est saisi sur les lignes DA."
                value={daDetailRows ? `${fmt(daTotaux.nb_lignes)} lignes · ${fmt(daTotaux.nb_da)} DA` : '…'}
                rate={pct(daTotaux.nb_lignes_commandees, daTotaux.nb_lignes)} rateLabel="commandé"
                onClick={() => openDaDetail('DA — lignes restant à commander', (r) => r.nb_lignes_restantes > 0, 'nb_lignes_restantes')}
                onValueClick={() => openDaDetail('DA — toutes les demandes', () => true, 'nb_lignes')}
                onRateClick={() => openDaDetail('DA — lignes commandées', (r) => r.nb_lignes_commandees > 0, 'nb_lignes_commandees', '#2e7d32')}
                details={[
                  { label: `${fmt(daTotaux.nb_lignes_restantes)} lignes à commander (${daTotaux.nb_da_ouvertes} DA)`, color: '#00695c', strong: true,
                    onClick: () => openDaDetail('DA — lignes restant à commander', (r) => r.nb_lignes_restantes > 0, 'nb_lignes_restantes') },
                  { label: `${fmt(daTotaux.nb_lignes_commandees)} commandées`, color: '#2e7d32',
                    onClick: () => openDaDetail('DA — lignes commandées', (r) => r.nb_lignes_commandees > 0, 'nb_lignes_commandees', '#2e7d32') },
                  ...(daTotaux.nb_da_retard > 0 ? [{ label: `🔴 ${daTotaux.nb_da_retard} DA en attente >15j`, color: '#c62828',
                    onClick: () => openDaDetail('DA en attente depuis plus de 15 jours', (r) => r.nb_lignes_restantes > 0 && r.anciennete_jours > 15, 'nb_lignes_restantes', '#c62828') }] : [])
                ]}
              />
            </Col>
          </Row>

          {/* ── ligne 4 : Chèques (md=4) + mini-cards alertes (md=8) ── */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <KpiCard
                icon={<FaMoneyCheckAlt />} color="#c62828"
                label="Chèques sans facture"
                info="Chèques émis aux fournisseurs sans aucune facture rattachée dans la période. 🔴 Critique = plus de 30 jours depuis l'émission · 🟠 Alerte = 16 à 30 jours."
                value={`${fmt(totaux.montant_cheques_sans_facture)} MAD`}
                onClick={() => openKpiDetail('Chèques sans facture — détail',
                  toDocRows(checksDetail, pickCheque, (r) => r.montant, true), COLS_CHEQUES, 'montant', '#c62828', 'sec-checks')}
                details={[
                  { label: `${totaux.nb_cheques_sans_facture} chèque(s)`, color: '#757575',
                    onClick: () => openKpiDetail('Chèques sans facture — détail',
                      toDocRows(checksDetail, pickCheque, (r) => r.montant, true), COLS_CHEQUES, 'montant', '#757575', 'sec-checks') },
                  ...(totaux.nb_cheques_critique > 0 ? [{ label: `🔴 ${totaux.nb_cheques_critique} critique(s)`, color: '#c62828',
                    onClick: () => openKpiDetail('Chèques critiques (>30j) — détail',
                      toDocRows(checksDetail.filter((r) => r.statut_alerte === 'critique'), pickCheque, (r) => r.montant, true), COLS_CHEQUES, 'montant', '#c62828', 'sec-checks') }] : []),
                  ...(totaux.nb_cheques_minimal > 0 ? [{ label: `🟠 ${totaux.nb_cheques_minimal} alerte(s)`, color: '#e65100',
                    onClick: () => openKpiDetail('Chèques en alerte (16-30j) — détail',
                      toDocRows(checksDetail.filter((r) => r.statut_alerte === 'minimal'), pickCheque, (r) => r.montant, true), COLS_CHEQUES, 'montant', '#e65100', 'sec-checks') }] : [])
                ]}
              />
            </Col>
            {checksDetail.length > 0 && (
              <Col xs={12} md={8}>
                <Row className="g-2 h-100">
                  {checksAlertStats.map(s => (
                    <Col key={s.key} xs={4}>
                      <div
                        onClick={() => {
                          setChecksAlertFilter(checksAlertFilter === s.key ? null : s.key);
                          scrollTo('sec-checks');
                        }}
                        style={{
                          cursor: 'pointer', borderRadius: 10, height: '100%',
                          border: `2px solid ${checksAlertFilter === s.key ? s.color : s.color + '44'}`,
                          background: checksAlertFilter === s.key ? s.color + '22' : s.bg,
                          padding: '0.6rem 0.85rem',
                          boxShadow: checksAlertFilter === s.key ? `0 0 0 3px ${s.color}33` : '0 1px 4px rgba(0,0,0,0.07)',
                          transition: 'all 0.18s'
                        }}
                      >
                        <div style={{ fontSize: '0.65rem', color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>
                          {s.icon} {s.label}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>
                          {fmt(s.montant)} <span style={{ fontSize: '0.7rem', fontWeight: 500 }}>MAD</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 3 }}>
                          <span style={{ fontSize: '0.72rem', color: '#777' }}>
                            {s.count} chèque{s.count > 1 ? 's' : ''}
                            {checksAlertFilter === s.key && <span style={{ marginLeft: 5, color: s.color, fontWeight: 700 }}>✕</span>}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: s.color, opacity: 0.8 }}>↓ détail</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Col>
            )}
          </Row>

          {/* ── mini-cards échéances ── */}
          {facturesEcheance.length > 0 && (
            <Row className="g-2 mb-4">
              {echeanceStats.map(s => (
                <Col key={s.key} xs={6} sm={6} lg={3}>
                  <div
                    onClick={() => {
                      setEcheanceFilter(echeanceFilter === s.key ? null : s.key);
                      scrollTo('sec-factures-echeance');
                    }}
                    style={{
                      cursor: 'pointer', borderRadius: 10,
                      border: `2px solid ${echeanceFilter === s.key ? s.color : s.color + '44'}`,
                      background: echeanceFilter === s.key ? s.color + '22' : s.bg,
                      padding: '0.6rem 0.85rem',
                      boxShadow: echeanceFilter === s.key ? `0 0 0 3px ${s.color}33` : '0 1px 4px rgba(0,0,0,0.07)',
                      transition: 'all 0.18s', height: '100%'
                    }}
                  >
                    <div style={{ fontSize: '0.65rem', color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>
                      {s.icon} {s.label}
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>
                      {fmt(s.montant)} <span style={{ fontSize: '0.7rem', fontWeight: 500 }}>MAD</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 3 }}>
                      <span style={{ fontSize: '0.72rem', color: '#777' }}>
                        {s.count} facture{s.count > 1 ? 's' : ''}
                        {echeanceFilter === s.key && <span style={{ marginLeft: 5, color: s.color, fontWeight: 700 }}>✕</span>}
                      </span>
                      <span style={{ fontSize: '0.68rem', color: s.color, opacity: 0.8 }}>↓ détail</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* ── mini-cards societe client (CA vente) ── */}
          {fvParSociete.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.65rem' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6d4e36' }}>
                  📋 Factures de vente par société cliente
                </span>
                <span style={{ fontSize: '0.7rem', color: '#aaa' }}>
                  ({fvParSociete.length} société{fvParSociete.length > 1 ? 's' : ''})
                </span>
              </div>
              <Row className="g-2">
                {(showAllSociete ? fvParSociete : fvParSociete.slice(0, 8)).map(s => {
                  const p = s.total - s.rg > 0 ? Math.round((s.payes / (s.total - s.rg)) * 100) : 0; // hors RG
                  const barColor = p >= 80 ? '#2e7d32' : p >= 40 ? '#e65100' : '#c62828';
                  const m = { societe_client: s.societe_client };
                  const drillTotal = () => openDrill(`Factures vente — ${s.societe_client}`, 'factures_vente', 'total_ttc', null, m);
                  const drillPaye  = () => openDrill(`Factures vente encaissées — ${s.societe_client}`, 'factures_vente', 'paye_ttc', 'Encaissé', m);
                  const drillReste = () => openDrill(`Factures vente à encaisser — ${s.societe_client}`, 'factures_vente', 'non_paye_ttc', 'Reliquat à encaisser (hors RG)', m);
                  const drillRg    = () => openDrill(`RG retenue — ${s.societe_client}`, 'factures_vente', 'rg_ttc', 'RG retenue', m);
                  return (
                    <Col key={s.societe_client} xs={12} sm={6} lg={3}>
                      <div
                        onClick={() => {
                          const already = pending.societe_client?.value === s.societe_client_id;
                          onChangeSocieteClient(already ? null : { value: s.societe_client_id, label: s.societe_client });
                        }}
                        style={{
                          cursor: 'pointer', borderRadius: 12,
                          border: pending.societe_client?.value === s.societe_client_id ? '2px solid #6d4e36' : '1.5px solid #d8c8b8',
                          background: pending.societe_client?.value === s.societe_client_id ? 'linear-gradient(160deg, #f0e0cc 0%, #e8d4b8 100%)' : 'linear-gradient(160deg, #ffffff 0%, #fdf6ee 100%)',
                          padding: '0.9rem 1rem 0.75rem',
                          boxShadow: '0 2px 8px rgba(109,78,54,0.09)',
                          transition: 'transform 0.15s, box-shadow 0.15s',
                          textAlign: 'center',
                          height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(109,78,54,0.16)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(109,78,54,0.09)'; }}
                      >
                        {s.logo
                          ? <img src={s.logo} alt={s.societe_client} title={s.societe_client}
                              style={{ maxHeight: 42, maxWidth: '80%', objectFit: 'contain', marginBottom: 2 }} />
                          : <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#3d2510', letterSpacing: 0.1 }}>🏢 {s.societe_client}</div>
                        }
                        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#6d4e36' }}>
                          <DrillSpan onClick={drillTotal}>{fmt(s.total)}</DrillSpan> <span style={{ fontSize: '0.65rem', fontWeight: 500, color: '#a08060' }}>MAD</span>
                        </div>
                        <div style={{ width: '100%' }}>
                          <div style={{ background: '#e8ddd4', borderRadius: 6, height: 7, overflow: 'hidden' }}>
                            <div style={{ width: `${p}%`, height: '100%', background: barColor, borderRadius: 6, transition: 'width 0.5s ease' }} />
                          </div>
                        </div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 900, color: barColor, lineHeight: 1, letterSpacing: -1 }}>
                          <DrillSpan onClick={drillPaye}>{p}%</DrillSpan>
                          <span style={{ fontSize: '0.63rem', fontWeight: 500, color: '#999', marginLeft: 4, letterSpacing: 0 }}>encaissé</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, width: '100%' }}>
                          {s.non_payes > 0 && (
                            <div onClick={(e) => { e.stopPropagation(); drillReste(); }} title="Voir les documents" style={{
                              background: '#fff0f0', border: '1.5px solid #f5c6c6', borderRadius: 8, cursor: 'pointer',
                              padding: '3px 10px', color: '#c62828', fontWeight: 800, fontSize: '0.82rem', width: '100%', textAlign: 'center'
                            }}>
                              ✗ {fmt(s.non_payes)} <span style={{ fontSize: '0.6rem', fontWeight: 500 }}>MAD restant ↗</span>
                            </div>
                          )}
                          {s.rg > 0.5 && (
                            <DrillSpan onClick={drillRg} style={{ color: '#7b5ea7', fontWeight: 600, fontSize: '0.68rem' }}>🔒 {fmt(s.rg)} RG retenue</DrillSpan>
                          )}
                          <DrillSpan onClick={drillPaye} style={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.68rem' }}>✓ {fmt(s.payes)} encaissé</DrillSpan>
                        </div>
                        <div
                          onClick={e => { e.stopPropagation(); onChangeSocieteClient({ value: s.societe_client_id, label: s.societe_client }); scrollTo('sec-fv'); }}
                          style={{ fontSize: '0.63rem', color: '#6d4e36', marginTop: 1, textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                        >↓ voir détail</div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              {fvParSociete.length > 8 && (
                <div className="text-center mt-3">
                  <button
                    onClick={() => setShowAllSociete(v => !v)}
                    style={{
                      background: 'none', border: '1px solid #6d4e36', color: '#6d4e36',
                      borderRadius: 8, padding: '3px 14px', fontSize: '0.8rem',
                      cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    {showAllSociete ? '▲ Moins' : `▼ Voir ${fvParSociete.length - 8} autres sociétés`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── cards facture de vente par responsable ── */}
          {brParResp.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  📋 Facture de vente par responsable
                </div>
                <a
                  href="#sec-br-fv"
                  onClick={e => { e.preventDefault(); scrollTo('sec-br-fv'); }}
                  style={{ fontSize: '0.72rem', color: '#7b1fa2', textDecoration: 'none', fontWeight: 600, opacity: 0.8 }}
                >
                  ↓ tableau BR fact. vente
                </a>
              </div>
              <Row className="g-3">
                {(showAllResp ? brParResp : brParResp.slice(0, 8)).map((item, idx) => {
                  const pctBR     = item.br_total > 0 ? Math.round(item.br_fact / item.br_total * 100) : 0;
                  const pctFV     = item.fv_total > 0 ? Math.round(item.fv_fact / item.fv_total * 100) : 0;
                  // Score pondéré sur les 2 KPIs disponibles (BR-02 ×1.5 + BR-05 ×1.5)
                  const respKpis  = [
                    ...(item.br_total > 0 ? [{ val: pctBR, dir: 'h', w: 1.5, label: 'BR facturé four.' }] : []),
                    ...(item.fv_total > 0 ? [{ val: pctFV, dir: 'h', w: 1.5, label: 'BR fact. client'  }] : []),
                  ];
                  const respTotalW  = respKpis.reduce((s, k) => s + k.w, 0);
                  const pctTotal    = respTotalW > 0
                    ? Math.round(respKpis.reduce((s, k) => s + kpiScore(k.val, k.dir) * k.w, 0) / respTotalW)
                    : 0;
                  const respNote    = getKpiNote(pctTotal);
                  const respOpt  = metaRef.current.responsables.find(r => r.label === item.resp);
                  const isActive = pending.responsable?.value === respOpt?.value;
                  const handleClick = () => {
                    if (!respOpt) return;
                    onChangeResponsable(isActive ? null : respOpt);
                  };
                  // respOpt.label = login (clé commune mern_cienco ↔ shemspaiedb)
                  const docId = respOpt ? (avatarsMap[respOpt.label] || null) : null;
                  const m = { Responsable: item.resp === '—' ? (v) => !v : item.resp };
                  const drill = (title, valueKey, valueLabel) => openDrill(`${title} — ${item.resp}`, 'br', valueKey, valueLabel, m);
                  return (
                    <Col key={idx} xs={12} sm={6} md={4} lg={3}>
                      <div
                        onClick={handleClick}
                        style={{
                          border: isActive ? '2px solid #7b1fa2' : '1.5px solid #d9c8e8',
                          borderRadius: 12,
                          boxShadow: isActive ? '0 0 0 3px #7b1fa233' : '0 2px 8px rgba(0,0,0,0.07)',
                          background: isActive ? 'linear-gradient(160deg,#f0e6fa,#e8d8f5)' : 'linear-gradient(160deg,#fdfcff,#f7f2fd)',
                          padding: '0.85rem',
                          textAlign: 'center',
                          cursor: respOpt ? 'pointer' : 'default',
                          transition: 'transform 0.15s, box-shadow 0.15s'
                        }}
                        onMouseEnter={e => { if (respOpt) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = isActive ? '0 0 0 3px #7b1fa233' : '0 6px 16px rgba(123,31,162,0.13)'; }}}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isActive ? '0 0 0 3px #7b1fa233' : '0 2px 8px rgba(0,0,0,0.07)'; }}
                      >
                        <RespAvatar docId={docId} name={item.resp} size={52} />
                        <div style={{ fontWeight: 800, color: '#3e2723', marginBottom: 6, fontSize: '0.9rem' }}>
                          {item.resp}
                          {isActive && <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#7b1fa2', fontWeight: 700 }}>✕ actif</span>}
                        </div>

                        {/* ── score KPI pondéré (même style que le widget en haut) ── */}
                        {respKpis.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            {/* badge principal */}
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: respNote.bg, border: `1.5px solid ${respNote.border}`,
                              borderRadius: 10, padding: '3px 10px 3px 7px', marginBottom: 6,
                            }}>
                              <span style={{ fontSize: '1rem', lineHeight: 1 }}>{respNote.emoji}</span>
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                <span style={{ fontSize: '0.58rem', color: respNote.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{respNote.label}</span>
                                <span style={{ fontSize: '0.92rem', fontWeight: 900, color: respNote.color }}>{pctTotal}%</span>
                              </div>
                            </div>
                            {/* pills KPIs individuels */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                              {respKpis.map((k, ki) => {
                                const lv = kpiScore(k.val, k.dir) >= 80 ? { color: '#2e7d32', bg: '#e8f5e9' }
                                         : kpiScore(k.val, k.dir) >= 50 ? { color: '#e65100', bg: '#fff3e0' }
                                         :                                 { color: '#b71c1c', bg: '#ffebee' };
                                return (
                                  <div key={ki} style={{
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    background: lv.bg, borderRadius: 20,
                                    padding: '2px 8px 2px 6px',
                                    border: `1px solid ${lv.color}30`,
                                  }}>
                                    <span style={{ fontSize: '0.55rem', color: lv.color }}>●</span>
                                    <span style={{ fontSize: '0.65rem', color: '#555', fontWeight: 500 }}>{k.label}</span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: lv.color }}>{k.val}%</span>
                                    <span style={{ fontSize: '0.55rem', color: '#bbb', marginLeft: 1 }}>×{k.w}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Bloc 1 — BR achat */}
                        {item.br_total > 0 && (
                          <div style={{ marginBottom: 8, padding: '0.4rem 0.5rem', background: '#f3e5f5', borderRadius: 8 }}>
                            <div style={{ fontSize: '0.7rem', color: '#7b1fa2', fontWeight: 700, marginBottom: 4 }}>
                              Réceptions BR (TTC)
                            </div>
                            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#3e2723' }}>
                              <DrillSpan onClick={() => drill('BR', 'total_ttc')}>{fmt(item.br_total)} MAD</DrillSpan>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#7b1fa2', fontWeight: 600, marginBottom: 4 }}>
                              <DrillSpan onClick={() => drill('BR facturés', 'facturees_ttc', 'Facturé')}>{pctBR}% facturé</DrillSpan>
                            </div>
                            {item.br_nonfact > 0 && (
                              <div onClick={(e) => { e.stopPropagation(); drill('BR à facturer', 'non_facturees_ttc', 'Reliquat à facturer'); }} title="Voir les documents"
                                style={{ background: '#fff0f0', border: '1.5px solid #f5c6c6', borderRadius: 7, padding: '2px 8px', color: '#c62828', fontWeight: 800, fontSize: '0.75rem', marginBottom: 3, cursor: 'pointer' }}>
                                ✗ {fmt(item.br_nonfact)} <span style={{ fontSize: '0.6rem', fontWeight: 500 }}>à facturer ↗</span>
                              </div>
                            )}
                            <DrillSpan onClick={() => drill('BR facturés', 'facturees_ttc', 'Facturé')} style={{ fontSize: '0.68rem', color: '#2e7d32', fontWeight: 600 }}>✓ {fmt(item.br_fact)} facturé</DrillSpan>
                          </div>
                        )}

                        {/* Bloc 2 — BR fact. vente */}
                        {item.fv_total > 0 && (
                          <div style={{ padding: '0.4rem 0.5rem', background: '#ede7f6', borderRadius: 8 }}>
                            <div style={{ fontSize: '0.7rem', color: '#4a148c', fontWeight: 700, marginBottom: 4 }}>
                              BR — Fact. vente (TTC)
                            </div>
                            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#3e2723' }}>
                              <DrillSpan onClick={() => drill('BR (vente)', 'total_ttc')}>{fmt(item.fv_total)} MAD</DrillSpan>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#4a148c', fontWeight: 600, marginBottom: 4 }}>
                              <DrillSpan onClick={() => drill('BR facturés vente', 'facturees_vente_ttc', 'Facturé vente')}>{pctFV}% facturé vente</DrillSpan>
                            </div>
                            {item.fv_nonfact > 0 && (
                              <div onClick={(e) => { e.stopPropagation(); drill('BR à facturer vente', 'non_facturees_vente_ttc', 'À facturer vente'); }} title="Voir les documents"
                                style={{ background: '#fff0f0', border: '1.5px solid #f5c6c6', borderRadius: 7, padding: '2px 8px', color: '#c62828', fontWeight: 800, fontSize: '0.75rem', marginBottom: 3, cursor: 'pointer' }}>
                                ✗ {fmt(item.fv_nonfact)} <span style={{ fontSize: '0.6rem', fontWeight: 500 }}>à facturer ↗</span>
                              </div>
                            )}
                            <DrillSpan onClick={() => drill('BR facturés vente', 'facturees_vente_ttc', 'Facturé vente')} style={{ fontSize: '0.68rem', color: '#2e7d32', fontWeight: 600 }}>✓ {fmt(item.fv_fact)} facturé</DrillSpan>
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
              {brParResp.length > 8 && (
                <div className="text-center mt-3">
                  <button
                    onClick={() => setShowAllResp(v => !v)}
                    style={{
                      background: 'none', border: '1px solid #7b1fa2', color: '#7b1fa2',
                      borderRadius: 8, padding: '3px 14px', fontSize: '0.8rem',
                      cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    {showAllResp ? '▲ Moins' : `▼ Voir ${brParResp.length - 8} autres responsables`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── conteneur flex des 11 sections-tableaux réordonnables (10) ── */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 1 — BDC par projet
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('bdc-projet')} {...sectionDropProps('bdc-projet')}>
          <div id="sec-bdc-projet" style={detailSectionCard}>
            <SectionHeader
              count={sBdcProjet.sorted.length} open={openBdcProjet} setOpen={setOpenBdcProjet}
              dragHandle={sectionDragHandle('bdc-projet')}
              unitPicker={<UnitPicker unit={getUnit('bdc-projet')} onChange={(v) => setUnit('bdc-projet', v)} />}
              onExport={() => exportExcel(sBdcProjet.sorted, 'BDC_par_projet')}
              onCopy={() => { const { html, plain } = rowsToHtml(sBdcProjet.sorted.map(r => ({
                'Projet': r.projet, 'Fournisseur': r.fournisseur || '—', 'Responsable': r.Responsable || '—',
                'Nb BDC': r.nb_bdc, 'Total TTC': fmt(r.total_commandes_ttc), 'Réceptionné TTC': fmt(r.receptionnes_ttc),
                'Non récép. TTC': fmt(r.non_receptionnes_ttc), 'Taux récep.': `${r.pct_receptionne}%`
              })), 'brown'); copyHtmlToClipboard(html, plain); }}
            >
              🛒 Bons de Commande par projet
            </SectionHeader>
            {openBdcProjet && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sBdcProjet.Th field="projet">Projet</sBdcProjet.Th>
                    <sBdcProjet.Th field="fournisseur">Fournisseur</sBdcProjet.Th>
                    <sBdcProjet.Th field="Responsable">Responsable</sBdcProjet.Th>
                    <sBdcProjet.Th field="nb_bdc" className="text-end">Nb BDC</sBdcProjet.Th>
                    <sBdcProjet.Th field="total_commandes_ttc" className="text-end">Total TTC</sBdcProjet.Th>
                    <sBdcProjet.Th field="receptionnes_ttc" className="text-end">Réceptionné TTC</sBdcProjet.Th>
                    <sBdcProjet.Th field="non_receptionnes_ttc" className="text-end">Non récép. TTC</sBdcProjet.Th>
                    <sBdcProjet.Th field="pct_receptionne" style={{ minWidth: 140 }}>Taux réception</sBdcProjet.Th>
                    <sBdcProjet.Th field="date_ancienne_nr">Date + anc. NR</sBdcProjet.Th>
                  </tr>
                </thead>
                <tbody>
                  {pBdcProjet.visible.length === 0
                    ? <tr><td colSpan={9} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pBdcProjet.visible.map((r, i) => {
                      const m = { projet_id: r.projet_id, fournisseur_id: r.IDFOURNISSEUR };
                      const ctx = `${r.projet} / ${r.fournisseur || '—'}`;
                      return (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                        <td style={{ padding: '5px 12px' }}>{r.fournisseur || '—'}</td>
                        <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC — ${ctx}`, 'bdc', 'total_ttc', null, m)}>{fmt(r.nb_bdc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC — ${ctx}`, 'bdc', 'total_ttc', null, m)}>{fmt(r.total_commandes_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC réceptionnés — ${ctx}`, 'bdc', 'receptionne_ttc', 'Réceptionné', m)}>{fmt(r.receptionnes_ttc)}</DrillCell>
                        {r.non_receptionnes_ttc > 0
                          ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#e65100', fontWeight: 600 }} onClick={() => openDrill(`BDC non réceptionnés — ${ctx}`, 'bdc', 'non_receptionne_ttc', 'Reliquat à réceptionner', m)}>{fmt(r.non_receptionnes_ttc)}</DrillCell>
                          : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>0</td>}
                        <td style={{ padding: '5px 12px' }}><PctBar value={r.pct_receptionne} /></td>
                        <td style={{ padding: '5px 12px', color: r.date_ancienne_nr ? '#c62828' : '#aaa', fontSize: '0.73rem' }}>
                          {fmtDate(r.date_ancienne_nr)}
                        </td>
                      </Tr>
                      );
                    })}
                </tbody>
                {sBdcProjet.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={4}>TOTAL ({sBdcProjet.sorted.length} lignes)</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Commandes BDC — tous les BDC', 'bdc', 'total_ttc')}>{fmtUnit(sBdcProjet.sorted.reduce((a, r) => a + r.total_commandes_ttc, 0), getUnit('bdc-projet'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BDC — part réceptionnée', 'bdc', 'receptionne_ttc', 'Réceptionné')}>{fmtUnit(sBdcProjet.sorted.reduce((a, r) => a + r.receptionnes_ttc, 0), getUnit('bdc-projet'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Restant à réceptionner — détail BDC', 'bdc', 'non_receptionne_ttc', 'Reliquat à réceptionner')}>{fmtUnit(sBdcProjet.sorted.reduce((a, r) => a + r.non_receptionnes_ttc, 0), getUnit('bdc-projet'))}</DrillCell>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pBdcProjet} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 2 — BDC par fournisseur
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('bdc-fournisseur')} {...sectionDropProps('bdc-fournisseur')}>
          <div id="sec-bdc-fournisseur" style={detailSectionCard}>
            <SectionHeader
              count={sBdcFour.sorted.length} open={openBdcFour} setOpen={setOpenBdcFour}
              dragHandle={sectionDragHandle('bdc-fournisseur')}
              onExport={() => exportExcel(sBdcFour.sorted, 'BDC_par_fournisseur')}
              onCopy={() => { const { html, plain } = rowsToHtml(sBdcFour.sorted.map(r => ({
                'Fournisseur': r.fournisseur, 'Projets': r.nb_projets, 'Nb BDC': r.nb_bdc,
                'Total TTC': fmt(r.total_commandes_ttc), 'Réceptionné TTC': fmt(r.receptionnes_ttc),
                'Non récép. TTC': fmt(r.non_receptionnes_ttc)
              })), 'brown'); copyHtmlToClipboard(html, plain); }}
            >
              🏭 Commandes BDC par fournisseur
            </SectionHeader>
            {openBdcFour && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sBdcFour.Th field="fournisseur">Fournisseur</sBdcFour.Th>
                    <sBdcFour.Th field="nb_projets" className="text-end">Projets</sBdcFour.Th>
                    <sBdcFour.Th field="nb_bdc" className="text-end">Nb BDC</sBdcFour.Th>
                    <sBdcFour.Th field="total_commandes_ttc" className="text-end">Total TTC</sBdcFour.Th>
                    <sBdcFour.Th field="receptionnes_ttc" className="text-end">Réceptionné TTC</sBdcFour.Th>
                    <sBdcFour.Th field="non_receptionnes_ttc" className="text-end">Non récép. TTC</sBdcFour.Th>
                    <ThStatic style={{ minWidth: 140 }}>Taux réception</ThStatic>
                  </tr>
                </thead>
                <tbody>
                  {pBdcFour.visible.length === 0
                    ? <tr><td colSpan={7} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pBdcFour.visible.map((r, i) => {
                      const p = pct(r.receptionnes_ttc, r.total_commandes_ttc);
                      const m = { fournisseur_id: r.IDFOURNISSEUR };
                      return (
                        <Tr key={i} style={trZebra(i)}>
                          <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.fournisseur}</td>
                          <td className="text-end" style={{ padding: '5px 12px' }}>{fmt(r.nb_projets)}</td>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC — ${r.fournisseur}`, 'bdc', 'total_ttc', null, m)}>{fmt(r.nb_bdc)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC — ${r.fournisseur}`, 'bdc', 'total_ttc', null, m)}>{fmt(r.total_commandes_ttc)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BDC réceptionnés — ${r.fournisseur}`, 'bdc', 'receptionne_ttc', 'Réceptionné', m)}>{fmt(r.receptionnes_ttc)}</DrillCell>
                          {r.non_receptionnes_ttc > 0
                            ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#e65100', fontWeight: 600 }} onClick={() => openDrill(`BDC non réceptionnés — ${r.fournisseur}`, 'bdc', 'non_receptionne_ttc', 'Reliquat à réceptionner', m)}>{fmt(r.non_receptionnes_ttc)}</DrillCell>
                            : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>0</td>}
                          <td style={{ padding: '5px 12px' }}><PctBar value={p} /></td>
                        </Tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <TablePager {...pBdcFour} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 3 — BR par projet
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('br')} {...sectionDropProps('br')}>
          <div id="sec-br" style={detailSectionCard}>
            <SectionHeader
              count={sBr.sorted.length} open={openBr} setOpen={setOpenBr}
              dragHandle={sectionDragHandle('br')}
              unitPicker={<UnitPicker unit={getUnit('br')} onChange={(v) => setUnit('br', v)} />}
              onExport={() => exportExcel(sBr.sorted, 'BR_par_projet')}
              onCopy={() => { const { html, plain } = rowsToHtml(sBr.sorted.map(r => ({
                'Projet': r.projet, 'Responsable': r.Responsable || '—', 'Nb BR': r.nb_br,
                'Total réception TTC': fmt(r.total_reception_ttc), 'Facturé TTC': fmt(r.facturees_ttc),
                'À facturer TTC': fmt(r.non_facturees_ttc), 'Taux fact.': `${r.pct_facture}%`
              })), 'brown'); copyHtmlToClipboard(html, plain); }}
            >
              📦 Bons de Réception par projet
            </SectionHeader>
            {openBr && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sBr.Th field="projet">Projet</sBr.Th>
                    <sBr.Th field="Responsable">Responsable</sBr.Th>
                    <sBr.Th field="nb_br" className="text-end">Nb BR</sBr.Th>
                    <sBr.Th field="total_reception_ttc" className="text-end">Total réception TTC</sBr.Th>
                    <sBr.Th field="facturees_ttc" className="text-end">Facturé TTC</sBr.Th>
                    <sBr.Th field="non_facturees_ttc" className="text-end">à facturer TTC</sBr.Th>
                    <sBr.Th field="pct_facture" style={{ minWidth: 140 }}>Taux facturation</sBr.Th>
                    <sBr.Th field="date_plus_ancienne_non_facturee">Date + anc. NF</sBr.Th>
                  </tr>
                </thead>
                <tbody>
                  {sBr.sorted.length === 0
                    ? <tr><td colSpan={8} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pBr.visible.map((r, i) => {
                      const m = { projet_id: r.projet_id };
                      return (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                        <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR — ${r.projet}`, 'br', 'total_ttc', null, m)}>{fmt(r.nb_br)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR — ${r.projet}`, 'br', 'total_ttc', null, m)}>{fmt(r.total_reception_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR facturés — ${r.projet}`, 'br', 'facturees_ttc', 'Facturé', m)}>{fmt(r.facturees_ttc)}</DrillCell>
                        {r.non_facturees_ttc > 0
                          ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#e65100', fontWeight: 600 }} onClick={() => openDrill(`BR à facturer — ${r.projet}`, 'br', 'non_facturees_ttc', 'Reliquat à facturer', m)}>{fmt(r.non_facturees_ttc)}</DrillCell>
                          : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>0</td>}
                        <td style={{ padding: '5px 12px' }}><PctBar value={r.pct_facture} color="#1565c0" /></td>
                        <td style={{ padding: '5px 12px', color: r.date_plus_ancienne_non_facturee ? '#c62828' : '#aaa', fontSize: '0.73rem' }}>
                          {fmtDate(r.date_plus_ancienne_non_facturee)}
                        </td>
                      </Tr>
                      );
                    })}
                </tbody>
                {sBr.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={3}>TOTAL</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Réceptions BR — tous les BR', 'br', 'total_ttc')}>{fmtUnit(sBr.sorted.reduce((a, r) => a + r.total_reception_ttc, 0), getUnit('br'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — part facturée fournisseur', 'br', 'facturees_ttc', 'Facturé')}>{fmtUnit(sBr.sorted.reduce((a, r) => a + r.facturees_ttc, 0), getUnit('br'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('À facturer — détail BR', 'br', 'non_facturees_ttc', 'Reliquat à facturer')}>{fmtUnit(sBr.sorted.reduce((a, r) => a + r.non_facturees_ttc, 0), getUnit('br'))}</DrillCell>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pBr} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 3b — BR facturation vente (QFV)
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('br-fv')} {...sectionDropProps('br-fv')}>
          <div id="sec-br-fv" style={detailSectionCard}>
            <SectionHeader
              count={sBrFv.sorted.length} open={openBrFv} setOpen={setOpenBrFv}
              dragHandle={sectionDragHandle('br-fv')}
              unitPicker={<UnitPicker unit={getUnit('br-fv')} onChange={(v) => setUnit('br-fv', v)} />}
              onExport={() => exportExcel(sBrFv.sorted, 'BR_facturation_vente')}
              onCopy={() => { const { html, plain } = rowsToHtml(sBrFv.sorted.map(r => ({
                'Projet': r.projet, 'Responsable': r.Responsable || '—', 'Nb BR': r.nb_br,
                'Total réception TTC': fmt(r.total_reception_ttc), 'Facturé vente TTC': fmt(r.facturees_vente_ttc),
                'À facturer TTC': fmt(r.non_facturees_vente_ttc), 'RG retenue': fmt(r.rg_vente_ttc || 0), 'Ancienneté (j)': r.anciennete_jours || ''
              })), 'purple'); copyHtmlToClipboard(html, plain); }}
            >
              🟣 Réceptions BR — Facturation vente par projet
            </SectionHeader>
            {openBrFv && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sBrFv.Th field="projet">Projet</sBrFv.Th>
                    <sBrFv.Th field="Responsable">Responsable</sBrFv.Th>
                    <sBrFv.Th field="nb_br" className="text-end">Nb BR</sBrFv.Th>
                    <sBrFv.Th field="total_reception_ttc" className="text-end">Total réception TTC</sBrFv.Th>
                    <sBrFv.Th field="facturees_vente_ttc" className="text-end">Facturé vente TTC</sBrFv.Th>
                    <sBrFv.Th field="non_facturees_vente_ttc" className="text-end">À facturer TTC</sBrFv.Th>
                    <sBrFv.Th field="rg_vente_ttc" className="text-end">RG retenue</sBrFv.Th>
                    <sBrFv.Th field="pct_facture_vente" style={{ minWidth: 140 }}>Taux fact. vente</sBrFv.Th>
                    <sBrFv.Th field="anciennete_jours" className="text-end">Ancienneté (j)</sBrFv.Th>
                    <sBrFv.Th field="date_plus_ancienne_nfv">Date + anc. NFV</sBrFv.Th>
                  </tr>
                </thead>
                <tbody>
                  {sBrFv.sorted.length === 0
                    ? <tr><td colSpan={10} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pBrFv.visible.map((r, i) => (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                        <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR (vente) — ${r.projet}`, 'br', 'total_ttc', null, { projet_id: r.projet_id })}>{fmt(r.nb_br)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR (vente) — ${r.projet}`, 'br', 'total_ttc', null, { projet_id: r.projet_id })}>{fmt(r.total_reception_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#2e7d32', fontWeight: 600 }} onClick={() => openDrill(`BR facturés vente — ${r.projet}`, 'br', 'facturees_vente_ttc', 'Facturé vente', { projet_id: r.projet_id })}>{fmt(r.facturees_vente_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828' }} onClick={() => openDrill(`BR à facturer vente — ${r.projet}`, 'br', 'non_facturees_vente_ttc', 'À facturer vente', { projet_id: r.projet_id })}>{fmt(r.non_facturees_vente_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#7b5ea7' }} onClick={() => openDrill(`BR RG retenue — ${r.projet}`, 'br', 'rg_vente_ttc', 'RG retenue', { projet_id: r.projet_id })}>{r.rg_vente_ttc ? fmt(r.rg_vente_ttc) : '—'}</DrillCell>
                        <td style={{ padding: '5px 12px' }}><PctBar value={pct(r.facturees_vente_ttc, r.total_reception_ttc)} color="#7b1fa2" /></td>
                        <td className="text-end" style={{ padding: '5px 12px' }}>
                          {r.anciennete_jours > 0
                            ? <span style={{ color: r.anciennete_jours > 60 ? '#c62828' : r.anciennete_jours > 30 ? '#e65100' : '#555', fontWeight: r.anciennete_jours > 30 ? 700 : 400 }}>
                                {fmt(r.anciennete_jours)} j
                              </span>
                            : '—'}
                        </td>
                        <td style={{ padding: '5px 12px' }}>{fmtDate(r.date_plus_ancienne_nfv)}</td>
                      </Tr>
                    ))}
                </tbody>
                {sBrFv.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={3}>TOTAL</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Réceptions BR (vente) — tous les BR', 'br', 'total_ttc')}>{fmtUnit(sBrFv.sorted.reduce((a, r) => a + r.total_reception_ttc, 0), getUnit('br-fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — facturés en vente', 'br', 'facturees_vente_ttc', 'Facturé vente')}>{fmtUnit(sBrFv.sorted.reduce((a, r) => a + r.facturees_vente_ttc, 0), getUnit('br-fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — à facturer en vente', 'br', 'non_facturees_vente_ttc', 'À facturer vente')}>{fmtUnit(sBrFv.sorted.reduce((a, r) => a + r.non_facturees_vente_ttc, 0), getUnit('br-fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — RG retenue sur factures vente', 'br', 'rg_vente_ttc', 'RG retenue')}>{fmtUnit(sBrFv.sorted.reduce((a, r) => a + (r.rg_vente_ttc || 0), 0), getUnit('br-fv'))}</DrillCell>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pBrFv} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 3c — BR facturation vente par projet + fournisseur
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('br-fv-four')} {...sectionDropProps('br-fv-four')}>
          <div id="sec-br-fv-four" style={detailSectionCard}>
            <SectionHeader
              count={sBrFvFour.sorted.length} open={openBrFvFour} setOpen={setOpenBrFvFour}
              dragHandle={sectionDragHandle('br-fv-four')}
              unitPicker={<UnitPicker unit={getUnit('br-fv-four')} onChange={(v) => setUnit('br-fv-four', v)} />}
              onExport={() => exportExcel(sBrFvFour.sorted, 'BR_facturation_vente_fournisseur')}
              onCopy={() => { const { html, plain } = rowsToHtml(sBrFvFour.sorted.map(r => ({
                'Projet': r.projet, 'Responsable': r.Responsable || '—', 'Fournisseur': r.fournisseur || '—',
                'Nb BR': r.nb_br, 'Total réception TTC': fmt(r.total_reception_ttc),
                'Facturé vente TTC': fmt(r.facturees_vente_ttc), 'À facturer TTC': fmt(r.non_facturees_vente_ttc),
                'RG retenue': fmt(r.rg_vente_ttc || 0)
              })), 'purple'); copyHtmlToClipboard(html, plain); }}
            >
              🟣 Réceptions BR — Facturation vente par projet / responsable / fournisseur
            </SectionHeader>
            {openBrFvFour && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sBrFvFour.Th field="projet">Projet</sBrFvFour.Th>
                    <sBrFvFour.Th field="Responsable">Responsable</sBrFvFour.Th>
                    <sBrFvFour.Th field="fournisseur">Fournisseur</sBrFvFour.Th>
                    <sBrFvFour.Th field="nb_br" className="text-end">Nb BR</sBrFvFour.Th>
                    <sBrFvFour.Th field="total_reception_ttc" className="text-end">Total réception TTC</sBrFvFour.Th>
                    <sBrFvFour.Th field="facturees_vente_ttc" className="text-end">Facturé vente TTC</sBrFvFour.Th>
                    <sBrFvFour.Th field="non_facturees_vente_ttc" className="text-end">À facturer TTC</sBrFvFour.Th>
                    <sBrFvFour.Th field="rg_vente_ttc" className="text-end">RG retenue</sBrFvFour.Th>
                    <sBrFvFour.Th field="pct_facture_vente" style={{ minWidth: 140 }}>Taux fact. vente</sBrFvFour.Th>
                    <sBrFvFour.Th field="anciennete_jours" className="text-end">Ancienneté (j)</sBrFvFour.Th>
                    <sBrFvFour.Th field="date_plus_ancienne_nfv">Date + anc. NFV</sBrFvFour.Th>
                  </tr>
                </thead>
                <tbody>
                  {sBrFvFour.sorted.length === 0
                    ? <tr><td colSpan={11} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pBrFvFour.visible.map((r, i) => (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                        <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                        <td style={{ padding: '5px 12px' }}>{r.fournisseur || '—'}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR (vente) — ${r.projet} / ${r.fournisseur || '—'}`, 'br', 'total_ttc', null, { projet_id: r.projet_id, fournisseur_id: r.fournisseur_id })}>{fmt(r.nb_br)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`BR (vente) — ${r.projet} / ${r.fournisseur || '—'}`, 'br', 'total_ttc', null, { projet_id: r.projet_id, fournisseur_id: r.fournisseur_id })}>{fmt(r.total_reception_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#2e7d32', fontWeight: 600 }} onClick={() => openDrill(`BR facturés vente — ${r.projet} / ${r.fournisseur || '—'}`, 'br', 'facturees_vente_ttc', 'Facturé vente', { projet_id: r.projet_id, fournisseur_id: r.fournisseur_id })}>{fmt(r.facturees_vente_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828' }} onClick={() => openDrill(`BR à facturer vente — ${r.projet} / ${r.fournisseur || '—'}`, 'br', 'non_facturees_vente_ttc', 'À facturer vente', { projet_id: r.projet_id, fournisseur_id: r.fournisseur_id })}>{fmt(r.non_facturees_vente_ttc)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#7b5ea7' }} onClick={() => openDrill(`BR RG retenue — ${r.projet} / ${r.fournisseur || '—'}`, 'br', 'rg_vente_ttc', 'RG retenue', { projet_id: r.projet_id, fournisseur_id: r.fournisseur_id })}>{r.rg_vente_ttc ? fmt(r.rg_vente_ttc) : '—'}</DrillCell>
                        <td style={{ padding: '5px 12px' }}><PctBar value={pct(r.facturees_vente_ttc, r.total_reception_ttc)} color="#7b1fa2" /></td>
                        <td className="text-end" style={{ padding: '5px 12px' }}>
                          {r.anciennete_jours > 0
                            ? <span style={{ color: r.anciennete_jours > 60 ? '#c62828' : r.anciennete_jours > 30 ? '#e65100' : '#555', fontWeight: r.anciennete_jours > 30 ? 700 : 400 }}>
                                {fmt(r.anciennete_jours)} j
                              </span>
                            : '—'}
                        </td>
                        <td style={{ padding: '5px 12px' }}>{fmtDate(r.date_plus_ancienne_nfv)}</td>
                      </Tr>
                    ))}
                </tbody>
                {sBrFvFour.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={4}>TOTAL</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Réceptions BR (vente) — tous les BR', 'br', 'total_ttc')}>{fmtUnit(sBrFvFour.sorted.reduce((a, r) => a + r.total_reception_ttc, 0), getUnit('br-fv-four'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — facturés en vente', 'br', 'facturees_vente_ttc', 'Facturé vente')}>{fmtUnit(sBrFvFour.sorted.reduce((a, r) => a + r.facturees_vente_ttc, 0), getUnit('br-fv-four'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — à facturer en vente', 'br', 'non_facturees_vente_ttc', 'À facturer vente')}>{fmtUnit(sBrFvFour.sorted.reduce((a, r) => a + r.non_facturees_vente_ttc, 0), getUnit('br-fv-four'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('BR — RG retenue sur factures vente', 'br', 'rg_vente_ttc', 'RG retenue')}>{fmtUnit(sBrFvFour.sorted.reduce((a, r) => a + (r.rg_vente_ttc || 0), 0), getUnit('br-fv-four'))}</DrillCell>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pBrFvFour} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 4 — Factures achat par projet
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('fa')} {...sectionDropProps('fa')}>
          <div id="sec-fa" style={detailSectionCard}>
            <SectionHeader
              count={sFa.sorted.length} open={openFa} setOpen={setOpenFa}
              dragHandle={sectionDragHandle('fa')}
              unitPicker={<UnitPicker unit={getUnit('fa')} onChange={(v) => setUnit('fa', v)} />}
              onExport={() => exportExcel(sFa.sorted, 'Factures_achat')}
              onCopy={() => { const { html, plain } = rowsToHtml(sFa.sorted.map(r => ({
                'Projet': r.projet, 'Fournisseur': r.fournisseur || '—', 'Responsable': r.Responsable || '—',
                'Nb fact.': r.nb_factures, 'Total TTC': fmt(r.total_facturation_ttc), 'Payé TTC': fmt(r.payes_ttc),
                'À payer TTC': fmt(r.non_payes_ttc)
              })), 'amber'); copyHtmlToClipboard(html, plain); }}
            >
              🧾 Factures achat par projet
            </SectionHeader>
            {openFa && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sFa.Th field="projet">Projet</sFa.Th>
                    <sFa.Th field="fournisseur">Fournisseur</sFa.Th>
                    <sFa.Th field="Responsable">Responsable</sFa.Th>
                    <sFa.Th field="nb_factures" className="text-end">Nb fact.</sFa.Th>
                    <sFa.Th field="total_facturation_ttc" className="text-end">Total TTC</sFa.Th>
                    <sFa.Th field="payes_ttc" className="text-end">Payé TTC</sFa.Th>
                    <sFa.Th field="non_payes_ttc" className="text-end">à payer TTC</sFa.Th>
                    <ThStatic style={{ minWidth: 140 }}>Taux paiement</ThStatic>
                    <sFa.Th field="date_ancienne_np">Date + anc. à payer</sFa.Th>
                  </tr>
                </thead>
                <tbody>
                  {sFa.sorted.length === 0
                    ? <tr><td colSpan={9} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pFa.visible.map((r, i) => {
                      const p = pct(r.payes_ttc, r.total_facturation_ttc);
                      const m = { projet_id: r.projet_id, fournisseur_id: r.IDFOURNISSEUR };
                      const ctx = `${r.projet} / ${r.fournisseur || '—'}`;
                      return (
                        <Tr key={i} style={trZebra(i)}>
                          <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                          <td style={{ padding: '5px 12px' }}>{r.fournisseur || '—'}</td>
                          <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Factures achat — ${ctx}`, 'fa', 'total_ttc', null, m)}>{fmt(r.nb_factures)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Factures achat — ${ctx}`, 'fa', 'total_ttc', null, m)}>{fmt(r.total_facturation_ttc)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#2e7d32' }} onClick={() => openDrill(`Factures achat payées — ${ctx}`, 'fa', 'paye_ttc', 'Payé', m)}>{fmt(r.payes_ttc)}</DrillCell>
                          {r.non_payes_ttc > 0
                            ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 600 }} onClick={() => openDrill(`Factures achat à payer — ${ctx}`, 'fa', 'non_paye_ttc', 'Reliquat à payer', m)}>{fmt(r.non_payes_ttc)}</DrillCell>
                            : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>0</td>}
                          <td style={{ padding: '5px 12px' }}><PctBar value={p} color="#2e7d32" /></td>
                          <td style={{ padding: '5px 12px', color: r.date_ancienne_np ? '#c62828' : '#aaa', fontSize: '0.73rem' }}>
                            {fmtDate(r.date_ancienne_np)}
                          </td>
                        </Tr>
                      );
                    })}
                </tbody>
                {sFa.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={4}>TOTAL ({sFa.sorted.length} lignes)</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — toutes (part projet)', 'fa', 'total_ttc')}>{fmtUnit(sFa.sorted.reduce((a, r) => a + r.total_facturation_ttc, 0), getUnit('fa'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — part payée', 'fa', 'paye_ttc', 'Payé')}>{fmtUnit(sFa.sorted.reduce((a, r) => a + r.payes_ttc, 0), getUnit('fa'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — reliquat à payer', 'fa', 'non_paye_ttc', 'Reliquat à payer')}>{fmtUnit(sFa.sorted.reduce((a, r) => a + r.non_payes_ttc, 0), getUnit('fa'))}</DrillCell>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pFa} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 4b — Factures achat par fournisseur
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('fa-four')} {...sectionDropProps('fa-four')}>
          <div id="sec-fa-four" style={detailSectionCard}>
            <SectionHeader
              count={sFaFour.sorted.length} open={openFaFour} setOpen={setOpenFaFour}
              dragHandle={sectionDragHandle('fa-four')}
              unitPicker={<UnitPicker unit={getUnit('fa-four')} onChange={(v) => setUnit('fa-four', v)} />}
              onExport={() => exportExcel(sFaFour.sorted, 'Factures_achat_par_fournisseur')}
              onCopy={() => { const { html, plain } = rowsToHtml(sFaFour.sorted.map(r => ({
                'Fournisseur': r.fournisseur || '—', 'Projets': r.nb_projets, 'Nb fact.': r.nb_factures,
                'Total TTC': fmt(r.total_facturation_ttc), 'Payé TTC': fmt(r.payes_ttc), 'À payer TTC': fmt(r.non_payes_ttc)
              })), 'amber'); copyHtmlToClipboard(html, plain); }}
            >
              🏭 Factures achat par fournisseur
            </SectionHeader>
            {openFaFour && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sFaFour.Th field="fournisseur">Fournisseur</sFaFour.Th>
                    <sFaFour.Th field="nb_projets" className="text-end">Projets</sFaFour.Th>
                    <sFaFour.Th field="nb_factures" className="text-end">Nb fact.</sFaFour.Th>
                    <sFaFour.Th field="total_facturation_ttc" className="text-end">Total TTC</sFaFour.Th>
                    <sFaFour.Th field="payes_ttc" className="text-end">Payé TTC</sFaFour.Th>
                    <sFaFour.Th field="non_payes_ttc" className="text-end">à payer TTC</sFaFour.Th>
                    <ThStatic style={{ minWidth: 140 }}>Taux paiement</ThStatic>
                  </tr>
                </thead>
                <tbody>
                  {sFaFour.sorted.length === 0
                    ? <tr><td colSpan={7} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pFaFour.visible.map((r, i) => {
                      const p = pct(r.payes_ttc, r.total_facturation_ttc);
                      const m = { fournisseur_id: r.IDFOURNISSEUR };
                      const ctx = r.fournisseur || '—';
                      return (
                        <Tr key={i} style={trZebra(i)}>
                          <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.fournisseur || '—'}</td>
                          <td className="text-end" style={{ padding: '5px 12px' }}>{r.nb_projets}</td>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Factures achat — ${ctx}`, 'fa', 'total_ttc', null, m)}>{r.nb_factures}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Factures achat — ${ctx}`, 'fa', 'total_ttc', null, m)}>{fmt(r.total_facturation_ttc)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#2e7d32' }} onClick={() => openDrill(`Factures achat payées — ${ctx}`, 'fa', 'paye_ttc', 'Payé', m)}>{fmt(r.payes_ttc)}</DrillCell>
                          {r.non_payes_ttc > 0
                            ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 600 }} onClick={() => openDrill(`Factures achat à payer — ${ctx}`, 'fa', 'non_paye_ttc', 'Reliquat à payer', m)}>{fmt(r.non_payes_ttc)}</DrillCell>
                            : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>0</td>}
                          <td style={{ padding: '5px 12px' }}><PctBar value={p} color="#2e7d32" /></td>
                        </Tr>
                      );
                    })}
                </tbody>
                {sFaFour.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={3}>TOTAL ({sFaFour.sorted.length} fournisseurs)</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — toutes (part projet)', 'fa', 'total_ttc')}>{fmtUnit(sFaFour.sorted.reduce((a, r) => a + r.total_facturation_ttc, 0), getUnit('fa-four'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — part payée', 'fa', 'paye_ttc', 'Payé')}>{fmtUnit(sFaFour.sorted.reduce((a, r) => a + r.payes_ttc, 0), getUnit('fa-four'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures achat — reliquat à payer', 'fa', 'non_paye_ttc', 'Reliquat à payer')}>{fmtUnit(sFaFour.sorted.reduce((a, r) => a + r.non_payes_ttc, 0), getUnit('fa-four'))}</DrillCell>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pFaFour} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 5 — Factures vente par projet
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('fv')} {...sectionDropProps('fv')}>
          <div id="sec-fv" style={detailSectionCard}>
            <SectionHeader
              count={sFv.sorted.length} open={openFv} setOpen={setOpenFv}
              dragHandle={sectionDragHandle('fv')}
              unitPicker={<UnitPicker unit={getUnit('fv')} onChange={(v) => setUnit('fv', v)} />}
              onExport={() => exportExcel(sFv.sorted, 'Factures_vente')}
              onCopy={() => { const { html, plain } = rowsToHtml(sFv.sorted.map(r => ({
                'Projet': r.projet, 'Responsable': r.Responsable || '—', 'Société Client': r.societe_client || '—',
                'Nb fact.': r.nb_factures, 'CA TTC': fmt(r.total_facturation_vente_ttc), 'Encaissé TTC': fmt(r.payes_ttc),
                'Restant (hors RG)': fmt(r.non_payes_ttc), 'RG retenue': fmt(r.rg_retenue_ttc)
              })), 'brown'); copyHtmlToClipboard(html, plain); }}
            >
              💰 Factures vente (CA) par projet
              <span title="Les filtres archivage et société (BDC/BR/factures achat) ne s'appliquent pas ici : mern_cienco.facturegroup n'est pas couvert."
                style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 500, marginLeft: 8 }}>
                (non filtré par archivage ni par société)
              </span>
            </SectionHeader>
            {openFv && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sFv.Th field="projet">Projet</sFv.Th>
                    <sFv.Th field="Responsable">Responsable</sFv.Th>
                    <sFv.Th field="societe_client">Societe Client</sFv.Th>
                    <sFv.Th field="nb_factures" className="text-end">Nb fact.</sFv.Th>
                    <sFv.Th field="total_facturation_vente_ttc" className="text-end">CA TTC</sFv.Th>
                    <sFv.Th field="payes_ttc" className="text-end">Encaissé TTC</sFv.Th>
                    <sFv.Th field="non_payes_ttc" className="text-end">Restant (hors RG)</sFv.Th>
                    <sFv.Th field="rg_retenue_ttc" className="text-end">RG retenue</sFv.Th>
                    <ThStatic style={{ minWidth: 140 }}>Taux encaissement (hors RG)</ThStatic>
                    <sFv.Th field="date_ancienne_np">Date + anc. à payer</sFv.Th>
                  </tr>
                </thead>
                <tbody>
                  {sFv.sorted.length === 0
                    ? <tr><td colSpan={10} className="text-center text-muted py-3">Aucune donnée</td></tr>
                    : pFv.visible.map((r, i) => {
                      const p = pct(r.payes_ttc, r.total_facturation_vente_ttc - (r.rg_retenue_ttc || 0));
                      const m = { projet_id: r.projet_id, societe_client_id: r.societe_client_id };
                      const ctx = `${r.projet} / ${r.societe_client || '—'}`;
                      return (
                        <Tr key={i} style={trZebra(i)}>
                          <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.projet}</td>
                          <td style={{ padding: '5px 12px' }}>{r.Responsable || '—'}</td>
                          <td style={{ padding: '5px 12px' }}>{r.societe_client || '—'}</td>
                          <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Factures vente — ${ctx}`, 'factures_vente', 'total_ttc', null, m)}>{fmt(r.nb_factures)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px', fontWeight: 600 }} onClick={() => openDrill(`Factures vente — ${ctx}`, 'factures_vente', 'total_ttc', null, m)}>{fmt(r.total_facturation_vente_ttc)}</DrillCell>
                          <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#2e7d32' }} onClick={() => openDrill(`Factures vente encaissées — ${ctx}`, 'factures_vente', 'paye_ttc', 'Encaissé', m)}>{fmt(r.payes_ttc)}</DrillCell>
                          {r.non_payes_ttc > 0.5
                            ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 600 }} onClick={() => openDrill(`Factures vente à encaisser — ${ctx}`, 'factures_vente', 'non_paye_ttc', 'Reliquat à encaisser (hors RG)', m)}>{fmt(r.non_payes_ttc)}</DrillCell>
                            : <td className="text-end" style={{ padding: '5px 12px' }}><Badge bg="success" style={{ fontSize: '0.68rem' }}>Soldé</Badge></td>}
                          {r.rg_retenue_ttc > 0.5
                            ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#7b5ea7' }} onClick={() => openDrill(`RG retenue — ${ctx}`, 'factures_vente', 'rg_ttc', 'RG retenue', m)}>{fmt(r.rg_retenue_ttc)}</DrillCell>
                            : <td className="text-end" style={{ padding: '5px 12px', color: '#bbb' }}>—</td>}
                          <td style={{ padding: '5px 12px' }}><PctBar value={p} color="#6d4e36" /></td>
                          <td style={{ padding: '5px 12px', color: r.date_ancienne_np ? '#c62828' : '#aaa', fontSize: '0.73rem' }}>
                            {fmtDate(r.date_ancienne_np)}
                          </td>
                        </Tr>
                      );
                    })}
                </tbody>
                {sFv.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={4}>TOTAL</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('CA Vente — toutes les factures vente', 'factures_vente', 'total_ttc')}>{fmtUnit(sFv.sorted.reduce((a, r) => a + r.total_facturation_vente_ttc, 0), getUnit('fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Factures vente — encaissé', 'factures_vente', 'paye_ttc', 'Encaissé')}>{fmtUnit(sFv.sorted.reduce((a, r) => a + r.payes_ttc, 0), getUnit('fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Restant à encaisser — détail factures vente', 'factures_vente', 'non_paye_ttc', 'Reliquat à encaisser (hors RG)')}>{fmtUnit(sFv.sorted.reduce((a, r) => a + r.non_payes_ttc, 0), getUnit('fv'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#7b5ea7' }} onClick={() => openDrill('RG retenue — factures vente', 'factures_vente', 'rg_ttc', 'RG retenue')}>{fmtUnit(sFv.sorted.reduce((a, r) => a + (r.rg_retenue_ttc || 0), 0), getUnit('fv'))}</DrillCell>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pFv} />
            </>)}
          </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              SECTION 6 — Charges par type
          ══════════════════════════════════════════════════════════════ */}
          {sCharges.sorted.length > 0 && (
            <div style={sectionWrapStyle('charges')} {...sectionDropProps('charges')}>
            <div id="sec-charges" style={detailSectionCard}>
              <SectionHeader
                count={sCharges.sorted.length} open={openCharges} setOpen={setOpenCharges}
                dragHandle={sectionDragHandle('charges')}
                unitPicker={<UnitPicker unit={getUnit('charges')} onChange={(v) => setUnit('charges', v)} />}
                onExport={() => exportExcel(sCharges.sorted, 'Charges_par_type')}
                onCopy={() => { const { html, plain } = rowsToHtml(sCharges.sorted.map(r => ({
                  'Type de charge': r.type_charge, 'Montant HT': fmt(r.montant_ht), 'Montant TTC': fmt(r.montant_ttc)
                })), 'brown'); copyHtmlToClipboard(html, plain); }}
              >
                <span><FaLayerGroup style={{ marginRight: 8 }} />Charges par type</span>
              </SectionHeader>
              {openCharges && (<>
              <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      <sCharges.Th field="type_charge">Type de charge</sCharges.Th>
                      <sCharges.Th field="montant_ht" className="text-end">Montant HT</sCharges.Th>
                      <sCharges.Th field="montant_ttc" className="text-end">Montant TTC</sCharges.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pCharges.visible.map((r, i) => (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px' }}>{r.type_charge}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openDrill(`Charges — ${r.type_charge}`, 'charges', 'montant_ttc', 'TTC', { type_charge: r.type_charge })}>{fmt(r.montant_ht)}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', fontWeight: 600 }} onClick={() => openDrill(`Charges — ${r.type_charge}`, 'charges', 'montant_ttc', 'TTC', { type_charge: r.type_charge })}>{fmt(r.montant_ttc)}</DrillCell>
                      </Tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }}>TOTAL</td>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Charges — toutes', 'charges', 'montant_ttc')}>{fmtUnit(sCharges.sorted.reduce((a, r) => a + r.montant_ht, 0), getUnit('charges'))}</DrillCell>
                      <DrillCell className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }} onClick={() => openDrill('Charges — toutes', 'charges', 'montant_ttc')}>{fmtUnit(sCharges.sorted.reduce((a, r) => a + r.montant_ttc, 0), getUnit('charges'))}</DrillCell>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <TablePager {...pCharges} />
              </>)}
            </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              SECTION — FACTURES ACHAT PAR ÉCHÉANCE
          ══════════════════════════════════════════════════════════════ */}
          {facturesEcheance.length > 0 && (() => {
            return (
              <div style={sectionWrapStyle('factures-echeance')} {...sectionDropProps('factures-echeance')}>
              <div id="sec-factures-echeance" style={detailSectionCard}>
                <SectionHeader
                  count={sFe.sorted.length} open={openFe} setOpen={setOpenFe}
                  dragHandle={sectionDragHandle('factures-echeance')}
                  unitPicker={<UnitPicker unit={getUnit('factures-echeance')} onChange={(v) => setUnit('factures-echeance', v)} />}
                  onExport={() => exportExcel(sFe.sorted, 'Factures_echeance')}
                  onCopy={() => { const { html, plain } = rowsToHtml(sFe.sorted.map(r => ({
                    'ID': r.IDFACTURE, 'N° Facture': r.NFacture || '—', 'Fournisseur': r.Fournisseur || '—',
                    'Société': r.Societe || '—', 'Date Fact.': fmtDate(r.DateFacture), 'Date Échéance': fmtDate(r.DateEcheance),
                    'Total TTC': fmt(r.TotalTTC), 'Reste à payer': fmt(r.ResteAPayer), 'Statut': r.statut_echeance
                  })), 'amber'); copyHtmlToClipboard(html, plain); }}
                >
                  📅 Factures achat — suivi des échéances
                </SectionHeader>
                {openFe && (<>

                {/* ── cards clicables ── */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '0.85rem 1rem 0.5rem' }}>
                  {echeanceStats.map(s => (
                    <div
                      key={s.key}
                      onClick={() => setEcheanceFilter(echeanceFilter === s.key ? null : s.key)}
                      style={{
                        flex: '1 1 180px', minWidth: 160, cursor: 'pointer',
                        border: `2px solid ${echeanceFilter === s.key ? s.color : s.color + '44'}`,
                        borderRadius: 10, background: echeanceFilter === s.key ? s.color + '22' : s.bg,
                        padding: '0.65rem 0.9rem',
                        boxShadow: echeanceFilter === s.key ? `0 0 0 3px ${s.color}33` : '0 1px 4px rgba(0,0,0,0.07)',
                        transition: 'all 0.18s'
                      }}
                    >
                      <div style={{ fontSize: '0.68rem', color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
                        {s.icon} {s.label}
                      </div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: s.color }}>
                        {fmt(s.montant)} <span style={{ fontSize: '0.72rem', fontWeight: 500 }}>MAD</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 2 }}>
                        {s.count} facture{s.count > 1 ? 's' : ''}
                        {echeanceFilter === s.key && (
                          <span style={{ marginLeft: 6, color: s.color, fontWeight: 700 }}>✕ filtré</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── table ── */}
                <div style={{ overflowX: 'auto', padding: '0 0.75rem 0.5rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <sFe.Th field="IDFACTURE">ID</sFe.Th>
                        <sFe.Th field="NFacture">N° Facture</sFe.Th>
                        <sFe.Th field="Fournisseur">Fournisseur</sFe.Th>
                        <sFe.Th field="Societe">Société</sFe.Th>
                        <sFe.Th field="DateFacture">Date Fact.</sFe.Th>
                        <sFe.Th field="DateEcheance">Date Échéance</sFe.Th>
                        <sFe.Th field="TotalTTC" className="text-end">Total TTC</sFe.Th>
                        <sFe.Th field="ResteAPayer" className="text-end">Reste à payer</sFe.Th>
                        <sFe.Th field="statut_echeance">Statut</sFe.Th>
                        <sFe.Th field="Projets">Projets</sFe.Th>
                        <sFe.Th field="Responsables">Responsables</sFe.Th>
                      </tr>
                    </thead>
                    <tbody>
                      {pFe.visible.length === 0
                        ? <tr><td colSpan={11} className="text-center text-muted py-3">Aucune donnée</td></tr>
                        : pFe.visible.map((r, i) => {
                            const cfg = {
                              depasse: { color: '#c62828', label: '🔴 Dépassée' },
                              proche:  { color: '#e65100', label: '🟠 Proche'   },
                              ok:      { color: '#2e7d32', label: '🟢 À venir'  },
                              paye:    { color: '#555',    label: '✅ Payé'     }
                            }[r.statut_echeance] || {};
                            return (
                              <Tr key={i} style={trZebra(i)}>
                                <td style={{ padding: '5px 12px' }}>
                                  <a href={`https://cienco.ngrok.io/fiche_facture/${r.IDFACTURE}`} target="_blank" rel="noreferrer"
                                    style={{ fontWeight: 600, color: '#6d4e36' }}>
                                    {r.IDFACTURE}
                                  </a>
                                </td>
                                <td style={{ padding: '5px 12px' }}>{r.NFacture || '—'}</td>
                                <td style={{ padding: '5px 12px' }}>{r.Fournisseur || '—'}</td>
                                <td style={{ padding: '5px 12px' }}>{r.Societe || '—'}</td>
                                <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>{fmtDate(r.DateFacture)}</td>
                                <td style={{ padding: '5px 12px', whiteSpace: 'nowrap', color: cfg.color, fontWeight: 600 }}>
                                  {fmtDate(r.DateEcheance)}
                                </td>
                                <td className="text-end" style={{ padding: '5px 12px' }}>{fmt(r.TotalTTC)}</td>
                                <td className="text-end" style={{ padding: '5px 12px', color: r.ResteAPayer > 0 ? cfg.color : '#aaa', fontWeight: r.ResteAPayer > 0 ? 700 : 400 }}>
                                  {fmt(r.ResteAPayer)}
                                </td>
                                <td style={{ padding: '5px 12px' }}>
                                  <span style={{ color: cfg.color, fontWeight: 600, fontSize: '0.75rem' }}>
                                    {cfg.label}
                                  </span>
                                </td>
                                <td style={{ padding: '5px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={r.Projets}>{r.Projets || '—'}</td>
                                <td style={{ padding: '5px 12px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={r.Responsables}>{r.Responsables || '—'}</td>
                              </Tr>
                            );
                          })}
                    </tbody>
                    {sFe.sorted.length > 1 && (
                      <tfoot>
                        <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                          <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={6}>TOTAL ({sFe.sorted.length} factures)</td>
                          <td className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }}>{fmtUnit(sFe.sorted.reduce((a, r) => a + r.TotalTTC, 0), getUnit('factures-echeance'))}</td>
                          <td className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }}>{fmtUnit(sFe.sorted.reduce((a, r) => a + r.ResteAPayer, 0), getUnit('factures-echeance'))}</td>
                          <td colSpan={3} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <TablePager {...pFe} />
                </>)}
              </div>
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════════
              SECTION — Chèques sans facture (checks-four + checks-det = 1 unité draggable)
          ══════════════════════════════════════════════════════════════ */}
          <div style={sectionWrapStyle('checks')} {...sectionDropProps('checks')}>
          <div id="sec-checks" style={detailSectionCard}>
            <SectionHeader
              count={sChecksFourSoc.sorted.length} open={openChecksFour} setOpen={setOpenChecksFour}
              dragHandle={sectionDragHandle('checks')}
              unitPicker={<UnitPicker unit={getUnit('checks-four')} onChange={(v) => setUnit('checks-four', v)} />}
              onExport={() => exportExcel(sChecksFourSoc.sorted, 'Cheques_sans_facture_par_fournisseur')}
              onCopy={() => { const { html, plain } = rowsToHtml(sChecksFourSoc.sorted.map(r => ({
                'Fournisseur': r.fournisseur || '—', 'Société': r.societe || '—', 'Nb chèques': r.nb_cheques,
                'Montant total': fmt(r.montant_total), 'Critique (>30j)': r.nb_critique, 'Alerte (16-30j)': r.nb_minimal
              })), 'red'); copyHtmlToClipboard(html, plain); }}
            >
              🧾 Chèques sans facture — par fournisseur / société
            </SectionHeader>
            {openChecksFour && (<>
            {checksDetail.length > 0 && (
              <div style={{ padding: '0.6rem 0.75rem 0', borderBottom: '1px solid #f0e8df' }}>
                <Row className="g-2">
                  {checksAlertStats.map(s => (
                    <Col key={s.key} xs={4} md={2}>
                      <div
                        onClick={() => {
                          setChecksAlertFilter(checksAlertFilter === s.key ? null : s.key);
                          scrollTo('sec-checks');
                        }}
                        style={{
                          cursor: 'pointer', borderRadius: 8,
                          border: `2px solid ${checksAlertFilter === s.key ? s.color : s.color + '44'}`,
                          background: checksAlertFilter === s.key ? s.color + '22' : s.bg,
                          padding: '0.45rem 0.7rem',
                          boxShadow: checksAlertFilter === s.key ? `0 0 0 3px ${s.color}33` : '0 1px 4px rgba(0,0,0,0.06)',
                          transition: 'all 0.18s'
                        }}
                      >
                        <div style={{ fontSize: '0.62rem', color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                          {s.icon} {s.label}
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 800, color: s.color }}>
                          {fmt(s.montant)} <span style={{ fontSize: '0.65rem', fontWeight: 400 }}>MAD</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
                          <span style={{ fontSize: '0.68rem', color: '#777' }}>
                            {s.count} chèque{s.count > 1 ? 's' : ''}
                            {checksAlertFilter === s.key && <span style={{ marginLeft: 4, color: s.color, fontWeight: 700 }}>✕</span>}
                          </span>
                          <span style={{ fontSize: '0.62rem', color: s.color, opacity: 0.8 }}>↓ filtre</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <sChecksFourSoc.Th field="fournisseur">Fournisseur</sChecksFourSoc.Th>
                    <sChecksFourSoc.Th field="societe">Société</sChecksFourSoc.Th>
                    <sChecksFourSoc.Th field="nb_cheques" className="text-end">Nb chèques</sChecksFourSoc.Th>
                    <sChecksFourSoc.Th field="montant_total" className="text-end">Montant total</sChecksFourSoc.Th>
                    <sChecksFourSoc.Th field="nb_critique" className="text-end">🔴 Critique (&gt;30j)</sChecksFourSoc.Th>
                    <sChecksFourSoc.Th field="nb_minimal"  className="text-end">🟠 Alerte (16-30j)</sChecksFourSoc.Th>
                  </tr>
                </thead>
                <tbody>
                  {sChecksFourSoc.sorted.length === 0
                    ? <tr><td colSpan={6} className="text-center text-muted py-3">Aucun chèque sans facture sur la période</td></tr>
                    : pChecksFourSoc.visible.map((r, i) => {
                      const ctx = `${r.fournisseur || '—'} / ${r.societe || '—'}`;
                      const openChecks = (title, alerte, color) => openKpiDetail(title,
                        toDocRows(checksDetail.filter((c) => c.fournisseur === r.fournisseur && c.societe === r.societe && (!alerte || c.statut_alerte === alerte)),
                          pickCheque, (c) => c.montant, true), COLS_CHEQUES, 'montant', color, 'sec-checks');
                      return (
                      <Tr key={i} style={trZebra(i)}>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.fournisseur || '—'}</td>
                        <td style={{ padding: '5px 12px' }}>{r.societe || '—'}</td>
                        <DrillCell className="text-end" style={{ padding: '5px 12px' }} onClick={() => openChecks(`Chèques sans facture — ${ctx}`, null, '#c62828')}>{r.nb_cheques}</DrillCell>
                        <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 600 }} onClick={() => openChecks(`Chèques sans facture — ${ctx}`, null, '#c62828')}>{fmt(r.montant_total)}</DrillCell>
                        {r.nb_critique > 0
                          ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 700 }} onClick={() => openChecks(`Chèques critiques (>30j) — ${ctx}`, 'critique', '#c62828')}>{r.nb_critique} <span style={{ fontSize: '0.72rem', fontWeight: 400 }}>({fmt(r.montant_critique)})</span></DrillCell>
                          : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>—</td>}
                        {r.nb_minimal > 0
                          ? <DrillCell className="text-end" style={{ padding: '5px 12px', color: '#e65100', fontWeight: 700 }} onClick={() => openChecks(`Chèques en alerte (16-30j) — ${ctx}`, 'minimal', '#e65100')}>{r.nb_minimal} <span style={{ fontSize: '0.72rem', fontWeight: 400 }}>({fmt(r.montant_minimal)})</span></DrillCell>
                          : <td className="text-end" style={{ padding: '5px 12px', color: '#aaa' }}>—</td>}
                      </Tr>
                      );
                    })}
                </tbody>
                {sChecksFourSoc.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={2}>TOTAL ({sChecksFourSoc.sorted.length} lignes)</td>
                      <td className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }}>{sChecksFourSoc.sorted.reduce((a, r) => a + r.nb_cheques, 0)}</td>
                      <td className="text-end" style={{ padding: '7px 12px', color: '#c62828' }}>{fmtUnit(sChecksFourSoc.sorted.reduce((a, r) => a + r.montant_total, 0), getUnit('checks-four'))}</td>
                      <td className="text-end" style={{ padding: '7px 12px', color: '#c62828' }}>{sChecksFourSoc.sorted.reduce((a, r) => a + r.nb_critique, 0)}</td>
                      <td className="text-end" style={{ padding: '7px 12px', color: '#e65100' }}>{sChecksFourSoc.sorted.reduce((a, r) => a + r.nb_minimal, 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pChecksFourSoc} />
            </>)}
          </div>

          <div style={detailSectionCard}>
            <SectionHeader
              count={sChecksDetail.sorted.length} open={openChecksDet} setOpen={setOpenChecksDet}
              unitPicker={<UnitPicker unit={getUnit('checks-det')} onChange={(v) => setUnit('checks-det', v)} />}
              onExport={() => exportExcel(sChecksDetail.sorted.map(r => ({
              'ID': r.id, 'N° Chèque': r.num_cheque, 'Date': r.date_payment,
              'Jours': r.jours_ecart, 'Alerte': r.statut_alerte,
              'Montant': r.montant, 'Fournisseur': r.fournisseur, 'Société': r.societe,
              'Chantier': r.chantier_noms, 'Responsable': r.responsable_noms,
              'Observation': r.OBSERVATION, 'Saisi par': r.SAISIE_PAR
            })), 'Cheques_sans_facture_detail')}
              onCopy={() => { const { html, plain } = rowsToHtml(sChecksDetail.sorted.map(r => ({
                'ID': r.id, 'N° Chèque': r.num_cheque || '—', 'Date': fmtDate(r.date_payment),
                'Jours': r.jours_ecart != null ? `${r.jours_ecart}j` : '—', 'Alerte': r.statut_alerte,
                'Montant': fmt(r.montant), 'Fournisseur': r.fournisseur || '—', 'Société': r.societe || '—'
              })), 'red'); copyHtmlToClipboard(html, plain); }}
            >
              🧾 Chèques sans facture — liste détaillée
              {checksAlertFilter && (
                <span style={{ marginLeft: 8, fontSize: '0.78rem', fontWeight: 400, opacity: 0.85 }}>
                  {checksAlertFilter === 'critique' ? '🔴 critique' : checksAlertFilter === 'minimal' ? '🟠 alerte' : '🟢 ok'}
                  {' '}— <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={e => { e.stopPropagation(); setChecksAlertFilter(null); }}>tout afficher</span>
                </span>
              )}
            </SectionHeader>
            {openChecksDet && (<>
            <div style={{ overflowX: 'auto', padding: '0.5rem 0.75rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <ThStatic style={{ minWidth: 52 }}>ID</ThStatic>
                    <sChecksDetail.Th field="num_cheque">N° Chèque</sChecksDetail.Th>
                    <sChecksDetail.Th field="date_payment">Date</sChecksDetail.Th>
                    <sChecksDetail.Th field="jours_ecart" className="text-end">Jours</sChecksDetail.Th>
                    <ThStatic>Alerte</ThStatic>
                    <sChecksDetail.Th field="montant" className="text-end">Montant</sChecksDetail.Th>
                    <sChecksDetail.Th field="fournisseur">Fournisseur</sChecksDetail.Th>
                    <sChecksDetail.Th field="societe">Société</sChecksDetail.Th>
                    <sChecksDetail.Th field="chantier_noms">Chantier</sChecksDetail.Th>
                    <sChecksDetail.Th field="responsable_noms">Responsable</sChecksDetail.Th>
                    <ThStatic>Observation</ThStatic>
                    <sChecksDetail.Th field="SAISIE_PAR">Saisi par</sChecksDetail.Th>
                  </tr>
                </thead>
                <tbody>
                  {sChecksDetail.sorted.length === 0
                    ? <tr><td colSpan={12} className="text-center text-muted py-3">Aucun chèque sans facture sur la période</td></tr>
                    : pChecksDetail.visible.map((r, i) => {
                        const alertCfg = r.statut_alerte === 'critique'
                          ? { color: '#c62828', bg: '#ffebee', label: '🔴 Critique' }
                          : r.statut_alerte === 'minimal'
                          ? { color: '#e65100', bg: '#fff3e0', label: '🟠 Alerte' }
                          : { color: '#2e7d32', bg: '#e8f5e9', label: '🟢 OK' };
                        return (
                          <Tr key={i} style={r.statut_alerte !== 'ok' ? { background: alertCfg.bg + '66' } : trZebra(i)}>
                            <td style={{ padding: '5px 12px' }}>
                              <a
                                href={`https://cienco.ngrok.io/fiche_cheque/${r.id}`}
                                target="_blank" rel="noreferrer"
                                style={{ fontWeight: 700, color: '#1565c0', textDecoration: 'none' }}
                                title="Ouvrir la fiche chèque"
                              >
                                #{r.id}
                              </a>
                            </td>
                            <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.num_cheque || '—'}</td>
                            <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>{fmtDate(r.date_payment)}</td>
                            <td className="text-end" style={{ padding: '5px 12px', color: alertCfg.color, fontWeight: 600 }}>
                              {r.jours_ecart != null ? `${r.jours_ecart}j` : '—'}
                            </td>
                            <td style={{ padding: '5px 12px' }}>
                              <span style={{
                                fontSize: '0.7rem', fontWeight: 700, color: alertCfg.color,
                                background: alertCfg.bg, borderRadius: 4, padding: '1px 6px',
                                border: `1px solid ${alertCfg.color}44`
                              }}>
                                {alertCfg.label}
                              </span>
                            </td>
                            <td className="text-end" style={{ padding: '5px 12px', color: '#c62828', fontWeight: 600 }}>{fmt(r.montant)}</td>
                            <td style={{ padding: '5px 12px' }}>{r.fournisseur || '—'}</td>
                            <td style={{ padding: '5px 12px' }}>{r.societe || '—'}</td>
                            <td style={{ padding: '5px 12px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={r.chantier_noms}>{r.chantier_noms || '—'}</td>
                            <td style={{ padding: '5px 12px' }}>{r.responsable_noms || '—'}</td>
                            <td style={{ padding: '5px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={r.OBSERVATION}>{r.OBSERVATION || '—'}</td>
                            <td style={{ padding: '5px 12px' }}>{r.SAISIE_PAR || '—'}</td>
                          </Tr>
                        );
                      })}
                </tbody>
                {sChecksDetail.sorted.length > 1 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                      <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={4}>TOTAL ({sChecksDetail.sorted.length} chèques)</td>
                      <td />
                      <td className="text-end" style={{ padding: '7px 12px', color: '#c62828' }}>{fmtUnit(sChecksDetail.sorted.reduce((a, r) => a + r.montant, 0), getUnit('checks-det'))}</td>
                      <td colSpan={6} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <TablePager {...pChecksDetail} />
            </>)}
          </div>
          </div>

          </div>
          {/* ── fin du conteneur flex des 11 sections-tableaux réordonnables ── */}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* SECTION : Balance interco                                          */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          <div id="sec-balance" style={detailSectionCard}>
            <SectionHeader>⇄ Balance inter-sociétés</SectionHeader>
            <div style={{ padding: '1rem' }}>

              {/* Sélecteurs societes */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 3 }}>Société A</div>
                  <Select
                    options={metaRef.current.societes}
                    value={balanceSocA}
                    onChange={v => { setBalanceSocA(v); setBalanceData(null); }}
                    placeholder="Choisir…"
                    isClearable
                    styles={selectStylesSm}
                  />
                </div>
                <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 3 }}>Société B</div>
                  <Select
                    options={[{ value: 'all', label: '— Toutes les sociétés —' }, ...metaRef.current.societes]}
                    value={balanceSocB}
                    onChange={v => { setBalanceSocB(v); setBalanceData(null); }}
                    placeholder="Choisir…"
                    isClearable
                    styles={selectStylesSm}
                  />
                </div>
                <div style={{ paddingTop: 18 }}>
                  <Button
                    size="sm"
                    disabled={!balanceSocA || !balanceSocB || balanceLoading}
                    onClick={() => fetchBalance(balanceSocA, balanceSocB)}
                    style={{ background: '#6d4e36', borderColor: '#6d4e36', minWidth: 110 }}
                  >
                    {balanceLoading ? <Spinner animation="border" size="sm" /> : '⇄ Calculer'}
                  </Button>
                </div>
              </div>

              {balanceError && <Alert variant="danger" className="py-2">{balanceError}</Alert>}

              {(() => {
                const hasData    = !!balanceData;
                const socA       = balanceSocA?.label || '…';
                const socB       = balanceSocB?.label || '…';
                const total_atob = balanceData?.totaux?.total_atob || 0;
                const total_btoa = balanceData?.totaux?.total_btoa || 0;
                const balance    = balanceData?.totaux?.balance    || 0;
                const creditor   = balance > 0 ? socB : socA;
                const debtor     = balance > 0 ? socA : socB;
                const absBalance = Math.abs(balance);
                // sens d'inclinaison de la balance : penche du côté qui doit le plus (le plus "lourd")
                const heavySide  = absBalance === 0 ? null : (total_atob > total_btoa ? 'A' : 'B');
                const tiltDeg    = heavySide === 'A' ? -16 : heavySide === 'B' ? 16 : 0;
                // proportions pour la barre de répartition A / B
                const sumAB  = total_atob + total_btoa;
                const pctA   = sumAB > 0 ? (total_atob / sumAB) * 100 : 50;
                const pctB   = 100 - pctA;

                const BalanceTable = ({ allRows, filteredRows, pag, sort, accentColor, activeFilter, onFilter, chipIdField = 'chantier_id', chipLabelField = 'chantier', extraCol, showChipTooltip = false }) => {
                  const chips = useMemo(() => {
                    const seen = new Map();
                    allRows.forEach(r => { const id = r[chipIdField]; if (id != null && !seen.has(id)) seen.set(id, r[chipLabelField]); });
                    return [...seen.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => String(a.label).localeCompare(String(b.label)));
                  }, [allRows]); // eslint-disable-line react-hooks/exhaustive-deps

                  const [tooltip, setTooltip] = useState(null); // { label, chantiers, x, y }

                  const handleChipEnter = (e, c) => {
                    if (!showChipTooltip) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const chantiers = [...new Set(allRows.filter(r => r[chipIdField] === c.id).map(r => r.chantier))].sort();
                    setTooltip({ label: c.label, chantiers, x: rect.left, y: rect.bottom + 6 });
                  };

                  return (
                    <>
                      {/* Chips chantiers / sociétés */}
                      {chips.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '8px 10px', borderBottom: '1px solid #f0e8df', background: '#fdfcf8' }}>
                          {chips.map(c => {
                            const active = activeFilter.includes(c.id);
                            const count  = allRows.filter(r => r[chipIdField] === c.id).length;
                            return (
                              <button
                                key={c.id}
                                onClick={() => onFilter(active ? activeFilter.filter(id => id !== c.id) : [...activeFilter, c.id])}
                                onMouseEnter={e => handleChipEnter(e, c)}
                                onMouseLeave={() => setTooltip(null)}
                                style={{
                                  fontSize: '0.72rem', padding: '2px 9px', borderRadius: 20, cursor: 'pointer',
                                  fontWeight: active ? 700 : 400,
                                  background: active ? accentColor : '#f5f0eb',
                                  color: active ? '#fff' : '#6d4e36',
                                  border: `1px solid ${active ? accentColor : '#ddd4c8'}`,
                                  transition: 'all 0.15s'
                                }}
                              >
                                {active ? '✓ ' : ''}{c.label} <span style={{ opacity: 0.7 }}>({count})</span>
                              </button>
                            );
                          })}
                          {activeFilter.length > 0 && (
                            <button
                              onClick={() => onFilter([])}
                              style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, cursor: 'pointer', background: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' }}
                            >
                              ✕ tout afficher ({activeFilter.length} sélectionné{activeFilter.length > 1 ? 's' : ''})
                            </button>
                          )}
                        </div>
                      )}

                      {/* Popover chantiers actifs */}
                      {tooltip && (
                        <div style={{
                          position: 'fixed', left: tooltip.x, top: tooltip.y, zIndex: 9999,
                          background: '#fff', border: '1px solid #ddd4c8', borderRadius: 8,
                          boxShadow: '0 4px 18px rgba(109,78,54,0.18)',
                          padding: '8px 12px', minWidth: 180, maxWidth: 280,
                          maxHeight: 240, overflowY: 'auto', pointerEvents: 'none'
                        }}>
                          <div style={{ fontWeight: 700, fontSize: '0.75rem', color: accentColor, marginBottom: 5, borderBottom: '1px solid #f0e8df', paddingBottom: 4 }}>
                            Chantiers — {tooltip.label}
                          </div>
                          {tooltip.chantiers.map((ch, i) => (
                            <div key={i} style={{ fontSize: '0.73rem', color: '#444', padding: '1px 0' }}>• {ch}</div>
                          ))}
                        </div>
                      )}

                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                          <thead>
                            <tr>
                              <ThStatic style={{ whiteSpace: 'nowrap' }}>ID</ThStatic>
                              <sort.Th field="IDFACTURE">ID Facture</sort.Th>
                              <sort.Th field="date_fac">Date</sort.Th>
                              {extraCol && <sort.Th field={extraCol.field}>{extraCol.label}</sort.Th>}
                              <sort.Th field="chantier">Chantier</sort.Th>
                              <sort.Th field="fournisseur">Fournisseur</sort.Th>
                              <sort.Th field="total_ttc" className="text-end">Total TTC</sort.Th>
                              <sort.Th field="part_chantier_ttc" className="text-end">Part chantier</sort.Th>
                              <sort.Th field="total_ttc_left" className="text-end">Reste</sort.Th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRows.length === 0
                              ? <tr><td colSpan={extraCol ? 9 : 8} className="text-center text-muted py-3">Aucune facture mal imputée</td></tr>
                              : pag.visible.map((r, i) => (
                                <Tr key={i} style={trZebra(i)}>
                                  <td style={{ padding: '5px 12px' }}>
                                    <a
                                      href={`https://cienco.ngrok.io/fiche_facture/${r.IDFACTURE}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: '#aaa' }}
                                    >
                                      {r.IDFACTURE}
                                    </a>
                                  </td>
                                  <td style={{ padding: '5px 12px' }}>
                                    <a
                                      href={`https://cienco.ngrok.io/fiche_facture/${r.IDFACTURE}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ fontWeight: 600, color: accentColor }}
                                    >
                                      {r.NFacture || `#${r.IDFACTURE}`}
                                    </a>
                                  </td>
                                  <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>{fmtDate(r.date_fac)}</td>
                                  {extraCol && (
                                    <td style={{ padding: '5px 12px', fontWeight: 600, color: accentColor }}>
                                      {r[extraCol.field] || '—'}
                                    </td>
                                  )}
                                  <td style={{ padding: '5px 12px', fontWeight: activeFilter.length && !extraCol ? 600 : 400 }}>{r.chantier}</td>
                                  <td style={{ padding: '5px 12px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fournisseur}>{r.fournisseur}</td>
                                  <td className="text-end" style={{ padding: '5px 12px' }}>{fmt(r.total_ttc)}</td>
                                  <td className="text-end" style={{ padding: '5px 12px', fontWeight: 600, color: '#c62828' }}>{fmt(r.part_chantier_ttc)}</td>
                                  <td className="text-end" style={{ padding: '5px 12px', color: r.total_ttc_left > 0 ? '#e65100' : '#2e7d32' }}>{fmt(r.total_ttc_left)}</td>
                                </Tr>
                              ))}
                          </tbody>
                          {filteredRows.length > 1 && (
                            <tfoot>
                              <tr style={{ fontWeight: 700, background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                                <td style={{ padding: '7px 12px', color: '#5c3317' }} colSpan={extraCol ? 6 : 5}>TOTAL ({filteredRows.length})</td>
                                <td className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }}>{fmt(filteredRows.reduce((a, r) => a + r.total_ttc, 0))}</td>
                                <td className="text-end" style={{ padding: '7px 12px', color: '#c62828' }}>{fmt(filteredRows.reduce((a, r) => a + r.part_chantier_ttc, 0))}</td>
                                <td className="text-end" style={{ padding: '7px 12px', color: '#5c3317' }}>{fmt(filteredRows.reduce((a, r) => a + r.total_ttc_left, 0))}</td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                      <TablePager {...pag} />
                    </>
                  );
                };

                const SocieteLogo = ({ societe, size = 32 }) => societe?.logo
                  ? <img src={societe.logo} alt={societe.label} title={societe.label}
                      style={{ height: size, width: size, objectFit: 'contain', borderRadius: 7, background: '#fff', border: '1px solid #e8ddd4', padding: 3 }} />
                  : <div style={{ height: size, width: size, borderRadius: 7, background: '#f0e8df', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5 }}>🏢</div>;

                /* Logo en grand avec le montant incrusté en bas, à la place du nom de la société */
                const SocieteLogoMontant = ({ societe, amount, color, bg, size = 130 }) => (
                  <div title={societe?.label} style={{
                    position: 'relative', width: size, height: size, borderRadius: 18,
                    overflow: 'hidden', border: `3px solid ${color}`, background: '#fff',
                    boxShadow: `0 4px 14px ${color}33`
                  }}>
                    {societe?.logo
                      ? <img src={societe.logo} alt={societe.label}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 12 }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, background: '#f0e8df' }}>🏢</div>
                    }
                    <div style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0,
                      background: bg, color, fontWeight: 800, fontSize: '0.92rem',
                      textAlign: 'center', lineHeight: 1.25, padding: '5px 4px'
                    }}>
                      {fmt(amount)}
                      <div style={{ fontWeight: 600, fontSize: '0.66rem' }}>MAD TTC</div>
                    </div>
                  </div>
                );

                const BalanceBadge = () => (
                  <div style={{
                    padding: '1rem 1.25rem', borderRadius: 12,
                    background: !hasData ? '#f7f5f2' : absBalance === 0 ? '#e8f5e9' : 'linear-gradient(135deg, #fdf6ee 0%, #fff 100%)',
                    border: `2px solid ${!hasData ? '#e0d8d0' : absBalance === 0 ? '#a5d6a7' : '#6d4e36'}`
                  }}>
                    {!hasData
                      ? <div style={{ textAlign: 'center', fontSize: '0.9rem', color: '#999', fontWeight: 600 }}>
                          ⇄ Choisissez les deux sociétés puis cliquez sur « Calculer »
                        </div>
                      : absBalance === 0
                      ? <div style={{ textAlign: 'center', fontSize: '0.95rem', color: '#2e7d32', fontWeight: 700 }}>
                          ✅ Balance nulle — aucune dette entre les deux sociétés
                        </div>
                      : <>
                          {/* Les deux sociétés face à face : le logo (en grand) porte directement le montant, pas besoin du nom */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                              <SocieteLogoMontant societe={balanceSocA} amount={total_atob} color="#e65100" bg="rgba(255,243,224,0.94)" />
                              <div style={{ fontSize: '0.7rem', color: '#999' }}>💸 factures mal imputées ici</div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                              <span style={{
                                fontSize: '3rem', display: 'inline-block',
                                transform: `rotate(${tiltDeg}deg)`, transition: 'transform 0.4s ease'
                              }}>⚖️</span>
                              <span style={{ fontSize: '0.66rem', fontWeight: 700, color: heavySide === 'A' ? '#e65100' : heavySide === 'B' ? '#1565c0' : '#999', whiteSpace: 'nowrap' }}>
                                {heavySide === 'A' && `⬇ côté ${socA}`}
                                {heavySide === 'B' && `⬇ côté ${isAllMode ? 'autres' : socB}`}
                                {!heavySide && 'équilibré'}
                              </span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                              <SocieteLogoMontant societe={isAllMode ? null : balanceSocB} amount={total_btoa} color="#1565c0" bg="rgba(227,242,253,0.94)" />
                              <div style={{ fontSize: '0.7rem', color: '#999' }}>💸 factures mal imputées ici</div>
                            </div>
                          </div>

                          {/* Barre de répartition : poids relatif des montants mal imputés de chaque côté */}
                          <div style={{ marginTop: 16 }}>
                            <div style={{ display: 'flex', height: 12, borderRadius: 7, overflow: 'hidden', border: '1px solid #e8ddd4', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)' }}>
                              <div style={{ width: `${pctA}%`, background: 'linear-gradient(90deg, #ffb74d, #e65100)', transition: 'width 0.5s ease' }} />
                              <div style={{ width: `${pctB}%`, background: 'linear-gradient(90deg, #1565c0, #64b5f6)', transition: 'width 0.5s ease' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginTop: 4 }}>
                              <span style={{ color: '#e65100', fontWeight: 700 }}>{socA} — {pctA.toFixed(0)}%</span>
                              <span style={{ color: '#1565c0', fontWeight: 700 }}>{isAllMode ? 'Autres sociétés' : socB} — {pctB.toFixed(0)}%</span>
                            </div>
                          </div>

                          {/* Résultat net */}
                          <div style={{
                            marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap',
                            padding: '0.55rem 1rem', borderRadius: 8, background: '#fff', border: '1px solid #e8ddd4'
                          }}>
                            <span style={{ fontSize: '1.1rem' }}>👉</span>
                            <span style={{ fontSize: '0.92rem' }}>
                              <span style={{ fontWeight: 800, color: '#c62828' }}>{debtor}</span>
                              <span style={{ fontWeight: 400, color: '#555' }}> doit </span>
                              <span style={{ fontWeight: 800, color: '#6d4e36', fontSize: '1.05rem' }}>{fmt(absBalance)} MAD TTC</span>
                              <span style={{ fontWeight: 400, color: '#555' }}> à </span>
                              <span style={{ fontWeight: 800, color: '#2e7d32' }}>{creditor}</span>
                            </span>
                          </div>
                        </>
                    }
                  </div>
                );

                const hdrSpan = (label, rows, color) => ({
                  style: { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 },
                  onMouseEnter: e => {
                    if (balanceHeaderPinned) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const chantiers = [...new Set(rows.map(r => r.chantier).filter(Boolean))].sort();
                    setBalanceHeaderTooltip({ label, chantiers, x: rect.left, y: rect.bottom + 6, color });
                  },
                  onMouseLeave: () => { if (!balanceHeaderPinned) setBalanceHeaderTooltip(null); },
                  onClick: e => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const chantiers = [...new Set(rows.map(r => r.chantier).filter(Boolean))].sort();
                    setBalanceHeaderTooltip({ label, chantiers, x: rect.left, y: rect.bottom + 6, color });
                    setBalanceHeaderPinned(true);
                  }
                });

                return (
                  <>
                    {/* Balance en haut */}
                    <div style={{ marginBottom: '1.25rem' }}><BalanceBadge /></div>

                    {/* Deux tableaux côte à côte */}
                    <Row className="g-3" style={{ marginBottom: '1.5rem' }}>
                      {/* Colonne A */}
                      <Col xs={12} xl={6}>
                        <div style={{ border: '1px solid #ffcc80', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                            padding: '0.45rem 0.75rem',
                            background: '#fff3e0', fontSize: '0.85rem', fontWeight: 700, color: '#e65100'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span {...hdrSpan(socA, sBalAtoB.sorted, '#e65100')}>{socA}</span>
                              <span style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                                — facturées ici, chantiers{' '}
                                {isAllMode ? 'autres sociétés' : <span {...hdrSpan(socB, sBalAtoB.sorted, '#e65100')}>{socB}</span>}
                              </span>
                            </span>
                            <button
                              onClick={() => exportBalanceExcel(balAtoB_filtered, isAllMode ? { field: 'societe_chantier', label: 'Société chantier' } : null, `Balance_${socA}_vers_${isAllMode ? 'autres' : socB}`)}
                              style={{
                                background: '#e8f5e9', border: '1px solid #4caf50', color: '#2e7d32',
                                borderRadius: 5, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600,
                                cursor: 'pointer', lineHeight: '1.6', whiteSpace: 'nowrap'
                              }}
                            >⬇ Excel</button>
                          </div>
                          <BalanceTable
                            allRows={sBalAtoB.sorted} filteredRows={balAtoB_filtered}
                            pag={pBalAtoB} sort={sBalAtoB} accentColor="#e65100"
                            activeFilter={balanceFilterA} onFilter={setBalanceFilterA}
                            chipIdField={isAllMode ? 'societe_chantier_id' : 'chantier_id'}
                            chipLabelField={isAllMode ? 'societe_chantier' : 'chantier'}
                            extraCol={isAllMode ? { field: 'societe_chantier', label: 'Société chantier' } : null}
                            showChipTooltip={isAllMode}
                          />
                        </div>
                      </Col>

                      {/* Colonne B */}
                      <Col xs={12} xl={6}>
                        <div style={{ border: '1px solid #90caf9', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                            padding: '0.45rem 0.75rem',
                            background: '#e3f2fd', fontSize: '0.85rem', fontWeight: 700, color: '#1565c0'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span {...hdrSpan(isAllMode ? 'Autres sociétés' : socB, sBalBtoA.sorted, '#1565c0')}>{isAllMode ? 'Autres sociétés' : socB}</span>
                              <span style={{ fontWeight: 400, fontSize: '0.78rem' }}>
                                — facturées ici, chantiers{' '}
                                <span {...hdrSpan(socA, sBalBtoA.sorted, '#1565c0')}>{socA}</span>
                              </span>
                            </span>
                            <button
                              onClick={() => exportBalanceExcel(balBtoA_filtered, isAllMode ? { field: 'societe_facture', label: 'Société facture' } : null, `Balance_${isAllMode ? 'autres' : socB}_vers_${socA}`)}
                              style={{
                                background: '#e8f5e9', border: '1px solid #4caf50', color: '#2e7d32',
                                borderRadius: 5, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600,
                                cursor: 'pointer', lineHeight: '1.6', whiteSpace: 'nowrap'
                              }}
                            >⬇ Excel</button>
                          </div>
                          <BalanceTable
                            allRows={sBalBtoA.sorted} filteredRows={balBtoA_filtered}
                            pag={pBalBtoA} sort={sBalBtoA} accentColor="#1565c0"
                            activeFilter={balanceFilterB} onFilter={setBalanceFilterB}
                            chipIdField={isAllMode ? 'societe_facture_id' : 'chantier_id'}
                            chipLabelField={isAllMode ? 'societe_facture' : 'chantier'}
                            extraCol={isAllMode ? { field: 'societe_facture', label: 'Société facture' } : null}
                            showChipTooltip={isAllMode}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Balance rappel en bas */}
                    <BalanceBadge />
                  </>
                );
              })()}

            </div>
          </div>

        </>
      )}

      {/* Overlay fermeture popover épinglé */}
      {balanceHeaderPinned && balanceHeaderTooltip && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9997 }}
          onClick={() => { setBalanceHeaderPinned(false); setBalanceHeaderTooltip(null); }}
        />
      )}

      {/* Popover header société */}
      {balanceHeaderTooltip && (
        <div style={{
          position: 'fixed', left: balanceHeaderTooltip.x, top: balanceHeaderTooltip.y, zIndex: 9999,
          background: '#fff', border: `1px solid ${balanceHeaderTooltip.color}44`,
          borderRadius: 8, boxShadow: '0 4px 18px rgba(0,0,0,0.15)',
          padding: '8px 12px', minWidth: 180, maxWidth: 300,
          maxHeight: 280, overflowY: 'auto',
          pointerEvents: balanceHeaderPinned ? 'auto' : 'none'
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.75rem', color: balanceHeaderTooltip.color, marginBottom: 5, borderBottom: '1px solid #f0e8df', paddingBottom: 4 }}>
            Chantiers — {balanceHeaderTooltip.label}
            {balanceHeaderPinned && <span style={{ float: 'right', opacity: 0.5, fontSize: '0.65rem', cursor: 'pointer' }} onClick={() => { setBalanceHeaderPinned(false); setBalanceHeaderTooltip(null); }}>✕</span>}
          </div>
          {balanceHeaderTooltip.chantiers.length === 0
            ? <div style={{ fontSize: '0.73rem', color: '#999' }}>Aucun chantier</div>
            : balanceHeaderTooltip.chantiers.map((ch, i) => (
                <div key={i} style={{ fontSize: '0.73rem', color: '#444', padding: '1px 0' }}>• {ch}</div>
              ))
          }
        </div>
      )}

      <KpiDetailPanel active={kpiDetail} onClose={() => setKpiDetail(null)} />

    </div>
    </div>
  );
}
